import ky, { type Options } from 'ky';
import { useAuthStore } from '@/store/auth';
import type { MfaPendingResponse } from '@/lib/mfaFlow';
import {
  classifyRefreshError, createSessionRefresher, outcomeFromRefreshBody,
  sessionEndedLoginPath, type RefreshOutcome,
} from '@/lib/sessionRefresh';
import { markExplicitSignOut } from '@/lib/sessionRestore';
import { alertListSearchParams, type AlertListFilter } from '@/lib/alertListQuery';
import type { ChargerTariffView, LocationTariffView } from '@/lib/tariffAssignment';
import type { PlanPricePair } from '@/lib/planPrices';
import type { InviteLink } from '@/lib/inviteLink';
export type { InviteLink } from '@/lib/inviteLink';
import type {
  ApiResponse, ApiError, LoginRequest, LoginResponse, PaginationMeta,
  OperatorDashboardData, OperatorDashboardPeriod,
  DashboardAnomaliesResponse, PendingPayoutsResponse,
  RecentSessionsResponse, TopRevenueLocationsResponse, TopRevenueRange,
  ChargerUptimeResponse, SessionTrendsResponse,
  PerLocationRevenueResponse, PerLocationRevenueRange,
  ChargerUtilizationResponse, SettlementSummaryResponse,
  StartTimeoutsResponse, StartTimeoutRange,
  StartSuccessResponse, StartSuccessRange, StartAttemptsResponse, SessionOutcomeSummary,
  DashboardMapPointsResponse,
  LocationSummaryData,
  CouponAdmin, CouponUpsertBody,
  AnnouncementAdmin, AnnouncementUpsertBody,
  NetworkSubmissionAdmin, NetworkSubmissionMine, NetworkSubmissionCreateBody,
  PublicUser,
  NewsletterListRow, NewsletterDetail, NewsletterCreateBody, NewsletterUpdateBody,
  NewsletterCreateResult, NewsletterApproveResult, NewsletterSendStarted, NewsletterSendStatus,
  NewsletterSendBody, CampaignRunRow,
  BlogPostListRow, BlogPostDetail, BlogPostCreateBody, BlogPostUpdateBody,
  EmailBlock,
  ChargerConnectivity,
  IdlePolicy, SessionIdleView, IdleReport, SiteIdleEvidence, SessionPaymentsRead,
  FacilitiesContactRole, BuildingStaffInviteRole, AlertDelivery,
} from '@evi/shared';

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api';

// The public applicant client never attaches staff credentials or refreshes an Admin session.
export const hostApplicationApi = ky.create({ prefixUrl: BASE_URL, credentials: 'include', retry: 0 });

/** The API base the admin app calls — exported for the rare FULL-PAGE
 *  navigation to an API route (the Google OAuth kickoff at
 *  /auth/google?surface=admin), where the ky client can't be used
 *  because the redirect chain leaves and returns the page. Works in
 *  both shapes: an absolute VITE_API_URL or the relative /api
 *  dev-proxy path (the admin vite proxy mirrors the PWA's). */
export const API_BASE_URL = BASE_URL;

/** POST /auth/refresh timeout. Above ky's 10 s default, which a slow
 *  network during a deploy can exceed, and below the 30 s request timeout. */
const REFRESH_TIMEOUT_MS = 15_000;

/** One raw refresh call (outside `api`, so its own 401 never re-enters the
 *  hook). Never throws. */
async function attemptRefresh(): Promise<RefreshOutcome> {
  try {
    const body = await ky
      .post(`${BASE_URL}/auth/refresh`, { credentials: 'include', timeout: REFRESH_TIMEOUT_MS, retry: 0 })
      .json<unknown>();
    return outcomeFromRefreshBody(body);
  } catch (err) {
    return classifyRefreshError(err);
  }
}

// Module-level, so the in-flight refresh it holds is shared by every 401
// in the tab. Rules and tests in lib/sessionRefresh.ts.
const sessionRefresher = createSessionRefresher({
  attempt: attemptRefresh,
  getAuth: () => {
    const s = useAuthStore.getState();
    return { isAuthenticated: s.isAuthenticated, accessToken: s.accessToken, userId: s.user?._id };
  },
  setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
  endSession: (cause) => {
    // The refresh cookie expired or was revoked (or now belongs to another
    // user). Clear auth and land on sign-in WITH a reason: the admin shell
    // has no toast store, the login banner is the pattern. No redirect from
    // an auth page, which would loop.
    useAuthStore.getState().clearAuth();
    const path = window.location.pathname;
    if (!path.startsWith('/login') && !path.startsWith('/auth')) {
      window.location.href = sessionEndedLoginPath(cause);
    }
  },
  // The replay keeps the original call's timeout, retry and fetch
  // (replayOptionsFrom): a download's 2 minutes, not ky's 10 s default.
  replay: (request, options) => ky(request, options as Options),
});

// Base ky instance
export const api = ky.create({
  prefixUrl: BASE_URL,
  credentials: 'include', // send cookies (refresh token)
  timeout: 30_000,
  retry: {
    limit: 1,
    // No 401: a retried 401 GET went out after the hook gave up (store
    // cleared) and triggered a second refresh. Gateway codes only, for the
    // load balancer during a deploy.
    statusCodes: [408, 502, 503, 504],
    methods: ['get'],
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().accessToken;
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      (request, options, response) => sessionRefresher.afterResponse(request, response, options),
    ],
  },
});

// ── Terms versioning (admin-side gate) ────────────────────────────────────────

export type TermsDocType = 'driver_tos' | 'payout_terms' | 'sp_terms' | 'site_host_terms';

export interface TermsStatus {
  doc_type: TermsDocType;
  label: string;
  version: string;
  body: string | null;
  hosted_url: string | null;
  accepted: boolean;
  /** Published history exists but no current version; the gate BLOCKS. */
  unavailable?: boolean;
  /** new: never accepted (first sign-in); changed: accepted before a material change; accepted otherwise. */
  state?: 'new' | 'changed' | 'accepted';
  material?: boolean;
  change_summary?: string;
  /** A non-material update since the person accepted: the banner, until they close it. Never a gate. */
  update?: { version: string; change_summary: string } | null;
}

export async function markTermsSeen(docType: TermsDocType, version: string): Promise<void> {
  await api.post('me/terms/seen', { json: { doc_type: docType, version } }).json();
}

export async function fetchTermsStatus(docs?: TermsDocType[]): Promise<TermsStatus[]> {
  const q = docs && docs.length ? `?docs=${docs.join(',')}` : '';
  const res = await api.get(`me/terms/status${q}`).json<ApiResponse<TermsStatus[]>>();
  return res.data;
}

export async function acceptTerms(
  docType: TermsDocType,
): Promise<{ doc_type: TermsDocType; version: string; accepted: boolean }> {
  const res = await api
    .post('me/terms/accept', { json: { doc_type: docType } })
    .json<ApiResponse<{ doc_type: TermsDocType; version: string; accepted: boolean }>>();
  return res.data;
}

// ── Error-message extraction ──────────────────────────────────────────────────
//
// ky throws `HTTPError` on any 4xx/5xx, with `err.message` defaulting to a
// generic "Request failed with status code N". The Fastify routes attach a
// structured error envelope to the response body
// (`{ success: false, error: { code, message, details? } }`) and admin
// surfaces want to display server-side validation feedback verbatim — e.g.
// "electricity_tou_windows have overlapping intervals on Mon" — instead of
// the generic ky message.
//
// This helper extracts the server's `error.message` ONLY for VALIDATION_ERROR
// responses, which are designed to carry user-facing validation feedback.
// For any other code (INTERNAL_ERROR, FORBIDDEN, etc.) we return the
// caller-provided fallback — those messages can be internal/sensitive
// ("Database connection refused", "User x cannot access tenant y") and
// shouldn't leak verbatim into the admin UI.
//
// Usage in a save handler:
//   try {
//     await updateLocation(id, patch);
//   } catch (e) {
//     setEditErr(await getDisplayErrorMessage(e, 'Save failed. Please try again.'));
//   }
//
// The helper is intentionally non-throwing — it falls back to the provided
// message on any parsing edge (non-JSON body, missing fields, network errors
// where `response` is absent). Callers can rely on always getting a string.
/** True when ky's HTTPError carries a 429 — the AUTH_TIGHT rate
 *  limiter saying "back off". The MFA screens branch on this to
 *  show a "too many attempts, wait a moment" state instead of a
 *  generic failure. Non-throwing on any other error shape. */
export function isRateLimited(err: unknown): boolean {
  return (err as { response?: Response }).response?.status === 429;
}

export async function getDisplayErrorMessage(
  err: unknown,
  fallback: string,
): Promise<string> {
  const response = (err as { response?: Response }).response;
  if (!response) {
    // Network error — ky never got an HTTP response. No body to parse.
    return fallback;
  }
  try {
    const body = await response.clone().json() as Partial<ApiError>;
    // Surface the server message for ANY structured error envelope, not
    // just VALIDATION_ERROR. Previously this branch was gated to
    // code === 'VALIDATION_ERROR' which meant CONFLICT (e.g. 409
    // "Email already in use"), FORBIDDEN, NOT_FOUND, and INTERNAL_ERROR
    // responses all fell back to the caller's generic string — every
    // admin form's "Failed to save" was hiding actually-useful copy
    // the server had already written. Broadened to any { success:false,
    // error:{message} } shape so each caller's fallback is reserved for
    // the genuine parse-edge cases (network errors, non-JSON bodies).
    if (
      body?.success === false
      && typeof body.error?.message === 'string'
      && body.error.message.length > 0
    ) {
      return body.error.message;
    }
  } catch {
    // Body wasn't JSON, or shape mismatch — fall through to the generic
    // fallback. Logging would be noisy; pages handle their own diagnostics.
  }
  return fallback;
}

/**
 * Extract the server's structured error `code` (e.g. 'USER_ALREADY_ACTIVE')
 * from a thrown ky HTTPError, or null on any parse edge. Lets a page branch on
 * a specific outcome (e.g. resolve a race gracefully) rather than only showing
 * a message. Non-throwing.
 */
export async function getErrorCode(err: unknown): Promise<string | null> {
  const response = (err as { response?: Response }).response;
  if (!response) return null;
  try {
    const body = await response.clone().json() as Partial<ApiError>;
    return typeof body?.error?.code === 'string' ? body.error.code : null;
  } catch {
    return null;
  }
}

// ── Auth endpoints ────────────────────────────────────────────────────────────

/**
 * Phase 7g.3 — Sales Partner invite acceptance.
 *
 * Two public endpoints (no auth header required). The validate
 * endpoint feeds the landing page's initial state; the accept
 * endpoint creates the User + UserRoleAssignment, sets the
 * refresh-token cookie, and returns access_token + user shape
 * compatible with the existing setAuth(user, access_token) flow.
 */
export type { SalesPartnerInviteAccount } from '@evi/shared';
export interface SalesPartnerInviteValidateResponse {
  valid: boolean;
  /** Present only when valid=true. */
  agent_name?: string;
  agent_email?: string;
  /** The payout account's country, only once the agent has one; null
   *  before that (the stored value is then a schema default, not a
   *  choice). Older Apis sent the default 'CA'. */
  agent_country?: string | null;
  expires_at?: string;
  /** What accepting does for this email (20 October 2026): create a login, add the partner role to an existing one, or refuse. */
  account?: import('@evi/shared').SalesPartnerInviteAccount;
  /** A session came with the request: the invite's account ('invitee') or someone else ('other'), 29 October 2026. */
  signed_in?: InviteSignedIn;
  /** Present only when valid=false. Three values mirror the
   *  server's classification; 'invalid' for unknown tokens (also
   *  the indistinguishability response for deactivated agents). */
  reason?: 'invalid' | 'expired' | 'accepted';
}

export async function validateSalesPartnerInvite(
  token: string,
): Promise<SalesPartnerInviteValidateResponse> {
  const res = await api
    .get('sales-partner-invites/validate', { searchParams: { token } })
    .json<ApiResponse<SalesPartnerInviteValidateResponse>>();
  return res.data;
}

/** Who an invite page's request was signed in as (29 October 2026). */
export type InviteSignedIn = 'invitee' | 'other' | null;
/** An existing account's own sign-in on an invite page (Warren, 29 October 2026): its password, or the one-time
 *  sign-in link its mailbox or Google sign-in received. */
export interface InviteSignIn { password?: string; link?: string }

export async function acceptSalesPartnerInvite(body: {
  token: string;
  sign_in?: InviteSignIn;
}): Promise<(LoginResponse & { existing_account?: boolean }) | (MfaPendingResponse & { existing_account?: boolean })> {
  // Accepting answers the MFA pending shape (enroll, or challenge for an
  // account with an authenticator); the session comes from that step.
  const res = await api
    .post('sales-partner-invites/accept', { json: body })
    .json<ApiResponse<(LoginResponse & { existing_account?: boolean }) | (MfaPendingResponse & { existing_account?: boolean })>>();
  return res.data;
}

/** "Email me a sign-in link": a one-time link to the existing account's own mailbox, back to the invite page. */
export async function requestSalesPartnerInviteSignInLink(token: string): Promise<void> {
  await api.post('sales-partner-invites/sign-in-link', { json: { token } });
}

export async function login(body: LoginRequest): Promise<LoginResponse | MfaPendingResponse> {
  // origin:'admin' arms the server's admin-surface role gate: a
  // driver account with valid credentials is bounced with 403
  // before any token mints, the same rule magic-link verify and
  // Google apply to this surface. Set here rather than at call
  // sites so every admin password login carries it.
  //
  // Return type is a union as of the MFA arc: when MFA applies
  // (commit 5 arms the server), the response is the MFA-pending
  // shape instead of a session. Callers narrow via isMfaPending.
  const res = await api
    .post('auth/login', { json: { ...body, origin: 'admin' } })
    .json<ApiResponse<LoginResponse | MfaPendingResponse>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function logout(): Promise<void> {
  // Before the request: if it fails, the refresh cookie survives, and the
  // next startup in this tab must not restore the session anyway.
  markExplicitSignOut();
  await api.post('auth/logout').catch(() => null);
  useAuthStore.getState().clearAuth();
}

/**
 * Log out of every device: the server revokes every refresh token the
 * account holds, in both apps, then this tab signs out. auth/me goes
 * first so an expired access token is refreshed (the 401 hook), because
 * the revoke needs to know whose sessions to end. Resolves false, and the
 * tab stays signed in, when the server did not confirm it.
 */
export async function logoutEverywhere(): Promise<boolean> {
  try {
    await api.get('auth/me');
    const res = await api.post('auth/logout', { searchParams: { everywhere: '1' } })
      .json<ApiResponse<{ ok: true; signed_out_everywhere?: boolean }>>();
    if (!res.success || res.data.signed_out_everywhere !== true) return false;
  } catch {
    return false;
  }
  markExplicitSignOut();
  useAuthStore.getState().clearAuth();
  return true;
}

/**
 * Startup restore (useBootstrapAuth): when this tab has no saved session
 * but the 30-day admin refresh cookie is still good, sign back in without
 * the login form. Ported from the driver app.
 *
 * Raw `ky` for the refresh, so the reactive 401 hook never engages and the
 * two refresh paths cannot double-fire (the hook also skips signed-out
 * tabs). The caller's timeout aborts through `signal`.
 *
 * Returns true when a session was restored (setAuth ran).
 */
export async function bootstrapSilentRefresh(signal?: AbortSignal): Promise<boolean> {
  let token: string;
  try {
    const body = await ky
      .post(`${BASE_URL}/auth/refresh`, { credentials: 'include', timeout: REFRESH_TIMEOUT_MS, retry: 0, signal })
      .json<unknown>();
    const outcome = outcomeFromRefreshBody(body);
    if (outcome.kind !== 'success') return false;
    token = outcome.token;
  } catch {
    // No or expired cookie (401), abort, or network failure: no restore.
    return false;
  }
  if (signal?.aborted) return false;

  // Seed the token so fetchMe() authenticates, then hydrate the user.
  useAuthStore.getState().setAccessToken(token);
  // Clear only the half session this restore seeded: if the person signed in
  // on the login page while /auth/me was slow, that newer session stays.
  const clearOwnHalfSession = () => {
    const s = useAuthStore.getState();
    if (!s.isAuthenticated && s.accessToken === token) s.clearAuth();
  };
  try {
    const user = await fetchMe(signal);
    if (signal?.aborted) {
      clearOwnHalfSession();
      return false;
    }
    useAuthStore.getState().setAuth(user, token);
    return true;
  } catch {
    // Refreshed but /auth/me failed: do not leave a half session.
    clearOwnHalfSession();
    return false;
  }
}

/** Live persona read for admin reactivity. GET /auth/me returns the
 *  CURRENT role + SP/persona fields (is_sales_agent / agent_id /
 *  template) resolved server-side, so a refresh can reconcile a
 *  role change that happened since login without a re-login. The
 *  persona subset is spelled out here because the mount reconcile in
 *  App.tsx diffs exactly these fields. */
export interface MePersona {
  role: PublicUser['role'];
  is_sales_agent: boolean;
  agent_id?: string;
  template: PublicUser['template'];
  // Live permission strings recomputed from the current Role matrix
  // server-side (replaces the stale JWT claim). Carried into the auth
  // store by the App.tsx mount reconcile so a later commit can drive
  // capability-based nav filtering off live caps. Sorted server-side.
  permissions: PublicUser['permissions'];
}

/** The full session user /auth/me returns (me.ts), so the startup
 *  restore can hand setAuth a complete user, not just the persona. */
export type MeUser = PublicUser & MePersona;

export async function fetchMe(signal?: AbortSignal): Promise<MeUser> {
  const res = await api.get('auth/me', signal ? { signal } : undefined).json<ApiResponse<MeUser>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/**
 * Request a magic-link sign-in email for the admin surface (AAP.2).
 * Passes origin: 'admin' so the server-side router (AAP.1) constructs
 * the email URL against ADMIN_ORIGIN instead of WEB_ORIGIN. The send
 * endpoint always returns success regardless of whether the email is
 * registered (no enumeration leak); UI shows "If that email is
 * registered..." copy.
 */
export async function requestMagicLink(email: string): Promise<void> {
  const res = await api
    .post('auth/magic-link', { json: { email, origin: 'admin' } })
    .json<ApiResponse<{ message: string }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
}

/**
 * Verify a magic-link token + email pair and exchange for a session
 * (AAP.2). The verify endpoint is origin-agnostic — only the SEND side
 * needs to know which host to template into the email — so no `origin`
 * field here. Returns the standard LoginResponse shape; caller wires
 * setAuth(user, access_token).
 */
export async function verifyMagicLink(
  email: string,
  token: string,
): Promise<LoginResponse | MfaPendingResponse> {
  const res = await api
    // origin:'admin' is baked in — every verify from this client IS
    // the admin surface, and the flag makes the server reject driver
    // accounts BEFORE minting a session (admin is invite-only). The
    // PWA's client omits the field and keeps open driver sign-in.
    //
    // Union return as of the MFA arc — same contract as login();
    // callers narrow via isMfaPending.
    .post('auth/magic-link/verify', { json: { email, token, origin: 'admin' } })
    .json<ApiResponse<LoginResponse | MfaPendingResponse>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

// ── MFA endpoints (commit 4 of the admin-MFA arc) ─────────────────────
// All three ride the pending token in the BODY as pending_token —
// the field name routes/auth/mfa.ts already expects. None of these
// are reachable in practice until commit 5 makes login/magic-verify
// emit the pending shape.

export async function mfaEnrollStart(
  pendingToken: string,
): Promise<{ otpauth_uri: string }> {
  const res = await api
    .post('auth/mfa/enroll/start', { json: { pending_token: pendingToken } })
    .json<ApiResponse<{ otpauth_uri: string }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function mfaEnrollVerify(
  pendingToken: string,
  code: string,
): Promise<LoginResponse & { backup_codes: string[] }> {
  const res = await api
    .post('auth/mfa/enroll/verify', { json: { pending_token: pendingToken, code } })
    .json<ApiResponse<LoginResponse & { backup_codes: string[] }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function mfaChallenge(
  pendingToken: string,
  code: string,
): Promise<LoginResponse & { backup_codes_remaining?: number }> {
  const res = await api
    .post('auth/mfa/challenge', { json: { pending_token: pendingToken, code } })
    .json<ApiResponse<LoginResponse & { backup_codes_remaining?: number }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/**
 * Request a password-reset email for the admin surface (AAP.3).
 * Passes origin: 'admin' so AAP.1's caller-determined host routing
 * templates the email's reset link against ADMIN_ORIGIN. Server
 * endpoint path is /auth/forgot-password (the route filename is
 * password-reset.ts but the HTTP path differs — verified during
 * AAP.1 pre-flight). Server always returns success regardless of
 * whether the email is registered; UI shows the deliberately
 * ambiguous "if that email is registered..." copy.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const res = await api
    .post('auth/forgot-password', { json: { email, origin: 'admin' } })
    .json<ApiResponse<{ message: string }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
}

/**
 * Consume a password-reset token and set a new password (AAP.3).
 * Server endpoint is /auth/reset-password (distinct from the
 * /auth/reset PWA route path the email URL points at — the email
 * URL goes to the SPA which then POSTs to this server endpoint).
 * Origin-agnostic — the URL is already routed correctly by the
 * time the user lands here.
 */
export async function resetPassword(
  email: string,
  token: string,
  password: string,
): Promise<void> {
  const res = await api
    .post('auth/reset-password', { json: { email, token, password } })
    .json<ApiResponse<{ message: string }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export interface DashboardStats {
  revenue: { total: number; last_30_days: number; currency: string };
  active_sessions: number;
  chargers: { total: number; online: number };
  energy: { total_kwh: number };
  sessions_last_30_days: number;
}

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const res = await api.get('dashboard/stats').json<ApiResponse<DashboardStats>>();
  return res.data;
}

/**
 * Single-shot operator dashboard payload. Replaces the older per-widget
 * fan-out of fetchDashboardStats + fetchSessions + client-side bucketing
 * — aggregation lives server-side so the four KPIs, network ring,
 * revenue sparkline, and active-sessions table all land in one round
 * trip. See OperatorDashboardDataSchema in @evi/shared for the shape.
 */
/** Reporting v2 slice 1 — the wire filter set every report endpoint
 *  accepts. Compiled server-side under the caller's resolved scope
 *  (compileReportFilters), so these only ever narrow. */
export interface ReportFilterParams {
  idle_filter?: import('@evi/shared').IdleFeeFilter;
  location_ids?: readonly string[];
  group_id?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  location_types?: readonly string[];
  residential_enrollment?: 'enrolled' | 'not_enrolled' | null;
}

/** Serialize the filter set to query params, omitting empties. */
export function reportFilterSearchParams(f?: ReportFilterParams | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!f) return out;
  if (f.idle_filter && f.idle_filter !== 'all') out['idle_filter'] = f.idle_filter;
  if (f.location_ids?.length) out['location_ids'] = f.location_ids.join(',');
  if (f.group_id) out['group_id'] = f.group_id;
  if (f.country) out['country'] = f.country;
  if (f.state) out['state'] = f.state;
  if (f.city) out['city'] = f.city;
  if (f.location_types?.length) out['location_types'] = f.location_types.join(',');
  if (f.residential_enrollment) out['residential_enrollment'] = f.residential_enrollment;
  return out;
}

export async function fetchOperatorSummary(
  period: OperatorDashboardPeriod = '24h',
  options?: {
    locationIds?: readonly string[]; from?: string; to?: string;
    filters?: ReportFilterParams | null;
  },
): Promise<OperatorDashboardData> {
  const searchParams: Record<string, string> = {
    period,
    ...reportFilterSearchParams(options?.filters),
  };
  if (options?.locationIds && options.locationIds.length > 0) {
    // Server accepts a comma-separated list. Empty array = no filter
    // (preserves the existing dashboard behavior — that path never
    // passes locationIds, so the URL stays identical to the
    // pre-Phase-2 shape).
    searchParams['location_ids'] = options.locationIds.join(',');
  }
  if (options?.from && options?.to) {
    // Custom range (reports-custom-range). The server gives from/to
    // precedence over period and echoes window:'custom'; the period
    // param above becomes an inert placeholder on these requests.
    searchParams['from'] = options.from;
    searchParams['to'] = options.to;
  }
  const res = await api
    .get('dashboard/operator-summary', { searchParams })
    .json<ApiResponse<OperatorDashboardData>>();
  return res.data;
}

/**
 * Anomaly alerts widget. Bounded preview list (5 rows by default)
 * + total count for the "View all N" footer.
 *
 * Server-side cached for 30s per-tenant; safe to poll on a 60s
 * interval without compounding load when multiple admin tabs are
 * open. See `apps/api/src/routes/dashboard/anomalies.ts`.
 */
export async function fetchDashboardAnomalies(limit?: 'all' | number): Promise<DashboardAnomaliesResponse> {
  const res = await api
    .get('dashboard/anomalies', limit === undefined ? {} : { searchParams: { limit: String(limit) } })
    .json<ApiResponse<DashboardAnomaliesResponse>>();
  return res.data;
}

/**
 * Pending Stripe Payouts widget data. Operator-tier; the
 * response carries `widget_applies: false` for third_party_admin
 * so the frontend self-hides cleanly.
 *
 * Server-side cached for 5 minutes per-tenant. Polling at the
 * cache TTL is enough — Stripe payout schedule is daily/weekly
 * so the data doesn't change minute-to-minute.
 */
export async function fetchPendingPayouts(): Promise<PendingPayoutsResponse> {
  const res = await api
    .get('dashboard/pending-payouts')
    .json<ApiResponse<PendingPayoutsResponse>>();
  return res.data;
}

/**
 * Last 10 sessions in the caller's scope. Fresh per-poll — no
 * server cache, since the "last 10" set churns session-by-session.
 * `status: 'active'` keeps only sessions in progress (the dashboard
 * Sessions widget's Active view); without it every status is listed.
 */
export async function fetchDashboardRecentSessions(status?: 'active'): Promise<RecentSessionsResponse> {
  const res = await api
    .get('dashboard/recent-sessions', status ? { searchParams: { status } } : undefined)
    .json<ApiResponse<RecentSessionsResponse>>();
  return res.data;
}

/**
 * Top 10 revenue-grossing locations over a 7d / 30d / 90d window,
 * or an arbitrary from/to custom range (which the server gives
 * precedence over `range`). Server-side cached for 60s per
 * (tenant, range-or-custom-bounds) — polling at the same cadence
 * is the natural client side.
 */
export async function fetchTopRevenueLocations(
  range: TopRevenueRange,
  options?: { from?: string; to?: string; filters?: ReportFilterParams | null },
): Promise<TopRevenueLocationsResponse> {
  const searchParams: Record<string, string> = {
    range,
    ...reportFilterSearchParams(options?.filters),
  };
  if (options?.from && options?.to) {
    searchParams['from'] = options.from;
    searchParams['to'] = options.to;
  }
  const res = await api
    .get('dashboard/top-revenue-locations', { searchParams })
    .json<ApiResponse<TopRevenueLocationsResponse>>();
  return res.data;
}

/**
 * Charger uptime widget data: percent online now + 24h hourly
 * sparkline. Server-side cached for 60s per-tenant.
 */
export async function fetchChargerUptime(
  filters?: ReportFilterParams | null,
): Promise<ChargerUptimeResponse> {
  const res = await api
    .get('dashboard/charger-uptime', { searchParams: reportFilterSearchParams(filters) })
    .json<ApiResponse<ChargerUptimeResponse>>();
  return res.data;
}

/**
 * Session trends widget: 7d avg duration + 7d avg kWh +
 * 30 daily buckets for the trend sparklines.
 *
 * Server-side cached for 5 min per-tenant. Trend data doesn't
 * change minute-to-minute.
 */
export async function fetchSessionTrends(
  filters?: ReportFilterParams | null,
): Promise<SessionTrendsResponse> {
  const res = await api
    .get('dashboard/session-trends', { searchParams: reportFilterSearchParams(filters) })
    .json<ApiResponse<SessionTrendsResponse>>();
  return res.data;
}

/**
 * Per-location revenue widget. All locations in the caller's
 * scope with their windowed revenue, sorted desc. Differs from
 * fetchTopRevenueLocations which is bounded to top 10.
 *
 * Server-side cached for 60s per (tenant, range).
 */
export async function fetchPerLocationRevenue(
  range: PerLocationRevenueRange,
): Promise<PerLocationRevenueResponse> {
  const res = await api
    .get('dashboard/per-location-revenue', { searchParams: { range } })
    .json<ApiResponse<PerLocationRevenueResponse>>();
  return res.data;
}

/**
 * Per-charger utilization widget. 7-day window, sorted desc.
 * Server-side cached for 60s per (tenant, scope-fingerprint).
 */
export async function fetchChargerUtilization(
  filters?: ReportFilterParams | null,
): Promise<ChargerUtilizationResponse> {
  const res = await api
    .get('dashboard/charger-utilization', { searchParams: reportFilterSearchParams(filters) })
    .json<ApiResponse<ChargerUtilizationResponse>>();
  return res.data;
}

/**
 * Two-Minute Window 4.5 — app-start timeout rate per charger and per
 * site, flagged above 40% over ten or more accepted starts. Read-only.
 * Server-side cached for 60s per (tenant, range, scope-fingerprint).
 */
export async function fetchStartTimeouts(
  range: StartTimeoutRange = '30d',
  filters?: ReportFilterParams | null,
): Promise<StartTimeoutsResponse> {
  const res = await api
    .get('dashboard/start-timeouts', { searchParams: { ...reportFilterSearchParams(filters), range } })
    .json<ApiResponse<StartTimeoutsResponse>>();
  return res.data;
}

/**
 * Session success: start success rate and session completion per charger
 * and per site against the 98% target, and the network row for our own
 * roll or crash. Read-only; 60 s server cache.
 */
export async function fetchStartSuccess(
  range: StartSuccessRange = '7d',
  filters?: ReportFilterParams | null,
): Promise<StartSuccessResponse> {
  const res = await api
    .get('dashboard/start-success', { searchParams: { ...reportFilterSearchParams(filters), range } })
    .json<ApiResponse<StartSuccessResponse>>();
  return res.data;
}

/** One charger's start attempts with their causes, newest first (at most 200). */
export async function fetchStartAttempts(chargerId: string, range: StartSuccessRange = '30d'): Promise<StartAttemptsResponse> {
  const res = await api
    .get('dashboard/start-success/attempts', { searchParams: { charger_id: chargerId, range } })
    .json<ApiResponse<StartAttemptsResponse>>();
  return res.data;
}

/**
 * Settlement summary widget — what EVI owes the building(s) in
 * scope. v1 ships lifetime-unsettled semantics (no formal
 * settlement batching exists yet); the response includes a
 * `settlement_cadence: 'manual'` flag that drives the honest
 * "Settlement cadence is manual today" copy on the frontend.
 *
 * Server-side cached for 5 min per (tenant, scope-fingerprint).
 */
export async function fetchSettlementSummary(): Promise<SettlementSummary> {
  const res = await api
    .get('dashboard/settlement-summary')
    .json<ApiResponse<SettlementSummary>>();
  return res.data;
}

/** The settlement summary with its held-electricity pending figure
 *  (collect and hold, 2026-09-27): the Site's electricity on held payouts,
 *  which total_owed_cents cannot carry (their breakdown records $0) and a
 *  human pays manually. Optional here because an older API omits them
 *  (read as 0); the @evi/shared schema carries them too. */
export type SettlementSummary = SettlementSummaryResponse;

/**
 * Dashboard map widget data — in-scope locations + their
 * charger counts. Replaces the old fan-out of fetchLocations +
 * fetchChargers, which both read tenant-wide data with no
 * AccessAssignment filtering.
 *
 * Server-side cached for 60s per (tenant, scope-fingerprint).
 */
export async function fetchDashboardMapPoints(): Promise<DashboardMapPointsResponse> {
  const res = await api
    .get('dashboard/map-points')
    .json<ApiResponse<DashboardMapPointsResponse>>();
  return res.data;
}

// ── Locations ─────────────────────────────────────────────────────────────────

export interface Location {
  _id: string;
  tenant_id: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    coordinates?: { lat: number; lng: number };
  };
  type: string;
  charger_count: number;
  is_active: boolean;
  // Optional on reads so legacy docs (pre-flag deploy) don't typeerror.
  // Null/undefined is treated as visible by the backend default.
  is_visible_on_driver_map?: boolean;
  // Optional hero photo URL. Falls back to Street View + gradient
  // in StationHero when null/empty.
  photo_url?: string | null;
  // Optional eyebrow above the station title (Novelle treatment).
  subtitle?: string | null;
  /** Flat per-kWh electricity cost in cents. Used as fallback when
   *  electricity_tariff_id is not set. Optional on reads — legacy
   *  docs may have null. */
  electricity_rate_cents?: number | null;
  /** Unit selector for the FLAT branch (Commit D). 'per_kwh' default.
   *  Inline TOU windows + Tariffs carry their own rate_type elsewhere. */
  electricity_rate_type?: 'per_kwh' | 'per_hour';
  /** Phase 7.2 — TOU-aware electricity tariff. Null/missing means
   *  the flat electricity_rate_cents path applies (or inline TOU
   *  if those are populated, per the three-mode resolver). */
  electricity_tariff_id?: string | null;
  /** Inline TOU windows for electricity reimbursement (three-mode
   *  commit). Used when electricity_tariff_id is null and the
   *  array is non-empty. Mutually exclusive with the tariff path
   *  in the admin UI. */
  electricity_tou_windows?: Array<{
    day_of_week: number[];
    start_hour: number;
    end_hour: number;
    rate_cents: number;
    /** Per-window unit selector (Commit B). 'per_kwh' = legacy
     *  default = energy × rate; 'per_hour' = time-billed = duration
     *  × rate. Server defaults to 'per_kwh' when omitted; legacy
     *  docs hydrate as 'per_kwh' via the Mongoose schema default. */
    rate_type?: 'per_kwh' | 'per_hour';
  }> | null;
  /** IANA zone of a place. Time-of-use periods are read in it, so the API
   *  refuses a time-of-use rate without one (Site Host Terms 3.4). */
  timezone?: string | null;
  /** Per-Location join code (per-Location join codes commit). Null
   *  when no code is active. The Location's parent tenant may also
   *  have its own Tenant-tier code; both coexist and are looked up
   *  Location-first by the auth flows. */
  join_code?: string | null;
  join_code_generated_at?: string | null;
  /** Per-Location resident-managed-chargers gate (Phase 1+2+3 of
   *  the tenant→location migration). Defaults true on reads —
   *  the schema default + Phase 1 backfill ensure every existing
   *  Location has the field persisted, but `?:` on the type
   *  preserves backward compat with any pre-Phase-1 cached
   *  payload that might still surface. The LocationResidentManaged-
   *  Card on LocationDetailPage reads this value as the toggle
   *  state. */
  allow_resident_managed_chargers?: boolean;
  /** Per-building fob self-registration (F3). Opt-IN: absent means off.
   *  LocationFobRegistrationCard reads it as the toggle state. */
  allow_resident_fob_registration?: boolean;
  /** Google Maps place_id (Phase 2 of gamification rules update).
   *  Optional — Locations without a place_id render no
   *  post-session review CTA. Admin sets the value via the
   *  Location detail page's GMap card. */
  google_place_id?: string;
  /** EVI's platform share at this Location, in percent. Read first by
   *  resolveAdminFeeBps (Location, then Tenant.admin_fee_bps, then the
   *  15% fallback), and every NEW session's pin is floored at 15%
   *  (PLATFORM_FEE_FLOOR_BPS) whatever is stored here, so a value below
   *  15 is flagged in admin, not honoured. Absent on docs written before
   *  the field had a default. The API hides it from the fleet tier
   *  (siteSafeLocation). Written by super_admin only, through
   *  LocationPlatformShareCard. */
  revenue_split?: number | null;
  created_at: string;
  updated_at: string;
}

export interface LocationDetail extends Location {
  online_chargers: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  meta: PaginationMeta;
}

export async function fetchLocations(params?: {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  /** Include residential homes whose operator_tenant_id is the caller's
   *  tenant (Residential reporting). Reports location filter only. */
  include_residential_reporting?: boolean;
}): Promise<PaginatedResponse<Location>> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.include_residential_reporting) searchParams.set('include_residential_reporting', '1');
  return api.get('locations', { searchParams }).json<PaginatedResponse<Location>>();
}

export async function fetchLocation(id: string): Promise<LocationDetail> {
  const res = await api.get(`locations/${id}`).json<ApiResponse<LocationDetail>>();
  return res.data;
}

// ── Facilities contacts (Warren, 28 Oct 2026, item 6) ──────────────────────
// The people the offline and refused-start alerts email for one location.
// GET/PUT /locations/:id/facilities-contacts; can_edit is the server's own
// answer (location:write and the location in the caller's scope).

export interface FacilitiesContactView {
  name: string;
  email: string;
  /** property_manager, concierge, facilities, owner or other. */
  role: FacilitiesContactRole;
  /** How often they hear about outages (predictive maintenance stage 4). */
  alert_delivery: AlertDelivery;
  /** 'admin' for an Admin edit; 'seed:...' or 'import:...' from the scripts. */
  source: string;
  added_at: string;
}

export interface FacilitiesContactsView {
  location_id: string;
  location_name: string;
  contacts: FacilitiesContactView[];
  max: number;
  can_edit: boolean;
  /** True while the list is empty: this location's alerts go to EVI ops only. */
  alerts_go_to_ops: boolean;
}

export async function fetchFacilitiesContacts(locationId: string): Promise<FacilitiesContactsView> {
  const res = await api.get(`locations/${locationId}/facilities-contacts`).json<ApiResponse<FacilitiesContactsView>>();
  return res.data;
}

export async function saveFacilitiesContacts(
  locationId: string,
  contacts: Array<{ name: string; email: string; role: FacilitiesContactRole; alert_delivery: AlertDelivery }>,
): Promise<FacilitiesContactsView> {
  const res = await api.put(`locations/${locationId}/facilities-contacts`, { json: { contacts } })
    .json<ApiResponse<FacilitiesContactsView>>();
  return res.data;
}

// ── Building staff invites (Warren, 28 Oct 2026, item 5) ───────────────────
// The location page's "Invite building staff": Super Admin, the site's
// operator, or the site owner. A 403 from the list means the caller cannot
// invite here, and the card hides itself.

export interface BuildingStaffInviteView {
  id: string;
  email: string;
  name: string;
  role: BuildingStaffInviteRole;
  property_manager_access?: boolean;
  role_label: string;
  status: 'pending' | 'expired' | 'accepted';
  invited_by_name: string | null;
  sent_count: number;
  last_sent_at: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  accept_outcome: 'new_account' | 'granted' | 'location_added' | 'already_scoped' | 'staff' | null;
}

export async function fetchBuildingStaffInvites(locationId: string): Promise<{ location_name: string; can_grant_property_manager?: boolean; invites: BuildingStaffInviteView[] }> {
  const res = await api.get(`locations/${locationId}/building-staff-invites`)
    .json<ApiResponse<{ location_name: string; can_grant_property_manager?: boolean; invites: BuildingStaffInviteView[] }>>();
  return res.data;
}

export async function createBuildingStaffInvite(
  locationId: string, body: { email: string; name: string; role: BuildingStaffInviteRole; property_manager_access?: boolean },
): Promise<{ invite: BuildingStaffInviteView; email_sent: boolean; account: 'new' | 'existing' | 'staff' }> {
  const res = await api.post(`locations/${locationId}/building-staff-invites`, { json: body })
    .json<ApiResponse<{ invite: BuildingStaffInviteView; email_sent: boolean; account: 'new' | 'existing' | 'staff' }>>();
  return res.data;
}

export async function grantBuildingPropertyManager(locationId: string, inviteId: string): Promise<void> {
  await api.post('locations/' + locationId + '/building-staff-invites/' + inviteId + '/property-manager');
}

export async function resendBuildingStaffInvite(locationId: string, inviteId: string): Promise<{ invite: BuildingStaffInviteView; email_sent: boolean }> {
  const res = await api.post(`locations/${locationId}/building-staff-invites/${inviteId}/resend`)
    .json<ApiResponse<{ invite: BuildingStaffInviteView; email_sent: boolean }>>();
  return res.data;
}

/** Copy invite link (29 October 2026): a fresh link within the invite's expiry, answered once. Resend's gate. */
export async function copyBuildingStaffInviteLink(locationId: string, inviteId: string): Promise<InviteLink> {
  const res = await api.post(`locations/${locationId}/building-staff-invites/${inviteId}/link`).json<ApiResponse<InviteLink>>();
  return res.data;
}

export async function revokeBuildingStaffInvite(locationId: string, inviteId: string): Promise<{ was: string; access_removed: boolean }> {
  const res = await api.delete(`locations/${locationId}/building-staff-invites/${inviteId}`)
    .json<ApiResponse<{ was: string; access_removed: boolean }>>();
  return res.data;
}

/** The invite link's page (no sign-in: the token is the credential). */
export interface BuildingInviteState {
  property_manager_access?: boolean;
  status: 'pending' | 'expired' | 'accepted' | 'revoked';
  location_name: string;
  inviter_name: string | null;
  name: string;
  email: string;
  role_label: string;
  account: 'new' | 'existing' | 'staff';
  /** A session came with the request: the invite's account ('invitee') or someone else ('other'). */
  signed_in?: InviteSignedIn;
}

export async function fetchBuildingInvite(token: string): Promise<BuildingInviteState> {
  const res = await api.get(`building-staff-invites/${encodeURIComponent(token)}`).json<ApiResponse<BuildingInviteState>>();
  return res.data;
}

export async function acceptBuildingInvite(token: string, signIn?: InviteSignIn): Promise<{
  outcome: 'new_account' | 'granted' | 'location_added' | 'already_scoped' | 'staff';
  location_name: string;
  email: string;
  sign_in_path: string | null;
}> {
  const res = await api.post(`building-staff-invites/${encodeURIComponent(token)}/accept`, signIn ? { json: { sign_in: signIn } } : undefined)
    .json<ApiResponse<{ outcome: 'new_account' | 'granted' | 'location_added' | 'already_scoped' | 'staff'; location_name: string; email: string; sign_in_path: string | null }>>();
  return res.data;
}

/** "Email me a sign-in link" on the building staff invite page. */
export async function requestBuildingInviteSignInLink(token: string): Promise<void> {
  await api.post(`building-staff-invites/${encodeURIComponent(token)}/sign-in-link`);
}

// ── Building staff view (GET /facilities/sites) ────────────────────────────

export interface FacilitiesSiteView {
  id: string;
  name: string;
  address: string | null;
  timezone: string | null;
  chargers: Array<{
    id: string; name: string; identity: string; online: boolean; last_heard_at: string | null;
    connectors: Array<{ connector_id: number; status: string | null }>;
  }>;
  alerts: Array<{
    id: string; charger_id: string; kind: 'offline' | 'refused_starts'; open: boolean;
    detected_at: string | null; since: string | null; resolved_at: string | null;
  }>;
}

/** One contact's alert choice, from the link in every alert email (no sign-in: the signed link is the credential). */
export interface AlertPreferenceView {
  location_name: string;
  email: string;
  name: string;
  alert_delivery: AlertDelivery;
  choices: Array<{ value: AlertDelivery; label: string }>;
}

export async function fetchAlertPreference(token: string): Promise<AlertPreferenceView> {
  const res = await api.get(`alert-preferences/${encodeURIComponent(token)}`).json<ApiResponse<AlertPreferenceView>>();
  return res.data;
}

export async function saveAlertPreference(token: string, alertDelivery: AlertDelivery): Promise<AlertPreferenceView> {
  const res = await api.post(`alert-preferences/${encodeURIComponent(token)}`, { json: { alert_delivery: alertDelivery } })
    .json<ApiResponse<AlertPreferenceView>>();
  return res.data;
}

export async function fetchFacilitiesSites(): Promise<{ sites: FacilitiesSiteView[]; checked_at: string }> {
  const res = await api.get('facilities/sites').json<ApiResponse<{ sites: FacilitiesSiteView[]; checked_at: string }>>();
  return res.data;
}

// ── Site Electricity (Site Host Terms Section 13, Super Admin) ──────────────

export type ElectricityEnrollmentState =
  | 'enrolled' | 'transition_scheduled' | 'existing_agreement' | 'manual' | 'not_launched' | 'owner_site';
export type TransitionBasis = 'notice' | 'acceptance' | 'amendment';
export type TransitionMethod = 'email' | 'courier' | 'registered_mail' | 'hand_delivery' | 'other';
export interface TransitionNotice {
  sent_on: string; method: TransitionMethod; note: string; basis: TransitionBasis;
  transition_on: string; transition_at: string; time_zone: string; terms_version: string;
  recorded_by: string; recorded_at: string; revision: number;
}
export interface ElectricityTransitionView {
  location: { id: string; name: string; type: string | null; time_zone: string | null; transition_zone: string };
  enrollment: ElectricityEnrollmentState;
  launch: { first_at: string | null; source: 'commissioning' | 'session' | 'charger' | null; legacy_v1: boolean };
  site_host_terms: { published: boolean; version: string | null; published_at: string | null };
  site_host_terms_first_accepted_at: string | null;
  start_at: string | null;
  start_basis: 'launch' | 'transition' | null;
  grace_anchor_at?: string | null;
  bank_access_first_granted_at?: string | null;
  window_ends_at: string | null;
  notice: TransitionNotice | null;
  editable: boolean;
  /** Set while a notice whose date has passed can still be corrected. */
  correctable_until?: string | null;
  can_edit: boolean;
  /** Today's date in the zone the Transition Date is read in. */
  today: string;
  history: Array<{ action: string; at: string; actor: string | null; after: Partial<TransitionNotice> | null }>;
}
export async function fetchElectricityTransition(locationId: string): Promise<ElectricityTransitionView> {
  return (await api.get(`admin/locations/${locationId}/electricity-transition`).json<ApiResponse<ElectricityTransitionView>>()).data;
}
export async function recordElectricityTransition(locationId: string, body: {
  basis: TransitionBasis; sent_on: string; method: TransitionMethod; note: string; transition_on?: string;
}): Promise<ElectricityTransitionView> {
  return (await api.post(`admin/locations/${locationId}/electricity-transition`, { json: body })
    .json<ApiResponse<ElectricityTransitionView>>()).data;
}
export async function withdrawElectricityTransition(locationId: string, reason: string): Promise<ElectricityTransitionView> {
  return (await api.delete(`admin/locations/${locationId}/electricity-transition`, { json: { reason } })
    .json<ApiResponse<ElectricityTransitionView>>()).data;
}
export interface SiteElectricityRow {
  location_id: string; location_name: string; location_type: string | null; tenant_id: string; tenant_name: string | null;
  enrollment: ElectricityEnrollmentState;
  category: 'window_launch' | 'unenrolled_after_publication' | 'transition_before_tracking' | null;
  first_launch_at: string | null; first_launch_source: 'commissioning' | 'session' | 'charger' | null; legacy_v1: boolean;
  transition_on: string | null; transition_at: string | null; window_ends_at: string | null;
  period_start: string | null; period_end: string | null;
  rate_mode_now: string; time_zone: string | null; applicable: boolean;
  bank_account_connected: boolean; bank_ready_last_seen: boolean | null; bank_first_verified_at: string | null;
  site_host_terms_accepted_at: string | null; tenant_is_per_session_payee: boolean;
  totals: Array<{ currency: string | null; sessions: number; kwh: number; estimated_amount_cents: number;
    unpriced_sessions: number; per_hour_sessions: number; tou_without_timezone_sessions: number; pin_estimated_sessions: number;
    settled_with_session_sessions: number }>;
  mirrored_sessions_excluded: number; last_session_at: string | null; sessions_truncated: boolean;
}
export interface SiteElectricityReport {
  rows: SiteElectricityRow[]; truncated: boolean; cutoff: string;
  site_host_terms: { published: boolean; version: string | null; published_at: string | null };
}
export async function fetchSiteElectricity(): Promise<SiteElectricityReport> {
  return (await api.get('admin/site-electricity/locations').json<ApiResponse<SiteElectricityReport>>()).data;
}
export interface SiteTimeZoneRow {
  location_id: string; name: string; type: string | null; is_active: boolean | null; tenant_id: string;
  timezone: string | null; zone_state: 'missing' | 'invalid' | 'not_site_zone'; time_of_use: boolean; suggested_zone: string | null;
}
export async function fetchSiteTimeZones(): Promise<{ rows: SiteTimeZoneRow[]; total_locations: number; truncated: boolean;
  without_zone: number; time_of_use_without_zone: number }> {
  return (await api.get('admin/site-electricity/time-zones').json<ApiResponse<{ rows: SiteTimeZoneRow[]; total_locations: number;
    truncated: boolean; without_zone: number; time_of_use_without_zone: number }>>()).data;
}

/**
 * Per-location aggregated stats for the LocationInfoCard side panel.
 * Mirrors the operator-summary endpoint's response shape but scoped
 * to a single location. Server pre-applies tenant + scope checks; a
 * 404 means the location either doesn't exist or isn't accessible
 * to the caller (existence-leak guard, same posture as fetchLocation).
 */
export async function fetchLocationSummary(
  locationId: string,
  period: OperatorDashboardPeriod = '7d',
): Promise<LocationSummaryData> {
  const res = await api
    .get(`locations/${locationId}/summary`, { searchParams: { period } })
    .json<ApiResponse<LocationSummaryData>>();
  return res.data;
}

export async function createLocation(body: {
  name: string;
  address: Location['address'];
  type: string;
}): Promise<Location> {
  const res = await api.post('locations', { json: body }).json<ApiResponse<Location>>();
  return res.data;
}

export async function updateLocation(
  id: string,
  body: Partial<{
    name: string;
    address: Location['address'];
    type: string;
    is_active: boolean;
    is_visible_on_driver_map: boolean;
    photo_url: string | null;
    subtitle: string | null;
    electricity_rate_cents: number;
    electricity_tariff_id: string | null;
    /** Phase 3 of the resident-managed-chargers tenant→location
     *  migration. The per-Location card on LocationDetailPage
     *  patches this field; the API gate at chargers/index.ts
     *  reads it (Phase 2) and 403s with
     *  LOCATION_DISABLED_RESIDENT_REGISTRATION when false. */
    allow_resident_managed_chargers: boolean;
    /** Per-building fob self-registration switch (F3). A Site persona
     *  may write it; the driver tap-capture routes read it. */
    allow_resident_fob_registration: boolean;
    /** Google Maps place_id (Phase 2 of gamification rules
     *  update). Empty string clears the field; non-empty
     *  string sets it (Mongoose trims). The Location detail
     *  page's GMap card patches this. */
    google_place_id: string;
    /** EVI platform share (%), 15-50. super_admin only: the API 403s
     *  any other caller that sends the key. An API that predates the
     *  field strips it and still answers 200, so the caller compares
     *  the echoed value with what it sent (LocationPlatformShareCard). */
    revenue_split: number;
    /** The second step's answer for a flat rate above 40 cents per kWh (Warren, 21 October 2026, night: "raise the
     *  utility and reimbursement caps to 60 cents, with a second confirmation above 40"). Never stored. */
    confirm_above_40: boolean;
  }>,
): Promise<Location> {
  const res = await api.put(`locations/${id}`, { json: body }).json<ApiResponse<Location>>();
  return res.data;
}

export async function deleteLocation(id: string): Promise<void> {
  await api.delete(`locations/${id}`);
}

/** Impact summary from the location delete preflight + delete response. */
export interface LocationDeleteImpact {
  location_id: string;
  name: string;
  chargers_to_deactivate: number;
  circuits_to_deactivate: number;
  sessions_preserved: number;
}

export interface LocationDeletePreflight {
  blocked: boolean;
  block: DeleteBlock | null;
  impact: LocationDeleteImpact | null;
}

/** Preflight before deleting a location — does NOT mutate. Surfaces whether the
 *  delete is blocked (a child charger mid-session) and how many chargers /
 *  circuits the cascade would deactivate, so the confirm can state the impact. */
export async function getLocationDeleteImpact(id: string): Promise<LocationDeletePreflight> {
  const res = await api
    .get(`locations/${id}/delete-impact`)
    .json<ApiResponse<LocationDeletePreflight>>();
  return res.data;
}

// ── Chargers ──────────────────────────────────────────────────────────────────

/** Reason a delete is blocked (e.g. an in-use session). */
export interface DeleteBlock {
  reason: string;
  [key: string]: unknown;
}

/** Impact summary from the charger delete preflight + delete response. */
export interface ChargerDeleteImpact {
  charger_id: string;
  identity: string;
  display_name: string | null;
  is_online: boolean;
  sessions_preserved: number;
  in_circuit: boolean;
  groups_dereferenced: number;
  access_assignments_dereferenced: number;
}

export interface ChargerDeletePreflight {
  blocked: boolean;
  block: DeleteBlock | null;
  impact: ChargerDeleteImpact | null;
}

/** Preflight before deleting a charger — does NOT mutate. Surfaces whether the
 *  delete is blocked and what the cascade would touch, so the confirm dialog can
 *  state the impact. */
export async function getChargerDeleteImpact(id: string): Promise<ChargerDeletePreflight> {
  const res = await api
    .get(`chargers/${id}/delete-impact`)
    .json<ApiResponse<ChargerDeletePreflight>>();
  return res.data;
}

/** Soft-delete (deactivate) a charger with cascade. Throws on a 409 block (an
 *  active session) — call getChargerDeleteImpact first to gate the confirm. */
export async function deleteCharger(id: string): Promise<void> {
  await api.delete(`chargers/${id}`);
}

export interface Connector {
  connector_id: number;
  type: string;
  max_power_kw: number;
  /** Phase 3a/3c — operator-set per-connector current ceiling (amps). The DLM
   *  loop uses min(connector.max_current_a, vehicle.max_charge_current_a).
   *  Optional: blank/null = no per-connector ceiling (fair-share only). */
  max_current_a?: number | null;
  status: string;
  status_updated_at: string;
}

export interface Charger {
  _id: string;
  tenant_id: string;
  location_id: string;
  identity: string;
  /** Operator-editable label distinct from `identity`. Falls back to
   *  `identity` in display contexts when null. Surfaced on every
   *  /chargers list response (the server returns the full doc with
   *  no projection at apps/api/src/routes/chargers/index.ts:309). */
  display_name?: string | null;
  vendor: string;
  model: string;
  serial_number?: string;
  firmware_version?: string;
  ocpp_version: string;
  tariff_id?: string;
  connectors: Connector[];
  last_heartbeat?: string;
  /** Hidden from the driver map until it next connects (ISO); cleared on contact. */
  map_hidden_until_contact_after?: string | null;
  last_boot?: string;
  is_online: boolean;
  is_active: boolean;
  diagnostics?: {
    temperature?: number;
    voltage?: number;
    current?: number;
    power_factor?: number;
    last_power_w?: number;
    last_voltage_v?: number;
    last_current_a?: number;
    last_error_code?: string;
    last_error_timestamp?: string;
    last_error_info?: string;
    last_error_vendor_id?: string;
    last_error_vendor_error_code?: string;
    sim_status?: 'online' | 'offline' | 'unknown';
    sim_last_event_at?: string;
  };
  /** OCPP command completion — local auth list version last accepted by
   *  the charger (null = never pushed), plus the GetDiagnostics /
   *  UpdateFirmware lifecycles advanced by charger notifications. */
  local_auth_list_version?: number | null;
  diagnostics_upload?: {
    status?: string;
    file_name?: string | null;
    object_key?: string | null;
    requested_at?: string;
    updated_at?: string;
  };
  firmware_update?: {
    status?: string;
    location?: string;
    retrieve_date?: string;
    requested_at?: string;
    updated_at?: string;
  };
  /** Phase 5a-patch — operator-asserted SIM cellular provider. Drives
   *  webhook + reconciliation + rule 6/7 gating. */
  sim_provider?: '1nce' | 'other' | null;
  sim_iccid?: string | null;
  sim_imsi?: string | null;
  /** How the unit connects (2026-10-01), copied from its deployment's
   *  choice. null = not recorded. Read-only here; no billing. */
  connectivity?: ChargerConnectivity | null;
  connectivity_source?: 'deployment' | null;
  connectivity_set_at?: string | null;
  is_v2_registered?: boolean;
  maintenance_windows?: Array<{
    _id?: string;
    start: string;
    end: string;
    reason: string;
    created_by?: string;
    created_at?: string;
  }>;
  // Third-party (guest) access fields. Optional in the type for
  // back-compat with serializations that predate them; new docs
  // always carry the schema defaults from commit 1 (foundation).
  is_open_to_guests?: boolean;
  guest_pass_list?: string[];
  guest_time_windows?: GuestTimeWindowInput[];
  guest_markup_bps?: number | null;
  public_network?: boolean;
  /** The private switch (Warren, 2026-10-22): only the pass list, the site
   *  host and EVI staff start here. Edited through PrivateAccessSection. */
  private_access?: boolean;
  // Fleet Mode Phase 1 — fleet attribution + mixed-use override.
  // Both null on public chargers (the v1 default for every existing
  // doc). Phase 4 admin UI mutates these via PATCH
  // /chargers/:id/fleet-assignment.
  fleet_id?: string | null;
  allow_mixed_use?: boolean | null;
  /** DLM circuit membership + the control loop's last-pushed cap. Returned
   *  on the full /chargers list (the route uses no projection, see
   *  routes/chargers/index.ts:311). circuit_id is null when the charger
   *  isn't on a circuit; the cap and pushed-at stay null until the loop
   *  first manages it. These mirror the FleetChargerRow DLM fields and power
   *  the building Circuits surface. */
  circuit_id?: string | null;
  dlm_last_cap_a?: number | null;
  dlm_last_pushed_at?: string | null;
  /** Agent commission Phase 1a — sales-agent attribution. Sparse-
   *  indexed on the backend (most chargers stay null). Mutated by
   *  the Phase 7b Network Origin section on ChargerDetailPage —
   *  super_admin only; PUT /chargers/:id silently drops these
   *  fields server-side for non-super_admin callers. */
  agent_id?: string | null;
  agent_commission_bps?: number | null;
  /** EVI's connection fee at this charger, in cents; null = no fee.
   *  Pinned onto the session at start (Session.connection_fee_at_start_cents),
   *  billed only when the session's energy is priced, kept by EVI whole.
   *  Written by super_admin only: PUT /chargers/:id silently drops it
   *  for every other caller, like agent_id. Absent on chargers created
   *  before the field existed, which reads as no fee. */
  connection_fee_cents?: number | null;
  /** Provenance — distinguishes admin-managed from resident-managed.
   *  Powers the convert-to-resident / convert-to-admin admin tool. */
  created_via?: 'admin' | 'resident_self_service';
  /** Owner ObjectId when created_via === 'resident_self_service'.
   *  Null on admin-managed chargers. */
  created_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChargerDetail extends Charger {
  location_name: string;
  display_name?: string | null;
  /** The DLM circuit that currently owns this charger's cap, resolved
   *  server-side. NULL means nothing owns it — which, combined with a
   *  non-null dlm_last_cap_a, is an ORPHANED CAP: a limit still installed on
   *  the hardware that no circuit will ever recompute or clear. `is_active:
   *  false` is the same problem in a quieter form; a deactivated circuit
   *  drops out of the control loop entirely. */
  dlm_circuit?: {
    _id: string;
    name: string;
    limit_a: number;
    is_active: boolean;
  } | null;
  /** Outstanding obligation to remove the cap. 'release' = the platform knows
   *  a clear is owed and the sweep is retrying it; null = nothing owed. */
  dlm_cap_intent?: 'release' | null;
  /** Who installed the cap on the hardware. null/absent reads as 'dlm' — the
   *  control loop was the only writer before this field existed. Only a 'dlm'
   *  cap can be ORPHANED; an operator's or a resident's cap is owned by them,
   *  and a residential charger correctly has no circuit. */
  dlm_cap_source?: 'dlm' | 'admin' | 'owner' | null;
  /** Hydrated display name for created_by_user_id when the charger
   *  is resident-managed. Null on admin-managed and when the
   *  referenced user has been deleted. */
  created_by_user_name?: string | null;
  live_connection: {
    connected: boolean;
    connected_at?: string;
    ocpp_version?: string;
  };
}

export async function fetchChargers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  location_id?: string;
  is_online?: boolean;
}): Promise<PaginatedResponse<Charger>> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.location_id) searchParams.set('location_id', params.location_id);
  if (params?.is_online !== undefined) searchParams.set('is_online', String(params.is_online));
  return api.get('chargers', { searchParams }).json<PaginatedResponse<Charger>>();
}

export async function fetchCharger(id: string): Promise<ChargerDetail> {
  const res = await api.get(`chargers/${id}`).json<ApiResponse<ChargerDetail>>();
  return res.data;
}

// ── Network-wide charger oversight (super_admin) ────────────────────────────
// GET /admin/chargers/oversight — every charger across ALL tenants,
// residential home chargers included, with owner/tenant/type labels and
// live OCPP connection state. Cross-tenant; super_admin only.
export interface ChargerOversightRow {
  id: string;
  identity: string;
  display_name: string | null;
  tenant: { id: string; name: string };
  owner: { name: string | null; email: string | null } | null;
  location: { id: string; name: string; type: string | null };
  charger_type: string;
  is_online: boolean;
  live_connected: boolean;
  status: 'online' | 'offline' | 'stale';
  last_heartbeat: string | null;
  heartbeat_age_minutes: number | null;
  firmware_version: string | null;
  vendor: string | null;
  model: string | null;
  ocpp_version: string | null;
  created_via: string;
  sim_provider: string | null;
  connector_status: string | null;
}

export async function fetchChargerOversight(params?: {
  page?: number;
  limit?: number;
  q?: string;
  offline_only?: boolean;
  residential_only?: boolean;
  tenant_id?: string;
}): Promise<PaginatedResponse<ChargerOversightRow>> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.q) searchParams.set('q', params.q);
  if (params?.offline_only) searchParams.set('offline_only', 'true');
  if (params?.residential_only) searchParams.set('residential_only', 'true');
  if (params?.tenant_id) searchParams.set('tenant_id', params.tenant_id);
  return api.get('admin/chargers/oversight', { searchParams })
    .json<PaginatedResponse<ChargerOversightRow>>();
}

/**
 * Informational billing note on an add-charger response (`data.billing`,
 * built by chargerSeatBillingNote in
 * apps/api/src/lib/tenant-subscription-sync.ts). NEVER a gate — the
 * charger was created and is running either way. A subscribed tenant
 * hears how many seats the subscription now covers; a free tenant gets a
 * nudge toward Settings → Billing. Absent when the server had nothing
 * truthful to say (e.g. the billing sync threw).
 */
export type ChargerSeatBillingNote =
  | { code: 'CHARGER_SEAT_ADDED'; covered_chargers: number; message: string }
  | { code: 'SUBSCRIPTION_REQUIRED'; message: string; subscribe_url: string };

/** POST /chargers and POST /admin/locations/:id/chargers result: the
 *  charger, the one-time OCPP password, and the optional seat note. */
export type ChargerCreateResult = Charger & {
  auth_password: string;
  billing?: ChargerSeatBillingNote;
};

export async function createCharger(body: {
  location_id: string;
  identity: string;
  vendor: string;
  model: string;
  ocpp_version?: string;
  connectors: Array<{ connector_id: number; type: string; max_power_kw: number }>;
}): Promise<ChargerCreateResult> {
  const res = await api
    .post('chargers', { json: body })
    .json<ApiResponse<ChargerCreateResult>>();
  return res.data;
}

// ── CMS provisioning (super_admin / tenant_admin own-tenant) ────────────────
// Tenant-scoped create routes for the location + charger CMS flow
// (apps/api/src/routes/admin/cms-provisioning.ts). They mirror createLocation
// / createCharger / fetchLocations but target the tenant or location by route
// param, so a super_admin can provision for any tenant; requireTenantScope
// gates each call server-side.

export interface TenantLocationRow {
  _id: string;
  name: string;
  type: string;
  is_active: boolean;
  charger_count: number;
  address: Location['address'];
}

export async function fetchTenantLocations(tenantId: string): Promise<TenantLocationRow[]> {
  const res = await api
    .get(`admin/tenants/${tenantId}/locations`)
    .json<ApiResponse<TenantLocationRow[]>>();
  return res.data;
}

export async function createTenantLocation(
  tenantId: string,
  body: { name: string; address: Location['address']; type: string },
): Promise<Location> {
  const res = await api
    .post(`admin/tenants/${tenantId}/locations`, { json: body })
    .json<ApiResponse<Location>>();
  return res.data;
}

export async function createLocationCharger(
  locationId: string,
  body: {
    identity: string;
    vendor: string;
    model: string;
    ocpp_version?: string;
    connectors: Array<{ connector_id: number; type: string; max_power_kw: number }>;
  },
): Promise<ChargerCreateResult> {
  const res = await api
    .post(`admin/locations/${locationId}/chargers`, { json: body })
    .json<ApiResponse<ChargerCreateResult>>();
  return res.data;
}

// OCPP auth-password reveal / regenerate for the charger detail page
// (apps/api/src/routes/admin/cms-provisioning.ts). The password is stored
// plaintext and compared verbatim by the OCPP server, so reveal returns the
// usable value; regenerate mints a new one and returns it once. Both are gated
// server-side by requireTenantScope (super_admin any tenant, tenant_admin own).

export async function revealChargerAuthPassword(id: string): Promise<{ auth_password: string }> {
  const res = await api
    .get(`admin/chargers/${id}/auth-password`)
    .json<ApiResponse<{ auth_password: string }>>();
  return res.data;
}

export async function regenerateChargerAuthPassword(id: string): Promise<{ auth_password: string }> {
  const res = await api
    .post(`admin/chargers/${id}/auth-password/regenerate`)
    .json<ApiResponse<{ auth_password: string }>>();
  return res.data;
}

export interface GuestTimeWindowInput {
  start_hour: number;
  end_hour: number;
  days_of_week: number[];
}

/**
 * Phase 5a-patch Item 4 — PATCH connectivity fields on a Charger.
 *
 * Uses the existing PATCH /chargers/:id route which now accepts
 * display_name, sim_provider, sim_iccid (Phase 5a-patch Commit 1
 * extended the schema + audit). Returns the updated Charger doc.
 */
export async function patchChargerConnectivity(
  id: string,
  body: Partial<{
    sim_provider: '1nce' | 'other' | null;
    sim_iccid: string | null;
  }>,
): Promise<Charger> {
  const res = await api
    .patch(`chargers/${id}`, { json: body })
    .json<ApiResponse<Charger>>();
  return res.data;
}

/**
 * Fleet Mode Phase 4 — admin charger fleet assignment.
 *
 * Hits the dedicated PATCH /chargers/:id/fleet-assignment route
 * (separate from the generic PUT /chargers/:id so the cross-tenant
 * fleet-id validation can live server-side on a single endpoint).
 *
 * Body fields are independently optional. Sending fleet_id=null
 * clears the assignment AND resets allow_mixed_use to null
 * (server-side side effect — the override is meaningless without a
 * parent fleet to inherit from).
 */
// ── The private switch (Warren, 2026-10-22) ───────────────────────────
// GET/PATCH /chargers/:id/private-access: the switch and the pass list,
// gated like PUT /chargers/:id (Charger:update, EVI staff, scope). The
// PATCH carries `expected` (what the editor loaded); a charger changed
// since is a 409 with the current state.
export interface ChargerPrivateAccess {
  private_access: boolean;
  guest_pass_list: string[];
  members: Array<{ _id: string; name: string | null; email: string | null }>;
}

export async function fetchChargerPrivateAccess(id: string): Promise<ChargerPrivateAccess> {
  const res = await api.get(`chargers/${id}/private-access`).json<ApiResponse<ChargerPrivateAccess>>();
  return res.data;
}

export async function updateChargerPrivateAccess(
  id: string,
  body: {
    private_access?: boolean;
    guest_pass_list?: string[];
    expected: { private_access: boolean; guest_pass_list: string[] };
  },
): Promise<ChargerPrivateAccess> {
  const res = await api.patch(`chargers/${id}/private-access`, { json: body }).json<ApiResponse<ChargerPrivateAccess>>();
  return res.data;
}

export async function patchChargerFleetAssignment(
  id: string,
  body: Partial<{
    fleet_id: string | null;
    allow_mixed_use: boolean | null;
  }>,
): Promise<Charger> {
  const res = await api
    .patch(`chargers/${id}/fleet-assignment`, { json: body })
    .json<ApiResponse<Charger>>();
  return res.data;
}

export async function markChargerAs1nce(id: string): Promise<{
  sim_provider: string;
  immediate_fetch: { status: string | null; error: string | null };
}> {
  const res = await api
    .post(`admin/chargers/${id}/mark-1nce`)
    .json<ApiResponse<{
      sim_provider: string;
      immediate_fetch: { status: string | null; error: string | null };
    }>>();
  return res.data;
}

export interface SimReconciliationRow {
  charger_id: string;
  identity: string;
  display_name: string | null;
  location_name: string | null;
  sim_iccid: string;
  sim_imsi: string | null;
  current_sim_provider: '1nce' | 'other' | null;
  last_1nce_event_at: string | null;
  last_1nce_sim_status: 'online' | 'offline' | 'unknown' | null;
  onence_device_data?: unknown;
}

export interface SimReconciliationOrphan {
  iccid: string;
  imsi: string | null;
  msisdn: string | null;
  label: string | null;
  last_seen_at: string | null;
  status: string | null;
}

export interface SimReconciliationReport {
  cached_at: string;
  provisioning_misses: SimReconciliationRow[];
  migration_candidates: SimReconciliationRow[];
  orphan_sims: SimReconciliationOrphan[];
  total_1nce_devices: number;
}

export async function fetchSimReconciliation(): Promise<SimReconciliationReport> {
  const res = await api
    .get('admin/sim-reconciliation')
    .json<ApiResponse<SimReconciliationReport>>();
  return res.data;
}

export async function updateCharger(
  id: string,
  body: Partial<{
    location_id: string;
    vendor: string;
    model: string;
    ocpp_version: string;
    tariff_id: string;
    is_active: boolean;
    connectors: Array<{ connector_id: number; type: string; max_power_kw: number; max_current_a?: number | null }>;
    // Third-party (guest) access fields — optional. Schema validators
    // (server PUT route enables runValidators) enforce 0-50000 bps,
    // 100-user pass-list cap, and per-window day/hour bounds.
    is_open_to_guests: boolean;
    guest_pass_list: string[];
    guest_time_windows: GuestTimeWindowInput[];
    guest_markup_bps: number | null;
    public_network: boolean;
    // Network Origin (Phase 7b) — agent attribution. Server PUT
    // route strips these from the body when the caller isn't
    // super_admin; sending them as a tenant_admin is a no-op.
    agent_id: string | null;
    agent_commission_bps: number | null;
    // EVI's connection fee (integer cents 0-2000, null = no fee). Also
    // stripped silently for non-super_admin, and by an API that predates
    // the field, so the caller checks the echoed charger.
    connection_fee_cents: number | null;
  }>
): Promise<Charger> {
  const res = await api.put(`chargers/${id}`, { json: body }).json<ApiResponse<Charger>>();
  return res.data;
}

/**
 * Unified envelope returned by the new OCPP command surface
 * (Phase 2 of the commands-panel ticket). Both the kebab-case
 * canonical names and the legacy verbs (`start`, `stop`, etc.)
 * route here.
 */
export interface CommandResult {
  commandId: string;
  acceptedAt: string;
  chargerResponse?: unknown;
  /** What actually went on the wire — carries server-built values back
   *  (the allocated reservationId, the pushed listVersion). */
  sentPayload?: unknown;
}

export interface ChargerReservation {
  reservationId: number;
  connectorId: number;
  /** null for Site accounts: the reserved tag is normally a driver's card UID. */
  idTag: string | null;
  expiryDate: string;
  status: 'active' | 'expired' | 'cancelled';
}

export async function fetchChargerReservations(
  chargerIdOrIdentity: string,
): Promise<ChargerReservation[]> {
  const res = await api
    .get(`chargers/${chargerIdOrIdentity}/reservations`)
    .json<ApiResponse<{ reservations: ChargerReservation[] }>>();
  return res.data.reservations;
}

export interface CommandError {
  error: string;
  code: string;
  hint?: string;
  commandId?: string;
}

export async function sendChargerCommand(
  chargerIdOrIdentity: string,
  action: string,
  body?: Record<string, unknown>,
): Promise<CommandResult> {
  // The API accepts either ObjectId or the OCPP identity string —
  // the admin UI passes ObjectId, agents typically pass identity.
  const res = await api
    .post(`chargers/${chargerIdOrIdentity}/commands/${action}`, { json: body ?? {} })
    .json<ApiResponse<CommandResult>>();
  return res.data;
}

// ── Logs (historical OCPP frames) ────────────────────────────────────

export interface OcppFrameRow {
  _id: string;
  charger_identity: string;
  direction: 'incoming' | 'outgoing';
  message_type: number;
  message_id: string;
  action: string;
  payload: Record<string, unknown>;
  error_code?: string;
  error_description?: string;
  ocpp_version: string;
  timestamp: string;
}

export interface PagedFrames {
  data: OcppFrameRow[];
  meta: { limit: number; count: number; nextBefore: string | null };
}

export async function fetchChargerLogs(
  chargerIdOrIdentity: string,
  params?: { limit?: number; before?: string; direction?: 'incoming' | 'outgoing'; action?: string },
): Promise<PagedFrames> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.before) sp.set('before', params.before);
  if (params?.direction) sp.set('direction', params.direction);
  if (params?.action) sp.set('action', params.action);
  const res = await api
    .get(`chargers/${chargerIdOrIdentity}/logs`, { searchParams: sp })
    .json<{ success: true; data: OcppFrameRow[]; meta: PagedFrames['meta'] }>();
  return { data: res.data, meta: res.meta };
}

// ── Active session for charger (live telemetry) ─────────────────────

export interface ActiveSessionSummary {
  _id: string;
  transaction_id: string;
  connector_id: number;
  started_at: string;
  energy_kwh: number;
  power_kw: number;
  duration_minutes: number;
  cost: {
    energy_cost: number;
    time_cost: number;
    flat_fee: number;
    tax: number;
    total: number;
    currency: string;
  };
  rfid_tag: string | null;
  user_id: string | null;
}

export async function fetchActiveSessionForCharger(
  chargerIdOrIdentity: string,
): Promise<ActiveSessionSummary | null> {
  const res = await api
    .get(`chargers/${chargerIdOrIdentity}/active-session`)
    .json<ApiResponse<{ session: ActiveSessionSummary | null }>>();
  return res.data.session;
}

// ── Admin RFID Cards ─────────────────────────────────────────────────

export interface RfidCardUserLite {
  _id: string;
  email: string;
  username: string | null;
  name: string | null;
}

export interface RfidCardRow {
  _id: string;
  tag_uid: string;
  label: string | null;
  is_active: boolean;
  /** 'vid' = an autocharge vehicle id (the car's controller MAC the charger
   *  forwards on plug-in), listed here because it rides the same table;
   *  'rfid' = a physical card. Optional for defensive parsing. */
  credential_kind?: 'rfid' | 'vid';
  /** Inactive because a model-level autocharge rule suspended it. */
  suspended_by_rule?: boolean;
  /** Inactive and waiting for its second sighting - not disabled. */
  pending?: boolean;
  /** Switched off by EVI support: stays off until reactivated here; the
   *  account cannot link or register it back. */
  disabled_by_admin?: boolean;
  /** A fleet pool fob (managed from the fleet). */
  fleet_fob?: boolean;
  tenant_id: string;
  user: RfidCardUserLite | null;
  created_at: string;
  updated_at: string;
}

export interface RfidCardCreateBody {
  tag_uid: string;
  user_id: string;
  label?: string;
  tenant_id?: string;
}

export interface RfidCardUpdateBody {
  label?: string | null;
  user_id?: string;
  is_active?: boolean;
}

export interface AdminUserSearchRow {
  _id: string;
  email: string;
  username: string | null;
  name: string | null;
  role: string;
  tenant_id: string;
}

export async function fetchRfidCards(
  params?: { q?: string; tenantId?: string; isActive?: boolean; limit?: number },
): Promise<RfidCardRow[]> {
  const sp = new URLSearchParams();
  if (params?.q) sp.set('q', params.q);
  if (params?.tenantId) sp.set('tenant_id', params.tenantId);
  if (params?.isActive !== undefined) sp.set('is_active', params.isActive ? 'true' : 'false');
  if (params?.limit) sp.set('limit', String(params.limit));
  const res = await api.get('admin/rfid-cards', { searchParams: sp }).json<ApiResponse<RfidCardRow[]>>();
  return res.data;
}

export async function createRfidCard(body: RfidCardCreateBody): Promise<{ _id: string; tag_uid: string; user_id: string }> {
  const res = await api.post('admin/rfid-cards', { json: body }).json<ApiResponse<{ _id: string; tag_uid: string; user_id: string }>>();
  return res.data;
}

export async function updateRfidCard(id: string, body: RfidCardUpdateBody): Promise<void> {
  await api.patch(`admin/rfid-cards/${id}`, { json: body }).json<ApiResponse<{ _id: string }>>();
}

export async function disableRfidCard(id: string): Promise<void> {
  await api.delete(`admin/rfid-cards/${id}`).json<ApiResponse<{ _id: string }>>();
}

export async function searchUsersForRfid(q: string): Promise<AdminUserSearchRow[]> {
  if (!q.trim()) return [];
  const sp = new URLSearchParams({ q });
  const res = await api.get('admin/users/search', { searchParams: sp }).json<ApiResponse<AdminUserSearchRow[]>>();
  return res.data;
}

// ── Agents (Phase 7b — Network Origin picker on ChargerDetailPage) ──
//
// Minimal row shape returned by GET /admin/agents. Distinct from the
// fuller AgentSummary type in @evi/shared (which includes aggregates
// like lifetime_commission_cents / assigned_charger_count) — this
// endpoint's projection is dropdown-only and intentionally lean. When
// the Phase 7c full Agents admin page ships, that page can use the
// fuller AgentSummary helper and this row type stays bounded to the
// picker surface.
//
// stripe_connect_account_id presence is the picker's Connect-readiness
// signal — a null value renders an "onboarding incomplete" hint on
// the selected option. The full charges_enabled flag isn't exposed
// here (avoiding a per-agent Stripe API roundtrip on the list path);
// runAgentTransfer in Phase 3e is the authoritative gate at transfer
// time, and account.updated webhook (Phase 5) keeps the
// stripe_connect_account_id field in sync once onboarding completes.
export interface AgentRow {
  contact_name?: string | null;
  program_info?: import('@evi/shared').SpProgramView;
  can_edit?: boolean;
  golden_enrolled?: boolean;
  debt_review_required?: boolean;
  id: string;
  name: string;
  email: string;
  /** Phase 7a — extended fields returned by GET /admin/agents
   *  alongside the picker-minimal subset from Phase 7b. The Network
   *  Origin charger picker (Phase 7b) ignores these; the Agents
   *  admin page (Phase 7a) consumes the full shape. */
  phone: string | null;
  default_commission_bps: number | null;
  stripe_connect_account_id: string | null;
  outstanding_clawback_cents: number;
  /** SP field-onboarding program (EVI_SP_PROGRAM_SPEC): running count of
   *  pay-to-use chargers deployed, and the qualification timestamp
   *  (non-null once earnings activate at the 4th). Powers the dashboard
   *  commission-tier card. Absent on legacy payloads → treat as 0 / null. */
  deployed_paid_count?: number;
  qualified_at?: string | null;
  /** Activity pace (EVI_SP_PROGRAM_SPEC, V1 visibility): pay-to-use chargers
   *  deployed in the trailing window versus the minimum, with on-pace / behind.
   *  Populated on the list + SP-self reads; null on create / patch / resend
   *  responses (and legacy payloads). */
  activity?: {
    deployed_last_window: number;
    window_days: number;
    min: number;
    on_pace: boolean;
    behind_by: number;
  } | null;
  active: boolean;
  notes: string | null;
  /** Phase 7g.2: ISO 3166-1 alpha-2 country code. Meaningful only
   *  with stripe_connect_account_id: then it is the payout account's
   *  country, recorded at /connect/start from the explicit choice made
   *  there (2026-10-03). Without an account it is the schema default
   *  'CA' and is not shown (agentPayoutAccountCountryLabel). */
  country: string;
  /** The payout account's country, null before an account exists. Sent
   *  on every row by an Api from 2026-10-03 on and absent from older
   *  ones, whose /connect/start ignored the chosen country: the start
   *  surfaces offer no account creation until the key is present
   *  (agentApiTakesCountry). */
  payout_country?: string | null;
  /** Phase 7g.1 invite-state fields. No standing token or link is
   *  projected (29 October 2026): Copy invite link
   *  (copyAgentInviteLink) mints a fresh one and shows it once. */
  invite_token_expires_at: string | null;
  invite_sent_at: string | null;
  invite_accepted_at: string | null;
  invite_status: AgentInviteStatus;
  /** Phase 7g.1 — bound User after acceptance. Null during
   *  pending/expired (the SP-side flow ships in Phase 7g.3). */
  user_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  /** Partner directory (24 October 2026). Payout setup from the stored
   *  snapshot; absent from an older Api (connectStateOf falls back). */
  connect_state?: 'not_started' | 'unchecked' | 'pending' | 'ready' | 'restricted';
  /** The current sales rep (null: house book) and its name, and the
   *  partner's live referral codes. Super Admin rows only. */
  sales_staff_id?: string | null;
  sales_rep_name?: string | null;
  referral_codes?: string[];
  /** The states and provinces the partner serves (25 October 2026), codes
   *  from @evi/shared SERVED_REGIONS. Absent from an older Api. */
  served_regions?: string[];
}

// Phase 7g.1 — three-state invite lifecycle. Orthogonal to
// AgentRow.active — an SP can be deactivated independent of
// where their invite stands.
export type AgentInviteStatus = 'pending' | 'expired' | 'accepted';

/**
 * Client-side recomputation of invite_status from raw fields.
 * The server already projects invite_status in AgentRow; this
 * helper exists so a component holding a stale projection across
 * the expiry boundary (e.g. modal kept open while a 21d window
 * elapses) renders the right pill on each tick. Mirror of the
 * server's deriveInviteStatus at agents.ts:deriveInviteStatus.
 */
export function deriveInviteStatus(a: {
  invite_accepted_at?: string | null;
  invite_token_expires_at?: string | null;
}): AgentInviteStatus {
  if (a.invite_accepted_at) return 'accepted';
  if (a.invite_token_expires_at
    && new Date(a.invite_token_expires_at).getTime() > Date.now()) {
    return 'pending';
  }
  return 'expired';
}

export async function listAgents(): Promise<AgentRow[]> {
  const res = await api.get('admin/agents').json<ApiResponse<AgentRow[]>>();
  return res.data;
}

// ── Sales Partner profile (24 October 2026) ─────────────────────────
// GET /admin/agents/:id/profile and GET /admin/agents/:id/history/:kind
// (apps/api/src/routes/admin/agent-profile.ts). Read-only.

export interface PartnerProfile {
  agent: AgentRow;
  /** What this viewer may do, computed by the Api from the checks the
   *  write routes make. The page gates every control on these. */
  capabilities: {
    edit: boolean; resend_invite: boolean; refresh_connect: boolean;
    /** Every history source (a Super Admin). */
    history: boolean;
    /** The history sources this viewer reads (25 October 2026); absent from
     *  an older Api, where history decides (historyKindsOf). */
    history_kinds?: PartnerHistoryKind[];
    tabs: string[];
    /** EVI-internal details: rep, application, referral link, team, Stripe ids. */
    internal?: boolean;
    /** PUT /admin/agents/:id/served-regions. */
    edit_regions?: boolean;
  };
  connect: {
    state: 'not_started' | 'unchecked' | 'pending' | 'ready' | 'restricted';
    details_submitted: boolean; payouts_enabled: boolean; transfers_active: boolean; information_due: boolean;
    checked_at: string | null; payout_country: string | null;
  };
  sales_rep: { staff_id: string; name: string; employed: boolean } | null;
  assignment_history: Array<{ staff_id: string | null; staff_name: string | null; effective_at: string | null; reason: string | null }>;
  user_account: { id: string; name: string | null; email: string; is_active: boolean; last_login: string | null; created_at: string | null } | null;
  application: {
    id: string; source: string; status: string; submitted_at: string | null; name: string | null; email: string; phone: string | null;
    notes: string | null; tags: string[]; referral_label: string | null; referral_resolution: string | null;
    approved_at: string | null; approval_reason: string | null;
    /** The regions given on the application (25 October 2026). */
    served_regions?: string[];
  } | null;
  referral_link: { code: string; url: string; apply_url: string; created_at: string | null } | null;
  team: Array<{ id: string; name: string | null; email: string; status: string; can_order: boolean; invited_at: string | null; accepted_at: string | null }>;
  /** Per source; null for a source this viewer does not read. */
  counts: Record<'jobs' | 'installs' | 'leads' | 'chargers' | 'sites' | 'payouts' | 'mail' | 'activity', number | null> | null;
}

export type PartnerHistoryKind = 'jobs' | 'installs' | 'leads' | 'chargers' | 'sites' | 'payouts' | 'ledger'
  | 'statements' | 'team_statements' | 'activity' | 'mail' | 'deliverability';
export interface PartnerHistoryPage<T> { kind: PartnerHistoryKind; rows: T[]; next_cursor: string | null; summary?: Record<string, number> }

export async function getPartnerProfile(agentId: string): Promise<PartnerProfile> {
  const res = await api.get(`admin/agents/${agentId}/profile`).json<ApiResponse<PartnerProfile>>();
  return res.data;
}

export async function getPartnerHistory<T>(agentId: string, kind: PartnerHistoryKind,
  params: { cursor?: string | null; limit?: number; status?: string } = {}): Promise<PartnerHistoryPage<T>> {
  const searchParams: Record<string, string> = {};
  if (params.cursor) searchParams['cursor'] = params.cursor;
  if (params.limit) searchParams['limit'] = String(params.limit);
  if (params.status && params.status !== 'all') searchParams['status'] = params.status;
  const res = await api.get(`admin/agents/${agentId}/history/${kind}`, { searchParams }).json<ApiResponse<PartnerHistoryPage<T>>>();
  return res.data;
}

/** PUT /admin/agents/:id/served-regions (Super Admin): the partner's states
 *  and provinces. Returns the stored list, in list order. */
export async function setPartnerServedRegions(agentId: string, served_regions: string[]): Promise<{ served_regions: string[]; unchanged: boolean }> {
  const res = await api.put(`admin/agents/${agentId}/served-regions`, { json: { served_regions } }).json<ApiResponse<{ served_regions: string[]; unchanged: boolean }>>();
  return res.data;
}

/** PUT /admin/sales-staff/:id/served-regions (Super Admin): an SP Manager's territory. */
export async function setSalesStaffServedRegions(staffId: string, served_regions: string[]): Promise<{ served_regions: string[]; unchanged: boolean }> {
  const res = await api.put(`admin/sales-staff/${staffId}/served-regions`, { json: { served_regions } }).json<ApiResponse<{ served_regions: string[]; unchanged: boolean }>>();
  return res.data;
}

// ── Leads (admin CRM over the unified Lead collection) ─────────────
/** Send-list audiences for lead imports and filters. */
export const LEAD_AUDIENCE_LISTS = [
  'newsletter', 'investor', 'sales_partner', 'sales_ops', 'social', 'mission_hills_investors',
] as const;
export type LeadAudienceList = (typeof LEAD_AUDIENCE_LISTS)[number];
export const LEAD_AUDIENCE_LIST_LABELS: Record<LeadAudienceList, string> = {
  newsletter: 'Newsletter',
  investor: 'Investors',
  mission_hills_investors: 'Mission Hills investors',
  sales_partner: 'Sales Partners',
  sales_ops: 'Sales / Ops',
  social: 'Social',
};

export interface LeadRow {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  audience: string;
  /** Send-list memberships (slice 3). */
  audiences: string[];
  source: string;
  status: string;
  tags: string[];
  notes: string | null;
  consent_source: string | null;
  consent_at: string | null;
  unsubscribed_at: string | null;
  /** Globally suppressed (slice 3). */
  suppressed: boolean;
  /** Why it is suppressed. An unsubscribe reason needs a re-consent note to
   *  lift (absent from an older Api). */
  suppression_reason?: string | null;
  user_id: string | null;
  created_at: string | null;
  next_action_at: string | null;
  last_touched_at: string | null;
  band: string;
  lead_score: number;
  /** Internal or test lead (28 October 2026): never scored, in the digest or
   *  speed-to-lead, or sent a campaign (absent from an older Api). */
  internal?: boolean;
  /** evi_domain, staff_list or manual; null when not internal. */
  internal_reason?: string | null;
}
export interface LeadListResponse {
  leads: LeadRow[];
  total: number;
  page: number;
  limit: number;
}
export interface LeadListParams {
  audience?: string;
  /** Send-list membership filter (slice 3): comma-separated subset of the five. */
  list?: string;
  status?: string;
  source?: string;
  tag?: string;
  band?: string;
  q?: string;
  /** "Overdue" quick filter (slice 2): hot/warm rows past their next action. */
  overdue?: boolean;
  /** created_at range (slice 3), ISO date strings. */
  created_from?: string;
  created_to?: string;
  page?: number;
  limit?: number;
}

/** The subset of LeadListParams that scopes rows (shared by list + export). */
export function leadFilterQuery(p: LeadListParams): Record<string, string> {
  const s: Record<string, string> = {};
  if (p.audience) s['audience'] = p.audience;
  if (p.list) s['list'] = p.list;
  if (p.status) s['status'] = p.status;
  if (p.source) s['source'] = p.source;
  if (p.tag) s['tag'] = p.tag;
  if (p.band) s['band'] = p.band;
  if (p.q) s['q'] = p.q;
  if (p.overdue) s['overdue'] = 'true';
  if (p.created_from) s['created_from'] = p.created_from;
  if (p.created_to) s['created_to'] = p.created_to;
  return s;
}

export async function listLeads(params: LeadListParams = {}): Promise<LeadListResponse> {
  const searchParams: Record<string, string> = { ...leadFilterQuery(params) };
  if (params.page) searchParams['page'] = String(params.page);
  if (params.limit) searchParams['limit'] = String(params.limit);
  const res = await api.get('admin/leads', { searchParams }).json<ApiResponse<LeadListResponse>>();
  return res.data;
}

export async function updateLead(
  id: string,
  body: { status?: string; notes?: string; next_action_at?: string | null; audiences?: string[]; internal?: boolean },
): Promise<LeadRow> {
  const res = await api.patch(`admin/leads/${id}`, { json: body }).json<ApiResponse<{ lead: LeadRow }>>();
  return res.data.lead;
}

/** Globally suppress the lead's email (org-wide do-not-send). Slice 3. */
export async function suppressLead(id: string, reason?: string): Promise<LeadRow> {
  const res = await api.post(`admin/leads/${id}/suppress`, { json: reason ? { reason } : {} })
    .json<ApiResponse<{ lead: LeadRow }>>();
  return res.data.lead;
}
/** Lift a suppression. One that records an unsubscribe needs the note of how
 *  the person consented again (409 UNSUBSCRIBE_NEEDS_RECONSENT without it). */
export async function unsuppressLead(id: string, reconsentNote?: string): Promise<LeadRow> {
  const res = await api.post(`admin/leads/${id}/unsuppress`, { json: reconsentNote ? { reconsent_note: reconsentNote } : {} })
    .json<ApiResponse<{ lead: LeadRow }>>();
  return res.data.lead;
}

/** Record a touch on a lead (last_touched_at + the admin's id) with no field
 *  change — the drawer's one-tap "actioned" that drops a due lead off the
 *  overdue digest. Slice 2. */
export async function touchLead(id: string): Promise<LeadRow> {
  const res = await api.post(`admin/leads/${id}/touch`).json<ApiResponse<{ lead: LeadRow }>>();
  return res.data.lead;
}

/** Known-contact drawer aggregate — the platform picture behind a lead row. */
export interface LeadIntelligence {
  email: string;
  rows: Array<{ source: string; status: string; created_at: string | null }>;
  user: {
    role: string;
    points: number;
    total_sessions: number;
    total_kwh: number;
    total_spending_cents: number;
    last_session_date: string | null;
  } | null;
  orders: { count: number; lifetime_cents: number; last_order_at: string | null };
  agent: { deployed_paid_count: number; tier_boundary: number | null } | null;
  score: {
    score_recency: number;
    score_frequency: number;
    score_monetary: number;
    score_intent: number;
    lead_score: number;
    band: string;
  };
}
export async function fetchLeadIntelligence(id: string): Promise<LeadIntelligence> {
  const res = await api.get(`admin/leads/${id}/intelligence`).json<ApiResponse<LeadIntelligence>>();
  return res.data;
}

/// ── Conversations (inbound-email agent) ────────────────────────────────────
export type ConversationStatus = 'needs_review' | 'human_handling' | 'awaiting_reply' | 'closed';
/** The list views (29 October 2026): the inbox hides filtered mail; Filtered shows only it. */
export type ConversationView = 'inbox' | 'filtered' | 'unmatched' | 'support_mail';
export type ConversationMatchStatus =
  | 'alias' | 'email' | 'reference' | 'person_link' | 'ambiguous' | 'candidates' | 'unmatched' | 'not_driver' | 'staff';

/** Where a thread came from, its tier and its match (29 October 2026); an older Api sends none of these. */
export interface ConversationChannelFields {
  source?: 'email_reply' | 'support_mail';
  tenant_id?: string | null;
  channel?: { route: string; group_alias: string | null; human_only: boolean; human_reason: string | null } | null;
  match?: { status: ConversationMatchStatus; sender_email: string | null; candidate_count: number; multiple_accounts: boolean } | null;
  filtered?: { reason: string; at: string | null } | null;
}

export interface ConversationListRow extends ConversationChannelFields {
  id: string;
  /** Null while no driver is tied to the sender (support@ mail). */
  user: { id: string; name: string | null; email: string | null } | null;
  subject: string;
  status: ConversationStatus;
  message_count: number;
  last_message: { direction: 'inbound' | 'outbound'; preview: string; created_at: string | null } | null;
  has_draft: boolean;
  assigned_to: string | null;
  updated_at: string | null;
  created_at: string | null;
}

export interface ConversationMessageView {
  n?: number;
  direction: 'inbound' | 'outbound';
  author?: 'driver' | 'evii' | 'person' | 'system' | null;
  body: string;
  from_email: string | null;
  created_at: string | null;
  subject?: string | null;
  route?: string | null;
  hop_verdict?: string | null;
  sender_verdict?: string | null;
  send_status?: string | null;
  auto_reply?: boolean;
  attachments?: Array<{ k: number; filename: string; content_type: string; size: number }>;
}

export interface ConversationMatchDetail {
  status: ConversationMatchStatus;
  sender_email: string | null;
  multiple_accounts: boolean;
  unmatched_references: string[];
  linked_by: string | null;
  linked_at: string | null;
  candidates: Array<{
    user: { id: string; name: string | null; email: string | null; tenant_id: string | null } | null;
    session: { id: string; started_at: string | null; total_cents: number | null; currency: string | null; location: string | null } | null;
    evidence: string[];
  }>;
}

export interface ConversationDetail extends ConversationChannelFields {
  id: string;
  user: { id: string; name: string | null; email: string | null } | null;
  subject: string;
  status: ConversationStatus;
  assigned_to: string | null;
  messages: ConversationMessageView[];
  draft: { body: string; model?: string | null; created_at: string | null } | null;
  /** Why the thread went to a person (EVII's hand-off or a person's); an older Api sends none. */
  handoff?: { reason: string | null; by: string | null; at: string | null } | null;
  /** The evidence a person links a driver by (never sent to the agent). */
  match_detail?: ConversationMatchDetail | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function listConversations(
  params: { status?: ConversationStatus; view?: ConversationView; page?: number; limit?: number } = {},
): Promise<{ conversations: ConversationListRow[]; total: number; page: number; limit: number }> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.view && params.view !== 'inbox') search.set('view', params.view);
  if (params.page) search.set('page', String(params.page));
  if (params.limit) search.set('limit', String(params.limit));
  const res = await api.get('admin/conversations', { searchParams: search })
    .json<ApiResponse<{ conversations: ConversationListRow[]; total: number; page: number; limit: number }>>();
  return res.data;
}

export async function fetchConversation(id: string): Promise<ConversationDetail> {
  const res = await api.get(`admin/conversations/${id}`).json<ApiResponse<ConversationDetail>>();
  return res.data;
}

export async function approveConversation(id: string, body?: string): Promise<{ id: string; status: ConversationStatus }> {
  const res = await api.post(`admin/conversations/${id}/approve`, { json: body ? { body } : {} })
    .json<ApiResponse<{ id: string; status: ConversationStatus }>>();
  return res.data;
}

export async function takeOverConversation(id: string): Promise<{ id: string; status: ConversationStatus }> {
  const res = await api.post(`admin/conversations/${id}/take-over`).json<ApiResponse<{ id: string; status: ConversationStatus }>>();
  return res.data;
}

export async function reopenConversation(id: string): Promise<{ id: string; status: ConversationStatus }> {
  const res = await api.post(`admin/conversations/${id}/reopen`).json<ApiResponse<{ id: string; status: ConversationStatus }>>();
  return res.data;
}

export async function draftConversation(id: string): Promise<unknown> {
  return api.post(`admin/conversations/${id}/draft`, { json: {} }).json();
}

export async function closeConversation(id: string): Promise<{ id: string; status: ConversationStatus }> {
  const res = await api.post(`admin/conversations/${id}/close`).json<ApiResponse<{ id: string; status: ConversationStatus }>>();
  return res.data;
}

/** A person ties the thread to a driver (audited; the thread moves to the driver's tenant). */
export async function linkConversationDriver(id: string, userId: string): Promise<{ id: string }> {
  const res = await api.post(`admin/conversations/${id}/link-driver`, { json: { user_id: userId } }).json<ApiResponse<{ id: string }>>();
  return res.data;
}

/** A person marks the sender as not a driver (audited). */
export async function markConversationNotDriver(id: string, reason?: string): Promise<{ id: string }> {
  const res = await api.post(`admin/conversations/${id}/not-a-driver`, { json: reason ? { reason } : {} }).json<ApiResponse<{ id: string }>>();
  return res.data;
}

/** The download path of one attachment (downloadAuthed carries the token; images and PDF only, within the S3 window). */
export function conversationAttachmentPath(id: string, message: number, attachment: number): string {
  return `admin/conversations/${id}/messages/${message}/attachments/${attachment}`;
}

// ── Inbound mail (Settings, 29 October 2026) ───────────────────────────────
export interface InboundMailChecklistStep { key: string; label: string; done: boolean; detail: string | null }
export interface InboundMailView {
  receiving: { enabled: boolean; topic_configured: boolean; bucket_configured: boolean; domain: string; reply_alias_on: boolean };
  intake: { address_masked: string | null; generated_at: string | null; generated: boolean };
  settings: Record<string, unknown>;
  checklist: InboundMailChecklistStep[];
  last_7_days: Record<string, number>;
  /** Read-only Stripe reads driver matching has used in the ET day (the budget is settings.stripe_reads_per_day). */
  stripe_reads_today?: number;
}
export interface ReceivedMailRow {
  id: string; received_at: string; source: string; route: string; routes: string[]; outcome: string; reason: string | null;
  hop: { verdict: string; reasons: string[] }; sender: { email: string | null; domain: string | null; verdict: string; source: string | null };
  group_alias: string | null; subject: string; preview: string | null; conversation_id: string | null; attempts: number; last_error: string | null;
}
export interface RereadReport {
  bucket: string | null; listed: number; finished: number; missing: Array<{ key: string; ledger: string }>;
  processed: Array<{ key: string; outcome: string; reason: string | null }>; truncated: boolean; applied: boolean;
  /** Apply was asked while receiving is switched off: nothing was read (the Api answers 409 INBOUND_RECEIVING_OFF first). */
  receiving_off?: boolean;
}

export async function fetchInboundMail(): Promise<InboundMailView> {
  return (await api.get('admin/inbound-mail').json<ApiResponse<InboundMailView>>()).data;
}
export async function generateIntakeAddress(rotate = false): Promise<{ address: string; rotated: boolean }> {
  return (await api.post('admin/inbound-mail/intake-address', { json: rotate ? { rotate: true } : {} }).json<ApiResponse<{ address: string; rotated: boolean }>>()).data;
}
export async function saveInboundMailSettings(patch: Record<string, unknown>): Promise<{ settings: Record<string, unknown> }> {
  return (await api.patch('admin/inbound-mail/settings', { json: patch }).json<ApiResponse<{ settings: Record<string, unknown> }>>()).data;
}
export async function listReceivedMail(outcome?: string): Promise<ReceivedMailRow[]> {
  const searchParams = new URLSearchParams();
  if (outcome) searchParams.set('outcome', outcome);
  return (await api.get('admin/inbound-mail/received', { searchParams }).json<ApiResponse<{ rows: ReceivedMailRow[] }>>()).data.rows;
}
export async function rereadReceivedMail(apply: boolean, sinceDays = 30): Promise<RereadReport> {
  return (await api.post('admin/inbound-mail/reread', { json: { apply, since_days: sinceDays } }).json<ApiResponse<RereadReport>>()).data;
}

export interface LeadImportRow {
  email: string;
  name?: string;
  phone?: string;
  tags?: string[];
  /** per_row consent basis: provenance string (slice 3). */
  consent_source?: string;
  /** Historical consent timestamp from the file (server-validated). */
  consent_at?: string;
}
export interface LeadImportResult {
  total: number;
  created: number;
  deduped: number;
  invalid: number;
  /** per_row mode: rows rejected for a missing/invalid consent basis (slice 3). */
  rejected_no_consent: number;
  /** Rows skipped because the email is globally suppressed (slice 3). */
  suppressed_skipped: number;
  known_contact_new_source: number;
}

export interface LeadImportBody {
  audience: string;
  /** Batch send-list memberships (slice 3): subset of the five. */
  audiences?: string[];
  source: string;
  /** 'attest' (batch attestation, default) or 'per_row' (rows carry consent). */
  consent_mode?: 'attest' | 'per_row';
  /** Required in attest mode. */
  consent_attested?: boolean;
  /** Explicit batch consent provenance (attest mode). */
  consent_source?: string;
  rows: LeadImportRow[];
}

export async function importLeads(body: LeadImportBody): Promise<LeadImportResult> {
  const res = await api.post('admin/leads/import', { json: body }).json<ApiResponse<LeadImportResult>>();
  return res.data;
}

/** Dry-run the import: same plan + read-only lookups, no writes (slice 3). */
export async function previewImportLeads(
  body: LeadImportBody,
): Promise<LeadImportResult & { preview: true }> {
  const res = await api.post('admin/leads/import/preview', { json: body })
    .json<ApiResponse<LeadImportResult & { preview: true }>>();
  return res.data;
}

/**
 * Phase 7a — create + update payloads. Both shapes are super_admin-
 * only on the wire; the server route gates with
 * `request.user.role !== 'super_admin'` → 403. The frontend's
 * Agents admin page is also nav-gated via `requiresSuperAdmin` so
 * tenant_admin never reaches a form that posts here.
 *
 * stripe_connect_account_id + outstanding_clawback_cents are
 * deliberately absent from both payloads — system-managed by the
 * Phase 5 webhook + Phase 3e/6b money flow respectively. Operator
 * editing them via the form would be wrong; the server's
 * additionalProperties:false on the route schema also rejects them.
 *
 * created_by_user_id is server-stamped from request.user.sub on
 * create; not exposed in either payload.
 */
// Phase 7g.2: the values Agent.country may hold (server-side enum at
// apps/api/src/routes/admin/agents.ts + Mongoose schema enum at
// packages/db/src/models/Agent.ts). Since 2026-10-03 the Admin form no
// longer sends it: the payout country is chosen at Connect start, US or
// CA only (lib/agentPayoutCountry.ts).
export type AgentCountry = 'CA' | 'US' | 'GB' | 'AU';

export interface AgentCreatePayload {
  contact_name?: string | null;
  name: string;
  email: string;
  phone?: string | null;
  default_commission_bps?: number | null;
  notes?: string | null;
  country?: AgentCountry;
}

export interface AgentUpdatePayload {
  contact_name?: string | null;
  name?: string;
  email?: string;
  phone?: string | null;
  default_commission_bps?: number | null;
  notes?: string | null;
  active?: boolean;
  country?: AgentCountry;
}

export async function createAgent(body: AgentCreatePayload): Promise<AgentRow> {
  const res = await api.post('admin/agents', { json: body }).json<ApiResponse<AgentRow>>();
  return res.data;
}

export async function updateAgent(id: string, body: AgentUpdatePayload): Promise<AgentRow> {
  const res = await api.patch(`admin/agents/${id}`, { json: body }).json<ApiResponse<AgentRow>>();
  return res.data;
}

// ── Incentive Programs (curated incentive layer editor, incentives v2) ────────
// super_admin / operator only (server gate). The monthly-review surface for the
// shop's /incentives curated layer.
export interface IncentiveProgramRow {
  id: string;
  key: string;
  country: 'US' | 'CA';
  region: string;
  administrator: string;
  name: string;
  scope_tag: string;
  property_types: string[];
  equipment: { dc_fast: boolean; level2_ac: boolean; make_ready: boolean };
  amount: string | null;
  summary: string;
  eligibility_notes: string | null;
  deadline: string | null;
  url: string;
  status: 'active' | 'waitlist' | 'paused' | 'closed';
  verified_at: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export interface IncentiveUpsertPayload {
  key?: string;
  country?: 'US' | 'CA';
  region?: string;
  administrator?: string;
  name?: string;
  scope_tag?: string;
  property_types?: string[];
  equipment?: { dc_fast: boolean; level2_ac: boolean; make_ready: boolean };
  amount?: string | null;
  summary?: string;
  eligibility_notes?: string | null;
  deadline?: string | null;
  url?: string;
  status?: 'active' | 'waitlist' | 'paused' | 'closed';
  /** 'now' touches verified_at to today; an ISO date sets it. */
  verified_at?: string;
  is_published?: boolean;
}

export async function listIncentivePrograms(): Promise<IncentiveProgramRow[]> {
  const res = await api.get('admin/incentives').json<ApiResponse<IncentiveProgramRow[]>>();
  return res.data;
}

export async function createIncentiveProgram(body: IncentiveUpsertPayload): Promise<IncentiveProgramRow> {
  const res = await api.post('admin/incentives', { json: body }).json<ApiResponse<IncentiveProgramRow>>();
  return res.data;
}

export async function updateIncentiveProgram(
  id: string,
  body: IncentiveUpsertPayload,
): Promise<IncentiveProgramRow> {
  const res = await api.patch(`admin/incentives/${id}`, { json: body }).json<ApiResponse<IncentiveProgramRow>>();
  return res.data;
}

/**
 * Phase 7g.1 resend of an SP invite (mint fresh token + extend
 * expiry 21 days from now). Server gates on agent.active +
 * !invite_accepted_at.
 */
/** Mints a fresh invite link and emails it (20 October 2026). invite_email says whether the email went. */
export async function resendAgentInvite(id: string): Promise<AgentRow & { invite_email?: { ok: boolean; error?: string; to?: string } }> {
  const res = await api.post(`admin/agents/${id}/invite/resend`).json<ApiResponse<AgentRow & { invite_email?: { ok: boolean; error?: string; to?: string } }>>();
  return res.data;
}

/** Copy invite link (29 October 2026): a fresh single-use link within the invite's expiry, answered once. Resend's gate. */
export async function copyAgentInviteLink(id: string): Promise<InviteLink> {
  const res = await api.post(`admin/agents/${id}/invite/link`).json<ApiResponse<InviteLink>>();
  return res.data;
}

/**
 * Phase 7a.1 — Stripe Connect Express onboarding for an agent.
 *
 * Twin of startTenantConnectOnboarding below. Returns a single-use
 * Stripe-hosted onboarding URL; caller is expected to redirect the
 * super_admin to it. Stripe redirects back to /agents on completion
 * or refresh.
 *
 * Idempotent in the create-account sense: if the agent already has
 * a stripe_connect_account_id, the route mints a fresh AccountLink
 * for the existing account (resume path). First call for an agent
 * also creates the underlying Stripe Account.
 *
 * After onboarding completes, the Phase 5 account.updated webhook
 * auto-unblocks any AgentPayout rows stuck in pending_stripe_setup
 * for this agent. No manual reconcile needed.
 *
 * `body` is agentConnectStartBody's result: { country } for a NEW
 * account (the route refuses to create one without it, 400
 * CONNECT_COUNTRY_REQUIRED, and never defaults), nothing for a resume.
 * The reply's `country` is the country of the account the link is for
 * (null when it cannot be confirmed, absent from an older Api); a caller
 * that sent a country checks it with agentStartCountryProblem before
 * opening Stripe.
 */
export async function startAgentConnect(
  id: string,
  body?: { country: 'US' | 'CA' },
): Promise<{ url: string; country?: string | null }> {
  const res = await api
    .post(`admin/agents/${id}/connect/start`, body ? { json: body } : undefined)
    .json<ApiResponse<{ url: string; country?: string | null }>>();
  return res.data;
}

export interface PartnerConnectStatus {
  state: 'not_started' | 'pending' | 'ready' | 'restricted';
  details_submitted: boolean; payouts_enabled: boolean; transfers_active: boolean;
  information_due: boolean; checked_at: string | null;
}
export async function fetchPartnerConnectStatus(id: string, refresh = false): Promise<PartnerConnectStatus> {
  return (await api.get(`admin/agents/${id}/connect/status`, { searchParams: refresh ? { refresh: 'true' } : {} })
    .json<ApiResponse<PartnerConnectStatus>>()).data;
}

/**
 * Phase 7g.4b.1 — fetch a one-time Stripe-hosted Express dashboard
 * login URL for an agent's Connect account. Mirror of the tenant
 * dashboard-link helper.
 *
 * Server gates on super_admin OR SP-self. Returns 400 when the
 * agent has no Connect account yet (CONNECT_NOT_STARTED) — caller
 * should hide the CTA in that case (the profile page reads
 * stripe_connect_account_id from the agent projection to decide
 * whether to show the "View Stripe Dashboard" button at all).
 *
 * Returned URL is single-use and expires quickly (Stripe convention,
 * ~few minutes). Caller should redirect immediately rather than
 * stashing the URL.
 */
export async function fetchAgentDashboardLink(id: string): Promise<{ url: string }> {
  const res = await api
    .post(`admin/agents/${id}/connect/dashboard-link`)
    .json<ApiResponse<{ url: string }>>();
  return res.data;
}

// ── Agent Ops Queue (Phase 7d) ───────────────────────────────────────
//
// Attention queue for AgentPayout rows in one of three states:
//   - status='clawback_pending' (refund-driven reversal failed,
//     debt recorded against Agent.outstanding_clawback_cents)
//   - status='permanently_failed' (Phase 4 retry cron exhausted
//     MAX_RETRIES; awaiting manual intervention)
//   - manual_review_required=true (Phase 3e flagged an edge case
//     like agent_not_found or no_source_charge; Phase 6b flagged
//     a transient reversal failure that needs ops attention)
//
// Endpoint returns AgentPayout rows + hydrated Agent + Session
// fields (batch-fetched server-side to avoid per-row roundtrips).

export interface AgentOpsQueueRow {
  // AgentPayout core
  id: string;
  session_id: string;
  agent_id: string;
  status: string;
  gross_commission_cents: number;
  clawback_applied_cents: number;
  net_transfer_cents: number;
  clawback_debt_recorded_cents: number;
  reversal_cents_cumulative: number;
  manual_review_required: boolean;
  last_error: string | null;
  attempt_count: number;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
  created_at: string;
  updated_at: string;
  // Hydrated Agent fields (null when the agent doc was deleted
  // out-of-band — defensive rendering on the table side)
  agent_name: string | null;
  agent_email: string | null;
  agent_active: boolean | null;
  agent_outstanding_clawback_cents: number | null;
  // Hydrated Session fields (null when the session doc was deleted
  // out-of-band — column renders em-dash)
  session_tenant_id: string | null;
  session_user_id: string | null;
  session_started_at: string | null;
  session_ended_at: string | null;
  // Phase 7e fields — populated by GET /admin/agents/:id/payouts
  // (the per-agent history endpoint). Always null in the ops-queue
  // response (those rows surface attention signals only; the
  // history view is the place to drill into transfer + reversal
  // details).
  stripe_transfer_id: string | null;
  refund_handling: string | null;
  refund_reversed_at: string | null;
  refund_absorbed_at: string | null;
  // Phase 7g.4b.3 Commit 2 — hydrated Charger + Location fields.
  // Populated by both /admin/agent-payouts/ops-queue and
  // /admin/agents/:id/payouts handlers via batch-fetched Charger
  // and Location lookups. Either field is null when the underlying
  // doc was deleted out-of-band; consumers render em-dash defensively.
  // location_id ships alongside the display fields so SP card can
  // group payouts by stable id (name collisions across cities ruled
  // out). All three are stable identifiers, safe for table column
  // display + client-side grouping keys.
  charger_identity: string | null;
  location_name: string | null;
  location_id: string | null;
}

export interface AgentOpsQueueResponse {
  rows: AgentOpsQueueRow[];
  /** True when the server hit the soft cap (200) and returned the
   *  most-recent 200 rows; rare in steady state. Frontend can
   *  surface a "showing 200 of N+" hint when true. */
  truncated: boolean;
  total_returned: number;
}

export async function listAgentOpsQueue(): Promise<AgentOpsQueueResponse> {
  const res = await api
    .get('admin/agent-payouts/ops-queue')
    .json<ApiResponse<AgentOpsQueueResponse>>();
  return res.data;
}

/**
 * Mark an attention-flagged AgentPayout row as resolved. Clears
 * manual_review_required → row drops from the queue on next fetch.
 *
 * v1 accepts only this single mutation. Other AgentPayout fields
 * are system-managed by Phase 3e/4/6b money-flow handlers. Phase
 * 7d.1 will add separate primitives for "Retry now" and "Write
 * off clawback" once real ops feedback exists for those nuanced
 * cases.
 */
export async function resolveAgentPayout(
  id: string,
): Promise<{ id: string; manual_review_required: boolean }> {
  const res = await api
    .patch(`admin/agent-payouts/${id}`, {
      json: { manual_review_required: false },
    })
    .json<ApiResponse<{ id: string; manual_review_required: boolean }>>();
  return res.data;
}

/**
 * Phase 7e — per-agent AgentPayout history. Backs the
 * AgentDetailPage history table at /agents/:id.
 *
 * Returns rows sorted by created_at desc. Soft cap 200 + truncation
 * flag in the response (same shape as listAgentOpsQueue). The
 * AgentPayout shape mirrors the ops queue's projection; this
 * endpoint additionally populates stripe_transfer_id +
 * refund_handling + refund_reversed_at + refund_absorbed_at, which
 * are null in the ops-queue response.
 *
 * Returns 404 if the agent doesn't exist (deep link to a deleted
 * agent gets a clean error state on the frontend rather than an
 * ambiguous empty list).
 */
export async function listAgentPayouts(agentId: string): Promise<AgentOpsQueueResponse> {
  const res = await api
    .get(`admin/agents/${agentId}/payouts`)
    .json<ApiResponse<AgentOpsQueueResponse>>();
  return res.data;
}

// ── Sales Partner installs (SP field onboarding) ─────────────────────
//
// Backs the SP Register Install wizard at /register-install. Server
// routes at apps/api/src/routes/sales-partner/installs.ts; both gate
// on the caller's linked Agent (403 FORBIDDEN for non-SP users).

/** A customer tenant the SP already has installs with — exactly the
 *  tenant set their charger scope exposes (GET /sp/customers only
 *  resolves names for it; no global tenant search). Feeds the
 *  wizard's "existing customer" picker. */
export interface SpCustomer {
  id: string;
  name: string;
  slug: string;
}

export async function fetchSpCustomers(): Promise<SpCustomer[]> {
  const res = await api.get('sp/customers').json<ApiResponse<SpCustomer[]>>();
  return res.data;
}

export type SpInstallConnectorType = 'J1772' | 'NACS' | 'CCS1';

/** POST /sp/installs body — mirrors the Fastify schema in
 *  routes/sales-partner/installs.ts (serial ^[A-Za-z0-9_-]{1,20}$,
 *  max_power_kw 1-350, 1-50 chargers, idempotency_key 8-64 chars). */
export interface SpInstallBody {
  /** Minted ONCE per wizard mount and reused across submit retries —
   *  the server keys new-customer provisioning on it so a resubmit
   *  no-ops at the tenant level instead of duplicating. */
  idempotency_key: string;
  customer:
    | { mode: 'new'; name: string; contact_name: string; contact_email: string }
    | { mode: 'existing'; tenant_id: string };
  location: {
    name: string;
    address: {
      street: string; city: string; state: string; zip: string;
      country?: string;
      coordinates?: { lat: number; lng: number };
    };
    google_place_id?: string;
    /** Site type (2026-09-30). Residential is refused: the homeowner registers it. */
    site_type?: 'commercial' | 'multifamily' | 'residential';
  };
  chargers: Array<{
    serial: string;
    connector_type: SpInstallConnectorType;
    connector_types?: SpInstallConnectorType[];
    max_power_kw: number;
    /** Whether drivers are billed at this charger. Only pay-to-use chargers
     *  advance the SP commission ordinal and carry a stamped rate. */
    pay_to_use: boolean;
  }>;
  /** How the deployment's chargers connect (2026-10-01). Required. */
  connectivity: ChargerConnectivity;
  turnstile_token?: string;
}

export interface SpInstallResult {
  deployment_id?: string;
  tenant_id: string;
  location_id?: string | null;
  /** Serials newly registered by this submit. */
  registered: string[];
  /** Serials this same agent had already registered (per-serial
   *  idempotency on retried submits). */
  already_registered: string[];
  /** Serials lost to a mid-flight registration race: never created,
   *  so they will not authenticate. Surfaced on the done screen. */
  skipped?: string[];
  /** True when the wizard's idempotency key already stood the new
   *  customer up on a previous submit (retry path). */
  already_provisioned?: boolean;
  /** One-time per-unit Basic auth credentials for the newly registered
   *  serials — shown ONCE on the done screen (the SP is standing at the
   *  unit); afterwards the charger page's audit-logged reveal is the way
   *  back to a password. */
  credentials?: Array<{ identity: string; password: string | null }>;
  /** Public OCPP websocket base for the per-unit recipe display. */
  ocpp_url?: string;
  /** New-customer installs only: the customer's account activates, and
   *  their sign-in link is emailed, when the first charger connects
   *  (Ruling 8, connect-first). Absent for existing-customer installs. */
  customer_activation?: 'pending_first_connect';
}

export async function submitSpInstall(body: SpInstallBody): Promise<SpInstallResult> {
  const res = await api.post('sp/installs', { json: body }).json<ApiResponse<SpInstallResult>>();
  return res.data;
}

// ── Audit (operator action history) ──────────────────────────────────

export interface AuditRow {
  _id: string;
  tenant_id: string;
  user_id?: string | null;
  action: string;
  resource: string;
  payload?: unknown;
  response?: unknown;
  ip_address?: string | null;
  created_at: string;
}

export interface PagedAudit {
  data: AuditRow[];
  meta: { limit: number; count: number; nextBefore: string | null };
}

// ── Locked-out drivers (commit 8 of hybrid-wallet ticket) ───────────

export interface LockedOutDriverRow {
  user_id: string;
  email: string;
  name: string | null;
  username: string | null;
  failed_charge_count: number;
  payment_lockout_until: string;
  locked_since: string;
  days_remaining: number;
  hours_remaining: number;
}

export interface LockedOutDriversResponse {
  data: LockedOutDriverRow[];
  meta: {
    count: number;
    lockoutDurationDays: number;
    consecutiveFailureThreshold: number;
  };
}

export async function fetchLockedOutDrivers(): Promise<LockedOutDriversResponse> {
  const res = await api
    .get('admin/drivers/locked-out')
    .json<{ success: true; data: LockedOutDriverRow[]; meta: LockedOutDriversResponse['meta'] }>();
  return { data: res.data, meta: res.meta };
}

export async function clearPaymentLockout(userId: string): Promise<void> {
  // 204 No Content — ky's `.json()` would explode on an empty body,
  // so we use the raw response form.
  await api.post(`admin/drivers/${userId}/clear-payment-lockout`);
}

export async function fetchChargerAudit(
  chargerIdOrIdentity: string,
  params?: { limit?: number; before?: string; action?: string },
): Promise<PagedAudit> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.before) sp.set('before', params.before);
  if (params?.action) sp.set('action', params.action);
  const res = await api
    .get(`chargers/${chargerIdOrIdentity}/audit`, { searchParams: sp })
    .json<{ success: true; data: AuditRow[]; meta: PagedAudit['meta'] }>();
  return { data: res.data, meta: res.meta };
}

// ── Display-name rename (audit-logged) ───────────────────────────────

export async function renameCharger(
  chargerId: string,
  displayName: string | null,
): Promise<ChargerDetail> {
  const res = await api
    .patch(`chargers/${chargerId}`, { json: { display_name: displayName } })
    .json<ApiResponse<ChargerDetail>>();
  return res.data;
}

// ── Charger management-type conversion (admin tool) ─────────────────

/** Flip an admin-managed charger to resident-managed by stamping
 *  the target user as the new owner. The user must be in the
 *  charger's tenant and have a phone on file. Server returns 412
 *  with structured error codes (USER_PHONE_REQUIRED,
 *  USER_TENANT_MISMATCH, ALREADY_RESIDENT_MANAGED) so the UI can
 *  surface friendly copy. */
export async function convertChargerToResident(
  chargerId: string,
  userId: string,
): Promise<ChargerDetail> {
  const res = await api
    .post(`admin/chargers/${chargerId}/convert-to-resident`, { json: { user_id: userId } })
    .json<ApiResponse<ChargerDetail>>();
  return res.data;
}

/** Reverse direction. Resident's settings (pass list, time windows,
 *  etc.) are preserved on the charger doc — re-conversion wouldn't
 *  lose them. created_by_user_id is cleared. */
export async function convertChargerToAdmin(
  chargerId: string,
): Promise<ChargerDetail> {
  const res = await api
    .post(`admin/chargers/${chargerId}/convert-to-admin`)
    .json<ApiResponse<ChargerDetail>>();
  return res.data;
}

/** Super admin: free the identity a resident registration holds but never
 *  used (register-first squatting). The API refuses a charger that has
 *  connected, has a session or has a verified factory reservation. The
 *  charger is renamed released-<id>-<identity> and deactivated. */
export async function releaseSelfRegistration(
  chargerId: string,
): Promise<{ _id: string; identity: string; released_identity: string }> {
  const res = await api
    .post(`admin/chargers/${chargerId}/release-self-registration`)
    .json<ApiResponse<{ _id: string; identity: string; released_identity: string }>>();
  return res.data;
}

// ── Tariffs ───────────────────────────────────────────────────────────────────

export interface Tariff {
  _id: string;
  tenant_id: string;
  name: string;
  type: 'flat' | 'time_of_use' | 'tiered';
  /** Per-tariff unit selector (Commit D). 'per_kwh' = legacy
   *  default = energy_kwh × rate; 'per_hour' = duration_hours ×
   *  rate. Applies to flat + TOU + tiered branches uniformly.
   *  Server defaults to per_kwh when omitted; legacy tariffs
   *  hydrate as per_kwh via the Mongoose schema default. */
  rate_type?: 'per_kwh' | 'per_hour';
  currency: string;
  rates: {
    energy_rate: number;
    time_rate?: number;
    /** The idle fee's grace, whole minutes after charging stops (5-30,
     *  required with an idle fee; Warren 2026-09-28). */
    idle_grace_minutes?: number | null;
    idle_max_charge_cents?: number | null;
    /** Legacy session start fee (cents). Retired from every write path
     *  in Tier 2: TariffForm never sends it and the API no longer
     *  accepts it. Bills only on sessions that started before the
     *  connection fee was pinned; EVI's fee is Charger.connection_fee_cents. */
    flat_fee?: number;
    time_of_use_schedule?: Array<{
      day_of_week: number[];
      start_hour: number;
      end_hour: number;
      energy_rate: number;
    }>;
    /** Tiered schedule — `tier_max` is the new field name (Commit D);
     *  `up_to_kwh` is the legacy alias retained through the migration
     *  window. Either may be present on a given doc; readers prefer
     *  tier_max with a fallback. */
    tiered_schedule?: Array<{
      tier_max?: number | null;
      up_to_kwh?: number | null;
      energy_rate: number;
    }>;
  };
  tax_rate: number;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function fetchTariffs(): Promise<Tariff[]> {
  const res = await api.get('tariffs').json<ApiResponse<Tariff[]>>();
  return res.data;
}

export async function createTariff(body: Partial<Tariff>): Promise<Tariff> {
  const res = await api.post('tariffs', { json: body }).json<ApiResponse<Tariff>>();
  return res.data;
}

export async function updateTariff(id: string, body: Partial<Tariff>): Promise<Tariff> {
  const res = await api.patch(`tariffs/${id}`, { json: body }).json<ApiResponse<Tariff>>();
  return res.data;
}

export async function deleteTariff(id: string): Promise<void> {
  await api.delete(`tariffs/${id}`);
}

// ── Pricing: a location's default tariff and a charger's own (2026-10-25) ──
// GET/PATCH /locations/:id/tariff and /chargers/:id/tariff. The PATCH carries
// what the editor loaded; a record changed since is a 409 whose body holds the
// current view (see errorView below).
export async function fetchLocationTariff(id: string): Promise<LocationTariffView> {
  const res = await api.get(`locations/${id}/tariff`).json<ApiResponse<LocationTariffView>>();
  return res.data;
}

export async function updateLocationTariff(
  id: string,
  body: { default_tariff_id: string | null; expected_default_tariff_id: string | null },
): Promise<LocationTariffView> {
  const res = await api.patch(`locations/${id}/tariff`, { json: body }).json<ApiResponse<LocationTariffView>>();
  return res.data;
}

export async function fetchChargerTariff(id: string): Promise<ChargerTariffView> {
  const res = await api.get(`chargers/${id}/tariff`).json<ApiResponse<ChargerTariffView>>();
  return res.data;
}

export async function updateChargerTariff(
  id: string,
  body: { tariff_id: string | null; expected_tariff_id: string | null },
): Promise<ChargerTariffView> {
  const res = await api.patch(`chargers/${id}/tariff`, { json: body }).json<ApiResponse<ChargerTariffView>>();
  return res.data;
}

/** The current view a 409 TARIFF_CHANGED carries, or null. Non-throwing. */
export async function tariffConflictView<T>(err: unknown): Promise<T | null> {
  const response = (err as { response?: Response }).response;
  if (!response || response.status !== 409) return null;
  try {
    const body = await response.clone().json() as { data?: T };
    return body?.data ?? null;
  } catch {
    return null;
  }
}

export async function assignTariff(
  tariffId: string,
  body: { target_type: 'location' | 'charger'; target_id: string }
): Promise<{ message: string }> {
  const res = await api
    .post(`tariffs/${tariffId}/assign`, { json: body })
    .json<ApiResponse<{ message: string }>>();
  return res.data;
}

// ── Sessions ─────────────────────────────────────────────────────────────────

/** Who started a session (apps/api lib/sessionDriverRef.ts). profile_id is
 *  set only when the driver profile opens for this caller. */
export interface SessionDriverRef {
  kind: 'driver' | 'fleet' | 'rfid' | 'vehicle' | 'commissioning' | 'none';
  label: string;
  profile_id: string | null;
}

export interface SessionCost {
  energy_cost: number; // cents
  time_cost: number;
  flat_fee: number;
  tax: number;
  total: number; // cents
  currency: string;
}

export interface Session {
  idle_report?: IdleReport;
  idle_evidence?: SiteIdleEvidence | null;
  /** v1 history: billed by v1, labelled from v1's own payment status. */
  mirrored_from_v1?: boolean;
  v1_payment_status?: string | null;
  /** The flip clean-up's collection of a v1 session (staff copy). */
  v1_cleanup?: { payment_intent?: string | null } | null;
  /** The same fact on a Site caller's copy, without payment ids. */
  v1_collected?: boolean | null;
  _id: string;
  tenant_id: string;
  charger_id: string;
  connector_id: number;
  location_id: string;
  user_id?: string;
  /** Who charged: driver display name with email fallback. Hydrated
   *  server-side for admin callers only (name + email join, no other
   *  PII); absent on driver-scoped reads. */
  driver_name?: string | null;
  /** Who started it, for the Driver column (item 9, 2026-10-23). Only for
   *  a caller who can open the driver profile (the Api's User:read gate,
   *  never a Site or Sales Partner); absent for everyone else. */
  driver?: SessionDriverRef;
  /** 'vid' when rfid_tag is a masked Autocharge vehicle id. */
  credential_kind?: 'rfid' | 'vid';
  rfid_tag?: string;
  transaction_id: string;
  status: 'active' | 'completed' | 'failed' | 'stopped_by_user' | 'stopped_by_system';
  started_at: string;
  ended_at?: string;
  energy_kwh: number;
  // Instantaneous power mirrored at the session root from the latest
  // MeterValues frame (0 after the session ends). Saves consumers
  // from reaching into meter_values[-1] to render live telemetry.
  power_kw: number;
  duration_minutes: number;
  meter_start: number;
  meter_stop?: number;
  cost: SessionCost;
  /** EVI's connection fee pinned at session start (Tier 2). A NUMBER,
   *  0 included, means the session is pinned and cost.flat_fee is EVI's
   *  connection fee as billed (0 on a session that could not bill).
   *  null/absent means a legacy session, whose cost.flat_fee is the
   *  tariff's old session fee. */
  connection_fee_at_start_cents?: number | null;
  stripe_payment_intent_id?: string;
  /** Payment facts the Payment label reads (lib/sessionPayment.ts). Staff
   *  rows carry them as stored; a Site caller's row carries none. */
  pre_auth_payment_intent_id?: string | null;
  payment_status?: string | null;
  stripe_receipt_url?: string | null;
  wallet_debited?: boolean;
  wallet_debit_cents?: number;
  manual_review_required?: boolean;
  /** Timestamp of the last MeterValues frame — the silence anchor the
   *  stalled-session machinery keys on. Absent on sessions that never
   *  produced a frame. */
  last_meter_value_at?: string;
  /** Sticky stall flag set by the watchdog at 30 min of meter silence
   *  (never cleared on stream resume — pair with current silence). */
  needs_force_stop?: boolean;
  /** Reconciler probe counter; at/above the cap (5) the session is
   *  PARKED for ops and force-close eligible even if streaming. */
  reconcile_probe_count?: number;
  meter_values: Array<{
    timestamp: string;
    energy_kwh: number;
    power_kw: number;
    soc_percent?: number;
    voltage?: number;
    current?: number;
  }>;
}

export interface SessionDetail extends Session {
  peak_power_kw?: number | null;
  /** What happened (session success): staff read the whole outcome. */
  outcome?: import('@/lib/sessionOutcome').SessionOutcomeRead | null;
  /** What happened, as a Site reads it: the cause and its owner only. */
  outcome_summary?: SessionOutcomeSummary | null;
  idle_policy_at_start?: IdlePolicy | null;
  idle_view?: SessionIdleView | null;
  /** Live server-resolved staff role; payment reads have their own scope guard. */
  can_read_payments?: boolean;
  charger_identity: string;
  charger_label: string;
  location_name: string;
  /** Item 4b — payout summary surfaced for the under-cover warning.
   *  Null when no Payout doc exists yet (active session, or legacy
   *  rows that predate StopTransaction's payout creation). */
  payout_summary?: {
    owner_payout_cents: number;
    gross_amount_cents: number;
  } | null;
  /** Phase 5 multi-window TOU segmentation. Populated on sessions
   *  that ran across a Location with inline TOU windows. Absent /
   *  empty array for flat-rate sessions and pre-Phase-5 rows. The
   *  admin Session-detail page renders a breakdown table below the
   *  cost breakdown when length > 1. Date fields serialize as ISO
   *  strings — convert with new Date(...) for display. */
  cost_segments?: Array<{
    start_at: string;
    end_at: string;
    duration_minutes: number;
    energy_kwh: number;
    rate_cents: number;
    rate_type: 'per_kwh' | 'per_hour';
    window_label: string;
    cost_cents: number;
  }>;
  /** Refund state (HIGH 4.1 / 7.1). Null when no Payout doc exists yet.
   *  Drives the admin Session-detail refund button + status section. */
  refund_state?: {
    payout_status: string | null;
    has_payment_intent: boolean;
    refund_status: 'pending' | 'completed' | 'failed_partial' | null;
    refund_id: string | null;
    refund_amount_cents: number | null;
    refund_reason: string | null;
    refund_initiated_by: 'admin' | 'stripe_dashboard' | null;
    refunded_at: string | null;
  } | null;
  /** Collect and hold (2026-09-27). Spread from the Session doc by
   *  GET /sessions/:id. Set when settlement refused the split because
   *  the Site's electricity reimbursement exceeded the session's energy
   *  revenue: the driver paid in full, the whole charge sits on EVI's
   *  balance, and a human pays the host and Site later. Absent on every
   *  ordinary session. */
  payout_hold?: {
    reason: 'electricity_exceeds_energy_revenue';
    electricity_cost_cents: number;
    energy_revenue_cents: number;
    held_at: string;
  } | null;
}

export async function fetchSessions(params?: {
  idle_filter?: import('@evi/shared').IdleFeeFilter;
  sort_by?: 'idle_fee';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  status?: string;
  charger_id?: string;
  location_id?: string;
  from?: string;
  to?: string;
  /** Reporting v2 slice 1 — the shared filter set (serialized to the
   *  same wire params every report endpoint accepts). */
  filters?: ReportFilterParams | null;
}): Promise<PaginatedResponse<Session>> {
  const searchParams = new URLSearchParams();
  if (params) {
    const { filters, ...rest } = params;
    for (const [k, v] of Object.entries(rest)) {
      if (v !== undefined && v !== '') searchParams.set(k, String(v));
    }
    for (const [k, v] of Object.entries(reportFilterSearchParams(filters))) {
      searchParams.set(k, v);
    }
  }
  return api.get('sessions', { searchParams }).json<PaginatedResponse<Session>>();
}

// ── Location groups (Reporting v2 slice 1) ─────────────────────────
// Tenant-shared named location sets for the reports Advanced filters.

export interface LocationGroupRow {
  _id: string;
  name: string;
  location_ids: string[];
}

export async function fetchLocationGroups(): Promise<LocationGroupRow[]> {
  const res = await api.get('admin/location-groups').json<ApiResponse<LocationGroupRow[]>>();
  return res.data;
}

export async function createLocationGroup(
  name: string, locationIds: readonly string[],
): Promise<LocationGroupRow> {
  const res = await api
    .post('admin/location-groups', { json: { name, location_ids: locationIds } })
    .json<ApiResponse<LocationGroupRow>>();
  return res.data;
}

export async function updateLocationGroup(
  id: string, patch: { name?: string; location_ids?: readonly string[] },
): Promise<LocationGroupRow> {
  const res = await api
    .patch(`admin/location-groups/${id}`, { json: patch })
    .json<ApiResponse<LocationGroupRow>>();
  return res.data;
}

export async function deleteLocationGroup(id: string): Promise<void> {
  await api.delete(`admin/location-groups/${id}`).json<ApiResponse<{ deleted: boolean }>>();
}

/**
 * Tenant-wide list of currently-active sessions for the admin UI.
 *
 * IMPORTANT: must call `/sessions?status=active`, NOT `/sessions/active`.
 * The `/sessions/active` endpoint is user-scoped (driver PWA use case)
 * and returns only sessions owned by the logged-in user — an admin
 * watching a charger that another user is charging at would otherwise
 * see an empty "Active" tab even though the tenant has live sessions.
 *
 * Uses a high limit so all active sessions fit on one page; in
 * practice a tenant's concurrent-session count is bounded by their
 * charger fleet and will not page.
 */
export async function fetchActiveSessions(): Promise<Session[]> {
  const sp = new URLSearchParams({ status: 'active', limit: '100' });
  const res = await api
    .get('sessions', { searchParams: sp })
    .json<PaginatedResponse<Session>>();
  return res.data;
}

export async function fetchSession(id: string): Promise<SessionDetail> {
  const res = await api.get(`sessions/${id}`).json<ApiResponse<SessionDetail>>();
  return res.data;
}

export type SessionPayments = SessionPaymentsRead;
export async function fetchSessionPayments(id: string): Promise<SessionPayments> {
  return (await api.get(`sessions/${id}/payments`).json<ApiResponse<SessionPayments>>()).data;
}

/**
 * Admin refund (HIGH 4.1). Full refund only in v2 — no amount param.
 * Server stamps Payout.refund_status='pending' synchronously after the
 * Stripe refund returns; the charge.refunded webhook flips it to
 * 'completed' (or 'failed_partial' on resident-transfer reversal
 * failures) once Stripe settles the refund.
 *
 * Maps server error codes to user-meaningful messages in the calling
 * UI; this helper just surfaces the JSON shape.
 */
export interface RefundResponseData {
  /** Stripe refund state at creation time. The webhook flips Payout
   *  to 'completed' (or 'failed_partial') asynchronously. */
  status: 'pending' | 'completed' | 'failed_partial';
  refund_id: string;
  amount_cents: number;
}

export async function refundSession(
  id: string,
  reason: string,
): Promise<RefundResponseData> {
  const res = await api
    .post(`sessions/${id}/refund`, { json: { reason } })
    .json<ApiResponse<RefundResponseData>>();
  return res.data;
}

/**
 * Admin force-close for a stranded session. Server-guarded: active AND
 * (silent >30 min OR flagged-and-parked by the reconciler) — a healthy
 * session 422s regardless of role. Settles through the paywall-safe
 * closeStrandedSession primitive (wallet collects measured energy; a
 * PAYG hold is captured for the measured cost as a clean stop would).
 * 409 SESSION_ALREADY_SETTLED means a real stop won the race — branch
 * on it via getErrorCode and treat as a good outcome.
 */
export interface ForceCloseResult {
  mode: 'wallet' | 'payg_card' | 'payg_card_pending' | 'payg_card_dark' | 'no_meter_data' | 'unbillable' | 'fleet_billed';
  silent_minutes: number | null;
  cost_cents: number;
  collected_cents: number;
  uncollected_cost_cents: number;
  pre_auth: 'cancelled' | 'failed' | 'none';
  payout_created: boolean;
  manual_review_required: boolean;
}

export async function forceCloseSession(id: string): Promise<ForceCloseResult> {
  const res = await api
    .post(`admin/sessions/${id}/force-close`)
    .json<ApiResponse<ForceCloseResult>>();
  return res.data;
}

export interface SessionFramesMeta {
  limit: number;
  count: number;
  session_started_at: string;
  session_ended_at: string | null;
  session_status: Session['status'];
}

export interface SessionFramesResponse {
  data: OcppFrameRow[];
  meta: SessionFramesMeta;
}

export async function fetchSessionFrames(
  sessionId: string,
  params?: { limit?: number; since?: string },
): Promise<SessionFramesResponse> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.since) sp.set('since', params.since);
  const res = await api
    .get(`sessions/${sessionId}/frames`, { searchParams: sp })
    .json<{ success: true; data: OcppFrameRow[]; meta: SessionFramesMeta }>();
  return { data: res.data, meta: res.meta };
}

// ── Building Admin Reports (commits 4 + 5 server, commit 6 client) ──
//
// Two paginated reports backed by /admin/reports/booking-history and
// /admin/reports/location-units-consumed. Both endpoints honour
// requireMatrixPermission('Reports', 'read') + resolveDashboardScope,
// so the response is already scoped to the caller's persona
// (super_admin: all tenants; tenant_admin: own tenant; building_admin
// / third_party_admin: AccessAssignment locations).
//
// CSV downloads route through downloadAuthenticatedCsv (commit 1)
// with the path builders below — same Bearer-aware blob-fetch
// pattern every other admin CSV consumer uses post-cleanup.

export type SessionStatusRollup = 'in_progress' | 'completed' | 'cancelled' | 'failed';

export interface BookingHistoryRow {
  session_id: string;
  customer_name: string | null;
  tenant_name: string | null;
  charger_identity: string | null;
  status: SessionStatusRollup;
  started_at: string;
  amount_cents: number;
  energy_kwh: number;
  duration_minutes: number;
}

export interface BookingHistoryResponse {
  rows: BookingHistoryRow[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface BookingHistoryParams {
  location_ids?: readonly string[];
  from: string;
  to: string;
  location_id?: string;
  status?: SessionStatusRollup;
  page?: number;
  limit?: number;
}

function bookingHistoryQuery(params: BookingHistoryParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('from', params.from);
  sp.set('to', params.to);
  if (params.location_id) sp.set('location_id', params.location_id);
  if (params.status) sp.set('status', params.status);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.location_ids?.length) sp.set('location_ids', params.location_ids.join(','));
  return sp;
}

export async function fetchBookingHistory(
  params: BookingHistoryParams,
): Promise<BookingHistoryResponse> {
  const res = await api
    .get('admin/reports/booking-history', { searchParams: bookingHistoryQuery(params) })
    .json<ApiResponse<BookingHistoryResponse>>();
  return res.data;
}

/** Build the path+query string for the booking-history CSV export.
 *  Returns a ky-relative path (no BASE_URL prefix) for direct use
 *  with downloadAuthenticatedCsv. */
export function bookingHistoryCsvPath(
  params: Omit<BookingHistoryParams, 'page' | 'limit'>,
): { path: string; query: Record<string, string | undefined> } {
  return {
    path: 'admin/reports/booking-history/export.csv',
    query: {
      location_ids: params.location_ids?.length ? params.location_ids.join(',') : undefined,
      from: params.from,
      to: params.to,
      location_id: params.location_id,
      status: params.status,
    },
  };
}

export type LocationUnitsSort =
  | 'total_kwh_desc'
  | 'sessions_desc'
  | 'last_session_desc'
  | 'name_asc';

export interface LocationUnitsRow {
  location_id: string;
  location_name: string;
  address_short: string | null;
  session_count: number;
  total_kwh: number;
  avg_kwh: number;
  unique_drivers: number;
  last_session_at: string | null;
}

export interface LocationUnitsKpis {
  total_kwh: number;
  total_sessions: number;
  avg_kwh_per_session: number;
  active_locations: number;
}

export interface LocationUnitsResponse {
  rows: LocationUnitsRow[];
  kpis: LocationUnitsKpis;
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface LocationUnitsParams {
  location_ids?: readonly string[];
  from: string;
  to: string;
  sort?: LocationUnitsSort;
  page?: number;
  limit?: number;
}

function locationUnitsQuery(params: LocationUnitsParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('from', params.from);
  sp.set('to', params.to);
  if (params.sort) sp.set('sort', params.sort);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.location_ids?.length) sp.set('location_ids', params.location_ids.join(','));
  return sp;
}

export async function fetchLocationUnitsConsumed(
  params: LocationUnitsParams,
): Promise<LocationUnitsResponse> {
  const res = await api
    .get('admin/reports/location-units-consumed', { searchParams: locationUnitsQuery(params) })
    .json<ApiResponse<LocationUnitsResponse>>();
  return res.data;
}

/** Build the path+query string for the location-units CSV export.
 *  Same shape as bookingHistoryCsvPath — returns a ky-relative path
 *  for downloadAuthenticatedCsv. */
export function locationUnitsCsvPath(
  params: Omit<LocationUnitsParams, 'page' | 'limit'>,
): { path: string; query: Record<string, string | undefined> } {
  return {
    path: 'admin/reports/location-units-consumed/export.csv',
    query: {
      location_ids: params.location_ids?.length ? params.location_ids.join(',') : undefined,
      from: params.from,
      to: params.to,
      sort: params.sort,
    },
  };
}

// exportSessionsCsvUrl was deleted in admin-export-auth-cleanup.
// The old <a href={exportSessionsCsvUrl()} download> pattern 401'd
// because top-level browser navigation can't carry the Bearer token.
// All three Sessions CSV consumers (operator ReportsPage filter bar,
// operator SessionsPage, SP ReportsPage SessionsTab) now route
// through downloadAuthenticatedCsv (apps/admin/src/lib/
// downloadAuthenticatedCsv.ts) which calls api.get('sessions/export'
// + query) through the authenticated ky client. The path-builder is
// inlined at each call site because the query params differ per
// consumer (operator + SP pass period + location filters; the
// standalone SessionsPage exports everything).

// ── /me account surface (Phase 7g.4b.4 Commit 2) ─────────────────────
//
// Admin-side helpers for the SP profile page. Shape mirrors the PWA's
// equivalents at apps/web/src/lib/api.ts. Phone helper duplicated
// rather than imported across apps because the two app bundles are
// separate; sharing through @evi/shared would be the cleanup if more
// /me endpoints land on admin.

/** Resident-tier User fields not exposed by the baseline /auth/me
 *  projection. Mirror of the PWA's AccountView at
 *  apps/web/src/lib/api.ts:2101. The SP profile page reads `phone`
 *  off this shape; the disclaimer + privacy fields are unused on
 *  admin but stay in the type so the server response deserializes
 *  cleanly. */
export interface AccountView {
  phone: string | null;
  first_resident_disclaimer_acked_at: string | null;
  first_pass_list_recipient_disclaimer_acked_at: string | null;
  current_tenant: {
    _id: string;
    name: string;
    slug: string;
    is_default: boolean;
  } | null;
  discoverable_by_username: boolean;
}

/** Read the caller's resident-tier account view. Seeds the SP profile
 *  page's phone state since PublicUser does not carry phone. */
export async function fetchMyAccount(): Promise<AccountView> {
  const res = await api.get('me/account').json<ApiResponse<AccountView>>();
  return res.data;
}

/** Update the caller's display name. Phase 7g.4b.4 added the
 *  PATCH /me/name endpoint as a sibling to /me/phone; the response
 *  is a narrow `{ name }` payload rather than the full AccountView. */
export async function updateMyName(name: string): Promise<{ name: string }> {
  const res = await api
    .patch('me/name', { json: { name } })
    .json<ApiResponse<{ name: string }>>();
  return res.data;
}

/** Update the caller's phone number (E.164). Server enforces the
 *  same regex User.phone uses; a malformed phone returns a 400. */
export async function updateMyPhone(phone: string): Promise<AccountView> {
  const res = await api
    .patch('me/phone', { json: { phone } })
    .json<ApiResponse<AccountView>>();
  return res.data;
}

// ── Download paths (Warren, 29 Oct 2026) ──────────────────────────────────────
//
// Ky-relative paths for the Api's file downloads, for downloadAuthed
// (lib/downloadAuthed.ts). They used to be absolute `${BASE_URL}/...` URLs
// fed to plain `<a href download>` links, which cannot carry the Bearer
// token, so the Api answered 401 "Authentication required" on every driver
// profile, group modal and fleet Sessions export. The sales partner
// commission CSV and the payouts CSV builders had no callers left and are
// gone; their pages call downloadAuthenticatedCsv with the path inline.

function datedPath(path: string, opts?: { from?: string; to?: string }): string {
  const sp = new URLSearchParams();
  if (opts?.from) sp.set('from', opts.from);
  if (opts?.to) sp.set('to', opts.to);
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * The per-driver invoices CSV export. Optional date range narrows the
 * export window; absent params = full history (capped server-side at
 * 10000 rows). The server's Content-Disposition names the file.
 */
export function driverInvoicesCsvPath(driverId: string, opts?: { from?: string; to?: string }): string {
  return datedPath(`admin/drivers/${driverId}/invoices/export`, opts);
}

/**
 * The per-group invoices CSV export. Aggregates sessions across every
 * member of the group, with driver_id / driver_name / driver_email columns
 * identifying which driver each row belongs to. Empty groups return a
 * header-only CSV so the download path is uniform regardless of membership.
 */
export function groupInvoicesCsvPath(groupId: string, opts?: { from?: string; to?: string }): string {
  return datedPath(`admin/groups/${groupId}/invoices/export`, opts);
}

/**
 * The per-driver branded PDF report. Same scoping and date range as the
 * CSV export, rendered as an executive-summary PDF (no receipt URLs; those
 * stay CSV-only for privacy). Filename prefix "report-" rather than
 * "invoices-", so the two formats are easy to tell apart.
 */
export function driverReportPdfPath(driverId: string, opts?: { from?: string; to?: string }): string {
  return datedPath(`admin/drivers/${driverId}/reports/pdf`, opts);
}

/**
 * The per-group branded PDF report: every member's sessions with per-row
 * driver attribution and one grand total. Same privacy stance and filename
 * prefix as the per-driver report.
 */
export function groupReportPdfPath(groupId: string, opts?: { from?: string; to?: string }): string {
  return datedPath(`admin/groups/${groupId}/reports/pdf`, opts);
}

/**
 * One finished session's receipt PDF from the driver profile's session
 * list: date, location, kWh, amount, currency and the card's last 4. The
 * server names it receipt-<date>-<short session id>.pdf.
 */
export function driverSessionReceiptPath(driverId: string, sessionId: string): string {
  return `admin/drivers/${driverId}/sessions/${sessionId}/receipt.pdf`;
}

/** The receipt's name when the server sends none: the start date (UTC)
 *  and the session id's last 8 characters. */
export function driverSessionReceiptFallbackName(sessionId: string, startedAt: string | null): string {
  const date = startedAt && /^\d{4}-\d{2}-\d{2}/.test(startedAt) ? startedAt.slice(0, 10) : 'undated';
  return `receipt-${date}-${sessionId.slice(-8)}.pdf`;
}

// ── Billing ───────────────────────────────────────────────────────────────────

export interface RevenueSummary {
  total_revenue_cents: number;
  stripe_fees_cents: number;
  /** Electricity, platform fees and owner payouts are over ordinary
   *  payouts only: a held payout's breakdown carries the whole charge as
   *  platform fee (collect and hold, 2026-09-27), reported below instead. */
  electricity_cost_cents: number;
  platform_fees_cents: number;
  owner_payouts_cents: number;
  total_energy_kwh: number;
  session_count: number;
  /** Held payouts in the window (absent from an older API: read as 0). */
  held_count?: number;
  /** Their gross: what the drivers were charged. */
  held_cents?: number;
  /** What EVI actually holds for them: gross less Stripe's fee. The
   *  waterfall line; gross − Stripe − held net − electricity − platform
   *  = owner payouts. */
  held_net_cents?: number;
  currency: string;
  from: string;
  to: string;
}

export async function fetchRevenue(params?: {
  from?: string;
  to?: string;
  location_id?: string;
  charger_id?: string;
}): Promise<RevenueSummary> {
  const searchParams = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v) searchParams.set(k, v);
  }
  const res = await api.get('billing/revenue', { searchParams }).json<ApiResponse<RevenueSummary>>();
  return res.data;
}

export interface Transaction {
  session_id: string;
  transaction_id: string;
  amount_cents: number;
  status: 'paid' | 'pending' | 'failed' | 'refunded';
  stripe_payment_intent_id?: string;
  energy_kwh: number;
  started_at: string;
  ended_at?: string;
  rfid_tag?: string;
  charger_id: string;
}

export async function fetchTransactions(params?: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<PaginatedResponse<Transaction>> {
  const searchParams = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') searchParams.set(k, String(v));
  }
  return api.get('billing/transactions', { searchParams }).json<PaginatedResponse<Transaction>>();
}

export interface Payout {
  host_cash_by_currency?: import('@evi/shared').CashAmount[] | null;
  idle_subtotal_cents?: number | null;
  idle_report?: IdleReport | null;
  payment_legs?: Array<{ id: string; kind: string; charge_id: string | null; transfer_id: string | null }>;
  transfer_ids?: string[];
  _id: string;
  session_id: string;
  status: string;
  breakdown: {
    site_fee_cents?:number;
    site_fee_tax_cents?:number;
    site_processing_cents?:number;
    site_share_cents?:number;
    standard_gross_cents?:number;
    gross_amount_cents: number;
    stripe_fee_cents: number;
    net_revenue_cents: number;
    electricity_cost_cents: number;
    revenue_after_electricity_cents: number;
    /** EVI's whole take: connection fee + percentage part - any
     *  EVI-funded discount. */
    platform_fee_cents: number;
    owner_payout_cents: number;
    /** EVI's connection fee, kept whole and INCLUDED in
     *  platform_fee_cents. Absent on payouts written before Tier 2. */
    connection_fee_cents?: number;
    /** An EVI coupon / points credit, funded from EVI's percentage part
     *  only; gross_amount_cents is what the card was charged after it.
     *  Absent on payouts written before Tier 2. */
    evi_funded_discount_cents?: number;
    platform_percentage: number;
    electricity_rate_cents: number;
    energy_kwh: number;
    currency: string;
  };
  stripe_payment_intent_id?: string;
  stripe_transfer_id?: string;
  paid_at?: string;
  transferred_at?: string;
  created_at: string;
  /** Collect and hold (2026-09-27): settlement refused the split because
   *  the Site's electricity reimbursement exceeded the session's energy
   *  revenue. The breakdown then carries the WHOLE charge as
   *  platform_fee_cents and 0 to the host and Site, until a human pays
   *  them; held_* are the numbers the refused split was computed from.
   *  null (or absent, from an older API) on every ordinary payout. */
  hold_reason?: 'electricity_exceeds_energy_revenue' | null;
  held_electricity_cost_cents?: number | null;
  held_energy_revenue_cents?: number | null;
}

export async function fetchPayouts(
  params?: {
    page?: number;
    limit?: number;
    status?: string;
    location_id?: string;
    /** ISO timestamps. The ledger ignored the period before 2026-09-24,
     *  so a report tab's period pills moved the KPI tiles and left the
     *  table showing all time. */
    from?: string;
    to?: string;
  },
  filters?: ReportFilterParams | null,
): Promise<PaginatedResponse<Payout>> {
  const searchParams = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') searchParams.set(k, String(v));
  }
  for (const [k, v] of Object.entries(reportFilterSearchParams(filters))) {
    searchParams.set(k, v);
  }
  return api.get('billing/payouts', { searchParams }).json<PaginatedResponse<Payout>>();
}

/** The query the payouts CSV export needs, so the file matches the view.
 *
 *  Returns params rather than a URL on purpose: the old
 *  exportPayoutsCsvUrl fed an `<a href download>`, which carries cookies
 *  but cannot attach the JS-held Bearer token the auth plugin reads —
 *  so it 401s. Callers pass this to downloadAuthenticatedCsv. */
export function payoutsCsvQuery(
  params?: { status?: string; location_id?: string; from?: string; to?: string },
  filters?: ReportFilterParams | null,
): Record<string, string> {
  const out: Record<string, string> = { format: 'csv' };
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') out[k] = String(v);
  }
  return { ...out, ...reportFilterSearchParams(filters) };
}

// ── Store orders (admin) ─────────────────────────────────────────────────────
//
// Admin surface for storefront orders (apps/api/src/routes/admin/orders.ts).
// Gated by the Billing matrix resource — read for list/detail/CSV, update
// for the lifecycle PATCH — so the same operators who see Revenue/Payouts/
// Transactions see Orders. Money fields are integer cents; provisioning_state
// is computed live server-side (Tenant.provisioned_order_id lookup keyed on
// the order number), never stored on the order.

export interface AdminOrderRow {
  id: string;
  order_number: string;
  /** Buy-now lifecycle statuses (paid/processing/shipped/delivered) are
   *  PATCHable; anything else (pending, cancelled, quote motion) is
   *  read-only on this surface. */
  status: string;
  checkout_flow: string;
  created_at: string;
  total_cents: number;
  subtotal_cents: number;
  shipping_cents: number;
  tax_cents: number;
  buyer_name: string | null;
  buyer_email: string | null;
  sales_partner_name: string | null;
  sales_partner_email: string | null;
  provisioning_state: 'not_required' | 'pending' | 'provisioned';
  provisioned_tenant_id: string | null;
  tracking_number: string | null;
  tracking_carrier: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  line_items: Array<{
    product_id: string;
    part_number: string;
    name: string;
    unit_price_cents: number;
    quantity: number;
    line_total_cents: number;
    cost_snapshot?: { unit_cost_cents: number; currency: string; absorbed_autel_fee_cents: number; recorded_at: string };
  }>;
  shipping_address: {
    name: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  } | null;
  status_history: Array<{
    from: string;
    to: string;
    at: string;
    by_user_id: string;
    reason?: string;
  }>;
  notes: string | null;
  /** Cumulative amount refunded, in cents (0 until a refund lands). */
  refunded_cents: number;
  refund_status: string;
  refunded_at: string | null;
}

export interface AdminOrderRefundResult {
  status: 'queued' | 'refunded' | 'partially_refunded';
  amount_cents: number;
  message?: string;
  order?: AdminOrderRow;
}

/**
 * Refund a store order. The server computes the amount (full remaining unless a
 * partial amount_cents is given) and gates on Billing:update plus the refund
 * money bar. Refunds at or above the second-approver threshold return status
 * 'queued' (a distinct second admin must approve via the agent-action queue);
 * below it they execute and return 'refunded' / 'partially_refunded'.
 */
export async function refundAdminOrder(
  id: string,
  reason: string,
  amountCents?: number,
): Promise<AdminOrderRefundResult> {
  const res = await api
    .post(`admin/orders/${id}/refund`, {
      json: { reason, ...(amountCents !== undefined ? { amount_cents: amountCents } : {}) },
    })
    .json<ApiResponse<AdminOrderRefundResult>>();
  return res.data;
}

export async function fetchAdminOrders(params?: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<PaginatedResponse<AdminOrderRow>> {
  const searchParams = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') searchParams.set(k, String(v));
  }
  return api.get('admin/orders', { searchParams }).json<PaginatedResponse<AdminOrderRow>>();
}

export async function fetchAdminOrder(id: string): Promise<AdminOrderRow> {
  const res = await api.get(`admin/orders/${id}`).json<ApiResponse<AdminOrderRow>>();
  return res.data;
}

/**
 * Advance (or, with a reason, rewind) the buy-now lifecycle:
 * paid → processing → shipped → delivered. Server rules the UI
 * mirrors: shipped requires a tracking number (400 TRACKING_REQUIRED
 * unless the order already carries one), a backward move requires a
 * reason (400 REASON_REQUIRED), and statuses outside the lifecycle
 * 409 (ORDER_NOT_IN_LIFECYCLE). Callers should only include the
 * optional keys they actually set — the route schema rejects empty
 * strings (minLength) and unknown keys (additionalProperties: false).
 */
export async function updateAdminOrderStatus(
  id: string,
  body: {
    status: 'processing' | 'shipped' | 'delivered';
    tracking_number?: string;
    tracking_carrier?: string;
    reason?: string;
  },
): Promise<AdminOrderRow> {
  const res = await api
    .patch(`admin/orders/${id}/status`, { json: body })
    .json<ApiResponse<AdminOrderRow>>();
  return res.data;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Format a cents value as a currency string. */
export function formatCents(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

// ── Gamification ──────────────────────────────────────────────────────────────

export interface BadgeDefinition {
  _id: string;
  badge_id: string;
  name: string;
  description: string;
  icon_url?: string;
  trigger_type: 'session_count' | 'kwh_delivered' | 'streak_days' | 'spending_total' | 'referral_count' | 'special_event';
  threshold: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  is_active: boolean;
  awarded_count?: number;
  earned?: boolean;
  earned_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Rank {
  _id: string;
  name: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  min_points: number;
  min_kwh: number;
  min_sessions: number;
  perks: string[];
  skin_unlocks: string[];
  display_order: number;
}

export interface LeaderboardEntry {
  user_id: string;
  username?: string;
  avatar_style?: string;
  avatar_seed?: string;
  avatar_url?: string;
  current_rank?: string;
  points: number;
  total_kwh: number;
  total_sessions: number;
  position: number;
}

export interface GamificationProfile {
  user_id: string;
  username?: string;
  avatar_style?: string;
  avatar_seed?: string;
  avatar_url?: string;
  equipped_skin?: string;
  points: number;
  total_kwh: number;
  total_sessions: number;
  current_rank?: string;
  next_rank?: string;
  points_to_next_rank?: number;
  badges_earned: number;
  leaderboard_position?: number;
}

export async function fetchGamificationProfile(): Promise<GamificationProfile> {
  const res = await api.get('gamification/profile').json<ApiResponse<GamificationProfile>>();
  return res.data;
}

export async function fetchUserBadges(): Promise<BadgeDefinition[]> {
  const res = await api.get('gamification/badges').json<ApiResponse<BadgeDefinition[]>>();
  return res.data;
}

export async function fetchLeaderboard(scope: 'global' | 'tenant' = 'global', limit = 50): Promise<LeaderboardEntry[]> {
  const searchParams = new URLSearchParams({ scope, limit: String(limit) });
  const res = await api.get('gamification/leaderboard', { searchParams }).json<ApiResponse<LeaderboardEntry[]>>();
  return res.data;
}

export async function fetchRanks(): Promise<Rank[]> {
  const res = await api.get('gamification/ranks').json<ApiResponse<Rank[]>>();
  return res.data;
}

// ── Badge admin (CRUD) ───────────────────────────────────────────────────────

export async function fetchAllBadges(): Promise<BadgeDefinition[]> {
  const res = await api.get('badges').json<ApiResponse<BadgeDefinition[]>>();
  return res.data;
}

export async function createBadge(body: Omit<BadgeDefinition, '_id' | 'awarded_count' | 'created_at' | 'updated_at'>): Promise<BadgeDefinition> {
  const res = await api.post('badges', { json: body }).json<ApiResponse<BadgeDefinition>>();
  return res.data;
}

export async function updateBadge(badgeId: string, body: Partial<BadgeDefinition>): Promise<BadgeDefinition> {
  const res = await api.patch(`badges/${badgeId}`, { json: body }).json<ApiResponse<BadgeDefinition>>();
  return res.data;
}

export async function retireBadge(badgeId: string): Promise<void> {
  await api.post(`badges/${badgeId}/retire`);
}

// ── Maintenance windows ──────────────────────────────────────────────────────

export interface MaintenanceWindow {
  _id?: string;
  start: string;
  end: string;
  reason: string;
  created_by?: string;
  created_at?: string;
}

export async function scheduleMaintenance(
  chargerId: string,
  body: { start: string; end: string; reason: string }
): Promise<{ maintenance_windows: MaintenanceWindow[] }> {
  const res = await api
    .post(`chargers/${chargerId}/maintenance`, { json: body })
    .json<ApiResponse<{ maintenance_windows: MaintenanceWindow[] }>>();
  return res.data;
}

export async function cancelMaintenance(chargerId: string, windowId: string): Promise<void> {
  await api.delete(`chargers/${chargerId}/maintenance/${windowId}`);
}

// ── Bulk charger operations ──────────────────────────────────────────────────

export type BulkChargerAction = 'activate' | 'deactivate' | 'assign_tariff' | 'schedule_maintenance' | 'delete';

export async function bulkChargerAction(body: {
  action: BulkChargerAction;
  charger_ids: string[];
  tariff_id?: string;
  maintenance?: { start: string; end: string; reason: string };
}): Promise<{ matched: number; modified: number; message: string }> {
  const res = await api.post('chargers/bulk', { json: body }).json<ApiResponse<{ matched: number; modified: number; message: string }>>();
  return res.data;
}

// ── Coupons (admin) ──────────────────────────────────────────────────────────

export async function fetchCouponsAdmin(): Promise<CouponAdmin[]> {
  const res = await api
    .get('coupons/admin')
    .json<ApiResponse<{ coupons: CouponAdmin[] }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data.coupons;
}

export async function createCouponAdmin(body: CouponUpsertBody): Promise<CouponAdmin> {
  const res = await api
    .post('coupons/admin', { json: body })
    .json<ApiResponse<{ coupon: CouponAdmin }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data.coupon;
}

export async function updateCouponAdmin(
  id: string, body: Partial<CouponUpsertBody>,
): Promise<CouponAdmin> {
  const res = await api
    .patch(`coupons/admin/${id}`, { json: body })
    .json<ApiResponse<{ coupon: CouponAdmin }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data.coupon;
}

export async function toggleCouponAdmin(id: string): Promise<CouponAdmin> {
  const res = await api
    .post(`coupons/admin/${id}/toggle`)
    .json<ApiResponse<{ coupon: CouponAdmin }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data.coupon;
}

export async function deleteCouponAdmin(id: string): Promise<void> {
  const res = await api
    .delete(`coupons/admin/${id}`)
    .json<ApiResponse<{ deleted: boolean }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
}

// ── Announcements (admin) ────────────────────────────────────────────────────

export async function fetchAnnouncementsAdmin(): Promise<AnnouncementAdmin[]> {
  const res = await api
    .get('admin/announcements')
    .json<ApiResponse<AnnouncementAdmin[]>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function createAnnouncementAdmin(
  body: AnnouncementUpsertBody,
): Promise<AnnouncementAdmin> {
  const res = await api
    .post('admin/announcements', { json: body })
    .json<ApiResponse<AnnouncementAdmin>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function updateAnnouncementAdmin(
  id: string, body: Partial<AnnouncementUpsertBody>,
): Promise<AnnouncementAdmin> {
  const res = await api
    .patch(`admin/announcements/${id}`, { json: body })
    .json<ApiResponse<AnnouncementAdmin>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function deleteAnnouncementAdmin(id: string): Promise<void> {
  const res = await api
    .delete(`admin/announcements/${id}`)
    .json<ApiResponse<{ deleted: boolean }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
}

// ── Newsletter (admin: draft / review / approve / send) ──────────────────────
// All ride the guarded /admin/newsletter/* routes. Bodies carry STRUCTURED
// BLOCKS only; the server re-validates + renders + appends the CASL footer.
// approve is the HUMAN gate; send is the shared consent+footer+audit choke point.

export async function listNewslettersAdmin(): Promise<NewsletterListRow[]> {
  const res = await api
    .get('admin/newsletter')
    .json<ApiResponse<NewsletterListRow[]>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function getNewsletterAdmin(id: string): Promise<NewsletterDetail> {
  const res = await api
    .get(`admin/newsletter/${id}`)
    .json<ApiResponse<NewsletterDetail>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function createNewsletterAdmin(
  body: NewsletterCreateBody,
): Promise<NewsletterCreateResult> {
  const res = await api
    .post('admin/newsletter', { json: body })
    .json<ApiResponse<NewsletterCreateResult>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function updateNewsletterAdmin(
  id: string, body: NewsletterUpdateBody,
): Promise<{ id: string; status: string }> {
  const res = await api
    .patch(`admin/newsletter/${id}`, { json: body })
    .json<ApiResponse<{ id: string; status: string }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** HUMAN approval: draft -> approved. An agent has no path to this. Bound to
 *  the version the page showed: the Api answers 409 DRAFT_CHANGED when the
 *  draft changed since. */
export async function approveNewsletterAdmin(id: string, version: string): Promise<NewsletterApproveResult> {
  const res = await api
    .post(`admin/newsletter/${id}/approve`, { json: { version } })
    .json<ApiResponse<NewsletterApproveResult>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** The shared send choke point (same one the MCP newsletter_send tool hits).
 *  Answers 202 once the send has claimed the draft; the send runs on in the
 *  background, so poll getNewsletterStatusAdmin until the status changes.
 *  The first send names its slice ({ max_recipients }); the remainder is
 *  { send_remainder: true } (23 October 2026). */
export async function sendNewsletterAdmin(id: string, body: NewsletterSendBody): Promise<NewsletterSendStarted> {
  const res = await api
    .post(`admin/newsletter/${id}/send`, { json: body })
    .json<ApiResponse<NewsletterSendStarted>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** Campaign send runs (the agent's campaign_send), for the remainder a person sends. */
export async function listCampaignRunsAdmin(status?: CampaignRunRow['status']): Promise<CampaignRunRow[]> {
  const res = await api
    .get('admin/campaigns/runs', status ? { searchParams: { status } } : {})
    .json<ApiResponse<CampaignRunRow[]>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** A person sends the rest of a campaign whose first slice went out (202; runs in the background). */
export async function sendCampaignRemainderAdmin(runId: string): Promise<{ status: 'sending'; run_id: string }> {
  const res = await api
    .post(`admin/campaigns/runs/${runId}/send-remainder`)
    .json<ApiResponse<{ status: 'sending'; run_id: string }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** A newsletter's send status, without the reach preview: cheap to poll. */
export async function getNewsletterStatusAdmin(id: string): Promise<NewsletterSendStatus> {
  const res = await api
    .get(`admin/newsletter/${id}/status`)
    .json<ApiResponse<NewsletterSendStatus>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

// ── Blog (admin: draft / approve / publish) ───────────────────────────────────
// Rides the guarded /admin/blog/* routes. Bodies carry STRUCTURED BLOCKS only;
// the server re-validates + is the only thing that renders them. Approve and
// publish are HUMAN gates (no agent path exists for either).

export async function listBlogPostsAdmin(): Promise<BlogPostListRow[]> {
  const res = await api.get('admin/blog').json<ApiResponse<BlogPostListRow[]>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function getBlogPostAdmin(id: string): Promise<BlogPostDetail> {
  const res = await api.get(`admin/blog/${id}`).json<ApiResponse<BlogPostDetail>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function createBlogPostAdmin(body: BlogPostCreateBody): Promise<BlogPostDetail> {
  const res = await api.post('admin/blog', { json: body }).json<ApiResponse<BlogPostDetail>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function updateBlogPostAdmin(id: string, body: BlogPostUpdateBody): Promise<BlogPostDetail> {
  const res = await api.patch(`admin/blog/${id}`, { json: body }).json<ApiResponse<BlogPostDetail>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** HUMAN gate #1: draft -> approved (mints the site preview token). */
export async function approveBlogPostAdmin(id: string): Promise<BlogPostDetail> {
  const res = await api.post(`admin/blog/${id}/approve`).json<ApiResponse<BlogPostDetail>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** HUMAN gate #2: approved -> published (live on the public API). */
export async function publishBlogPostAdmin(id: string): Promise<BlogPostDetail> {
  const res = await api.post(`admin/blog/${id}/publish`).json<ApiResponse<BlogPostDetail>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function unpublishBlogPostAdmin(id: string): Promise<BlogPostDetail> {
  const res = await api.post(`admin/blog/${id}/unpublish`).json<ApiResponse<BlogPostDetail>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** Live web preview — the SAME server renderer the public page uses. */
export async function previewBlogHtmlAdmin(blocks: EmailBlock[]): Promise<string> {
  const res = await api.post('admin/blog/preview', { json: { blocks } }).json<ApiResponse<{ html: string }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data.html;
}

// ── Network submissions ──────────────────────────────────────────────────────

export async function listNetworkSubmissionsAdmin(
  status?: string,
): Promise<NetworkSubmissionAdmin[]> {
  const res = await api
    .get('admin/network-submissions', status ? { searchParams: { status } } : {})
    .json<ApiResponse<{ submissions: NetworkSubmissionAdmin[] }>>();
  return res.data.submissions;
}

export async function decideNetworkSubmission(
  id: string,
  body: {
    action: 'approve' | 'reject' | 'needs_info';
    note?: string;
    admittance?: 'no_auth' | 'password';
    tenantId?: string;
    /** needs_info only: also email a single-use edit link that reopens
     *  this submission. */
    reinvite?: boolean;
  },
): Promise<NetworkSubmissionAdmin> {
  const res = await api
    .post(`admin/network-submissions/${id}/decide`, { json: body })
    .json<ApiResponse<{ submission: NetworkSubmissionAdmin }>>();
  return res.data.submission;
}

/** A submission invite as the admin list shows it — status computed
 *  server-side (revoked > used > expired > pending). */
export interface SubmissionInviteAdmin {
  id: string;
  email: string;
  note: string | null;
  status: 'pending' | 'used' | 'expired' | 'revoked';
  tenantId: string | null;
  submissionId: string | null;
  resubmitOf: string | null;
  expiresAt: string;
  createdAt: string;
}

export async function issueSubmissionInvite(body: {
  email: string;
  note?: string;
  tenantId?: string;
}): Promise<{ invite: SubmissionInviteAdmin; emailSent: boolean }> {
  const res = await api
    .post('admin/network-submissions/invites', { json: body })
    .json<ApiResponse<{ invite: SubmissionInviteAdmin; emailSent: boolean }>>();
  return res.data;
}

export async function listSubmissionInvites(): Promise<SubmissionInviteAdmin[]> {
  const res = await api
    .get('admin/network-submissions/invites')
    .json<ApiResponse<{ invites: SubmissionInviteAdmin[] }>>();
  return res.data.invites;
}

export async function revokeSubmissionInvite(id: string): Promise<SubmissionInviteAdmin> {
  const res = await api
    .post(`admin/network-submissions/invites/${id}/revoke`)
    .json<ApiResponse<{ invite: SubmissionInviteAdmin }>>();
  return res.data.invite;
}

export async function submitNetworkSubmission(
  body: NetworkSubmissionCreateBody,
  requestKey?: string,
): Promise<{ submission: NetworkSubmissionMine; ocppUrl: string }> {
  const res = await api
    .post('network-submissions', { json: body, ...(requestKey ? { headers: { 'Idempotency-Key': requestKey } } : {}) })
    .json<ApiResponse<{ submission: NetworkSubmissionMine; ocppUrl: string }>>();
  return res.data;
}

export async function fetchMyNetworkSubmissions(): Promise<{
  submissions: NetworkSubmissionMine[];
  ocppUrl: string;
}> {
  const res = await api
    .get('network-submissions/mine')
    .json<ApiResponse<{ submissions: NetworkSubmissionMine[]; ocppUrl: string }>>();
  return res.data;
}

export async function checkSubmissionIdentities(
  identities: string[],
): Promise<Array<{ identity: string; kind: 'existing_charger' | 'pending_submission' }>> {
  const res = await api
    .post('network-submissions/check-identities', { json: { identities } })
    .json<ApiResponse<{ flags: Array<{ identity: string; kind: 'existing_charger' | 'pending_submission' }> }>>();
  return res.data.flags;
}

// ── Settings — reward points ─────────────────────────────────────────────────

export interface RewardPointsSettings {
  pointsPerKwh: number;
  sessionCompletionBonus: number;
  referralBonusReferrer: number;
  referralBonusReferee: number;
  minRedeemablePoints: number;
  pointsPerDollar: number;
  tierMultipliers: Record<string, number>;
  // Friends Stage 2 — referral tier milestones. See
  // packages/gamification/src/settings.ts for the full doc on
  // shape, defaults, and reader normalization rules.
  referralTiers: Array<{ sessionCount: number; points: number }>;
  referralMaxMilestonesPerFriend: number | null;  // null = unlimited
}

/** Server-resolved view also surfaces extra fields we don't
 *  edit through this UI (anti-abuse guards, streak controls,
 *  etc.) — captured here as an index signature so the GET
 *  response types cleanly without forcing the editable surface
 *  to enumerate every gamification knob. The form only reads
 *  the fields it edits; the rest passes through untouched on
 *  partial-merge save (the server's PUT route does shallow
 *  merge, so omitting a field preserves it). */
export type RewardPointsSettingsResponse = RewardPointsSettings & Record<string, unknown>;

export interface RewardPointsResponse {
  value: RewardPointsSettings;
  defaults?: RewardPointsSettings;
}

export async function fetchRewardPointsSettings(): Promise<RewardPointsResponse> {
  const res = await api
    .get('settings/reward-points')
    .json<ApiResponse<RewardPointsResponse>>();
  return res.data;
}

export async function updateRewardPointsSettings(
  patch: Partial<RewardPointsSettings>,
): Promise<RewardPointsSettings> {
  const res = await api
    .put('settings/reward-points', { json: patch })
    .json<ApiResponse<{ value: RewardPointsSettings }>>();
  return res.data.value;
}

export async function invalidateRewardPointsCache(): Promise<void> {
  await api.post('settings/reward-points/invalidate-cache');
}

// ── Settings — RFID registration pricing ─────────────────────────────────────

export type RfidRegistrationPayer = 'driver' | 'operator' | 'none';

export interface RfidRegistrationPrice {
  amount_cents: number;
  payer: RfidRegistrationPayer;
  /** No longer read by the platform (2026-09-25): driver
   *  self-registration is switched per BUILDING
   *  (Location.allow_resident_fob_registration). Kept on the type because
   *  stored settings still carry it. */
  enabled?: boolean;
}

/** Map of setup type (registration channel) to its one-time price. */
export type RfidPricingSettings = Record<string, RfidRegistrationPrice>;

export interface RfidPricingResponse {
  value: RfidPricingSettings;
  defaults?: RfidPricingSettings;
}

/** The fee POST /admin/rfid-cards would charge for THIS caller —
 *  channel resolved server-side (persona→channel mapping is not
 *  client-knowable). Feeds the create modal's disclosure line. */
export interface RfidRegistrationFee {
  channel: string;
  amount_cents: number;
  payer: RfidRegistrationPayer;
  charges: boolean;
}

export async function fetchRfidRegistrationFee(): Promise<RfidRegistrationFee> {
  const res = await api
    .get('admin/rfid-cards/registration-fee')
    .json<ApiResponse<RfidRegistrationFee>>();
  return res.data;
}

export async function fetchRfidPricingSettings(): Promise<RfidPricingResponse> {
  const res = await api
    .get('settings/rfid-pricing')
    .json<ApiResponse<RfidPricingResponse>>();
  return res.data;
}

export async function updateRfidPricingSettings(
  patch: Partial<RfidPricingSettings>,
): Promise<RfidPricingSettings> {
  const res = await api
    .put('settings/rfid-pricing', { json: patch })
    .json<ApiResponse<{ value: RfidPricingSettings }>>();
  return res.data.value;
}

export async function invalidateRfidPricingCache(): Promise<void> {
  await api.post('settings/rfid-pricing/invalidate-cache');
}

// ── Tenant settings (Tenant document fields) ─────────────────────────────────
//
// Lives on the Tenant doc itself rather than the generic `settings`
// collection. Today the only knob is allow_resident_self_registration
// (gates the resident-managed peer-to-peer charger flow). Future
// fields slot into the same shape — additive, no migration.

export interface TenantSettings {
  tenant_id: string;
  allow_resident_self_registration: boolean;
  /** Building-join code (Session 8.a). Null when no code is
   *  active. */
  join_code: string | null;
  join_code_generated_at: string | null;
  /** Constructed PWA share URL. Null when no code is active. */
  join_url: string | null;
  /** Phase 7c — Tenant-level platform admin fee (basis points).
   *  Null = no tenant override. resolveAdminFeeBps reads
   *  Location.revenue_split first, then this, then
   *  FALLBACK_ADMIN_FEE_BPS = 1500 (15%); every new session's pin is
   *  floored at 1500 whatever is stored.
   *  Mutated by SettingsPage's Platform Admin Fee section
   *  (super_admin only — server PATCH silently strips this field
   *  for non-super_admin callers). */
  admin_fee_bps: number | null;
  /** ISO date string of the last admin_fee_bps mutation. Null
   *  when never set. Drives the "Last changed <date>" line on
   *  the SettingsPage section. */
  admin_fee_changed_at: string | null;
  /** User._id string of the last actor to mutate admin_fee_bps.
   *  v1 renders the last 8 chars as a forensic suffix; v2 will
   *  resolve to user name when a User-by-id helper ships. */
  admin_fee_changed_by: string | null;
}

export async function fetchTenantSettings(tenantId: string): Promise<TenantSettings> {
  const res = await api
    .get(`admin/tenants/${tenantId}/settings`)
    .json<ApiResponse<{ value: TenantSettings }>>();
  return res.data.value;
}

export async function updateTenantSettings(
  tenantId: string,
  // Phase 7c — admin_fee_bps added. Server strips for non-super_admin
  // callers (silent strip, mirror of Phase 7b's Charger agent_id
  // pattern). Frontend gates the section render on isSuperAdmin so
  // the strip is defense-in-depth.
  patch: Partial<Pick<TenantSettings, 'allow_resident_self_registration' | 'admin_fee_bps'>>,
): Promise<TenantSettings> {
  const res = await api
    .patch(`admin/tenants/${tenantId}/settings`, { json: patch })
    .json<ApiResponse<{ value: TenantSettings }>>();
  return res.data.value;
}

// ── Building join code (Session 8.a) ─────────────────────────────

export interface JoinCodeResponse {
  join_code: string | null;
  join_code_generated_at?: string | null;
  join_url?: string;
}

/** Generate (or replace) the building's join code. Server retries
 *  up to 5 times on collision; failure surfaces as a 5xx the
 *  caller can show as a generic error. */
export async function regenerateTenantJoinCode(
  tenantId: string,
): Promise<JoinCodeResponse> {
  const res = await api
    .post(`admin/tenants/${tenantId}/join-code/regenerate`)
    .json<ApiResponse<JoinCodeResponse>>();
  return res.data;
}

/** Clear the active join code. Subsequent /join links 404. */
export async function clearTenantJoinCode(
  tenantId: string,
): Promise<JoinCodeResponse> {
  const res = await api
    .delete(`admin/tenants/${tenantId}/join-code`)
    .json<ApiResponse<JoinCodeResponse>>();
  return res.data;
}

/** How many users have joined this tenant via the join-code path.
 *  Read-only stat for the admin card. */
export async function fetchJoinCodeUsage(
  tenantId: string,
): Promise<{ joined_via_code_count: number }> {
  const res = await api
    .get(`admin/tenants/${tenantId}/join-code/usage`)
    .json<ApiResponse<{ joined_via_code_count: number }>>();
  return res.data;
}

// ── Per-Location join code (per-Location join codes commit) ────
//
// Twin of the Tenant-side helpers above, scoped to a single
// Location. Powers the commercial-operator case where one Tenant
// owns many Locations and each Location wants its own building-
// poster code (e.g. "Wilson Avenue" rather than "Wilson Operations
// Group"). Cross-collection codegen on the server guarantees a
// code never collides between Tenant and Location.

export interface LocationJoinCodeResponse {
  location_id: string;
  join_code: string | null;
  join_code_generated_at?: string | null;
  join_url?: string;
}

/** Generate (or replace) the Location's join code. The shared lib
 *  on the server runs a cross-collection check so the same string
 *  can never coexist on a Tenant and a Location at once. */
export async function regenerateLocationJoinCode(
  locationId: string,
): Promise<LocationJoinCodeResponse> {
  const res = await api
    .post(`admin/locations/${locationId}/join-code/regenerate`)
    .json<ApiResponse<LocationJoinCodeResponse>>();
  return res.data;
}

/** Clear the active Location join code. Subsequent /join links
 *  pointing at this Location 404. Idempotent on already-null. */
export async function clearLocationJoinCode(
  locationId: string,
): Promise<{ join_code: null }> {
  const res = await api
    .delete(`admin/locations/${locationId}/join-code`)
    .json<ApiResponse<{ join_code: null }>>();
  return res.data;
}

/** How many users have joined this Location via its code. Tracks
 *  the same audit-row shapes as the Tenant-side stat, filtered to
 *  Location-tier rows by payload.location_id. */
export async function fetchLocationJoinCodeUsage(
  locationId: string,
): Promise<{ joined_via_code_count: number }> {
  const res = await api
    .get(`admin/locations/${locationId}/join-code/usage`)
    .json<ApiResponse<{ joined_via_code_count: number }>>();
  return res.data;
}

// ── Gamification admin views ────────────────────────────────────────────────

export interface AdminFriendship {
  id: string;
  status: 'pending' | 'accepted' | 'declined' | 'blocked';
  requestedAt: string;
  acceptedAt: string | null;
  declinedAt: string | null;
  blockedAt: string | null;
  requester: { id: string; name: string; username: string | null; email: string | null };
  recipient: { id: string; name: string; username: string | null; email: string | null };
}
export async function fetchAdminFriendships(status?: string): Promise<AdminFriendship[]> {
  const sp = new URLSearchParams();
  if (status) sp.set('status', status);
  const res = await api.get('admin/friendships', { searchParams: sp })
    .json<ApiResponse<AdminFriendship[]>>();
  return res.data;
}
export async function voidAdminFriendship(id: string): Promise<void> {
  await api.delete(`admin/friendships/${id}`);
}

export interface AdminStreak {
  id: string;
  userId: string;
  userName: string;
  userUsername: string | null;
  streakCode: string;
  active: boolean;
  currentWeekCount: number;
  longestWeekCount: number;
  freezeOfferedAt: string | null;
  lastFreezeAt: string | null;
  lastEvaluatedAt: string | null;
  enrolledAt: string;
}
export async function fetchAdminStreaks(filter?: { code?: string; status?: string }): Promise<AdminStreak[]> {
  const sp = new URLSearchParams();
  if (filter?.code) sp.set('code', filter.code);
  if (filter?.status) sp.set('status', filter.status);
  const res = await api.get('admin/streaks', { searchParams: sp })
    .json<ApiResponse<AdminStreak[]>>();
  return res.data;
}

export interface AdminChallenge {
  id: string;
  userId: string;
  userName: string;
  challengeCode: string;
  status: 'active' | 'completed' | 'expired';
  currentValue: number;
  weekStart: string;
  weekEnd: string;
  completedAt: string | null;
  rewardAwarded: boolean;
}
export async function fetchAdminChallenges(filter?: { status?: string; week?: string }): Promise<AdminChallenge[]> {
  const sp = new URLSearchParams();
  if (filter?.status) sp.set('status', filter.status);
  if (filter?.week) sp.set('week', filter.week);
  const res = await api.get('admin/challenges', { searchParams: sp })
    .json<ApiResponse<AdminChallenge[]>>();
  return res.data;
}

export type ThemeUnlockKind =
  | 'default'
  | 'kwh_milestone'
  | 'session_milestone'
  | 'fleet_only'
  | 'admin_only';

export interface AdminUnlock {
  id: string;
  code: string;
  name: string;
  description: string;
  category: 'skin' | 'priority_booking' | 'lounge_access';
  pricePoints: number;
  metadata: Record<string, unknown>;
  isActive: boolean;
  tenantScoped: boolean;
  ownedCount: number;
  // Theme milestones — only meaningful for `category: 'skin'`.
  // Null when the doc predates the schema field; the admin form
  // surfaces this as "unset" and operators can flip it.
  unlockCriteriaKind: ThemeUnlockKind | null;
  unlockCriteriaThreshold: number | null;
}
export async function fetchAdminUnlocks(): Promise<AdminUnlock[]> {
  const res = await api.get('admin/unlocks').json<ApiResponse<AdminUnlock[]>>();
  return res.data;
}
export interface AdminUnlockUpsertBody {
  code: string;
  name: string;
  description?: string;
  category: AdminUnlock['category'];
  pricePoints: number;
  metadata?: Record<string, unknown>;
  isActive?: boolean;
  tenantScoped?: boolean;
  unlockCriteriaKind?: ThemeUnlockKind | null;
  unlockCriteriaThreshold?: number | null;
}
export async function createAdminUnlock(body: AdminUnlockUpsertBody): Promise<void> {
  await api.post('admin/unlocks', { json: body });
}
export async function updateAdminUnlock(id: string, body: Partial<AdminUnlockUpsertBody>): Promise<void> {
  await api.patch(`admin/unlocks/${id}`, { json: body });
}
export async function toggleAdminUnlock(id: string): Promise<void> {
  await api.post(`admin/unlocks/${id}/toggle`);
}

export interface AntiAbuseFlagRow {
  userId: string;
  sessionCount: number;
  legitCount: number;
  blockedCount: number;
  totalKwh: number;
  avgKwh: number;
  currentRank: string | null;
  totalPoints: number | null;
  referrals: number;
  flags: string[];
}
export interface AntiAbuseFlagsPayload {
  windowHours: number;
  since: string;
  flagged: AntiAbuseFlagRow[];
  total: number;
}
export async function fetchAntiAbuseFlags(windowHours = 24): Promise<AntiAbuseFlagsPayload> {
  const res = await api
    .get('admin/anti-abuse/flags', { searchParams: { windowHours: String(windowHours) } })
    .json<ApiResponse<AntiAbuseFlagsPayload>>();
  return res.data;
}

// ── Roles & Permissions (Phase 2.5) ─────────────────────────────────

/**
 * RE-EXPORTED from @evi/shared, no longer hand-copied.
 *
 * This was a hardcoded mirror "so the admin bundle doesn't pull
 * @evi/shared's full surface for one constant" — a rationale that had
 * already lapsed (this app imports matrixToPermissionStrings,
 * resolvePersonaId and PersonaId from the same package) and that cost
 * more than it saved.
 *
 * WHAT THE DRIFT ACTUALLY DID. The list had fallen SEVEN resources
 * behind: Fleet, Session, Location, AdminAgent, Announcement, Agent and
 * Alert. RolesPage builds its editor grid by mapping over this array
 * (RolesPage.tsx:465) and saves the result wholesale (:226) — so
 * opening ANY role in the Roles UI and pressing Save silently DELETED
 * every stored row for a resource missing here. Renaming the Fleet
 * Operator role would have stripped its Fleet, AdminAgent and Alert
 * grants and broken every fleet admin's menu and gates, with no error
 * and nothing in the diff to look at.
 *
 * The backend's isPermissionResource gate does not catch this: it
 * 400s on an UNKNOWN resource arriving, not on a known one going
 * missing. Drift in this direction is silent by construction, which is
 * why the copy is gone rather than merely corrected.
 */
import { PERMISSION_RESOURCES, type PermissionResource } from '@evi/shared';
export { PERMISSION_RESOURCES, type PermissionResource };

export type SystemRoleValue =
  | 'super_admin' | 'tenant_admin' | 'operator' | 'viewer' | 'third_party_admin'
  // Phase 2 fleet platform — mirrors @evi/db Role.ts SYSTEM_ROLE_VALUES.
  // Seeded as "Fleet Operator" (Phase 1a); scoped via AccessAssignment.fleet_ids.
  | 'fleet_admin';

export type RoleTemplateValue =
  | 'building_admin' | 'charger_owner' | 'property_manager'
  // Agent commission Phase 1b — mirrors @evi/db Role.ts addition.
  | 'sales_agent';

export interface PermissionMatrixEntry {
  resource: PermissionResource;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

export interface RoleView {
  _id: string;
  tenant_id: string | null;
  system_role: SystemRoleValue | null;
  template: RoleTemplateValue | null;
  name: string;
  permissions: PermissionMatrixEntry[];
  is_system: boolean;
  created_at: string;
  updated_at: string;
  assignment_count: number;
}

export async function fetchRoles(template?: RoleTemplateValue): Promise<RoleView[]> {
  const sp = new URLSearchParams();
  if (template) sp.set('template', template);
  const res = await api
    .get('admin/roles', { searchParams: sp })
    .json<ApiResponse<RoleView[]>>();
  return res.data;
}

export async function fetchRole(id: string): Promise<RoleView> {
  const res = await api.get(`admin/roles/${id}`).json<ApiResponse<RoleView>>();
  return res.data;
}

export interface CreateRoleBody {
  name: string;
  template: RoleTemplateValue;
  permissions?: PermissionMatrixEntry[];
}
export async function createRole(body: CreateRoleBody): Promise<RoleView> {
  const res = await api.post('admin/roles', { json: body }).json<ApiResponse<RoleView>>();
  return res.data;
}

export interface UpdateRoleBody {
  name?: string;
  permissions?: PermissionMatrixEntry[];
}
export async function updateRole(id: string, body: UpdateRoleBody): Promise<RoleView> {
  const res = await api.patch(`admin/roles/${id}`, { json: body }).json<ApiResponse<RoleView>>();
  return res.data;
}

export async function deleteRole(id: string): Promise<void> {
  await api.delete(`admin/roles/${id}`);
}

// ── Access Management (Phase 2.6) ───────────────────────────────────

export interface AccessSummary {
  user_id: string;
  user_name: string;
  user_email: string;
  role_id: string | null;
  role_name: string | null;
  system_role: string | null;
  has_implicit_all_access: boolean;
  /** True when the user has a third_party_admin role but no
   *  effective scope assigned. AccessPage list view bubbles
   *  these rows to the top + renders a "Needs scope" badge. */
  needs_scope: boolean;
  location_ids: string[];
  charger_ids: string[];
}

export interface AccessDetailView {
  user: { _id: string; name: string; email: string };
  role: { _id: string; name: string; system_role: string | null } | null;
  has_implicit_all_access: boolean;
  location_ids: string[];
  charger_ids: string[];
  available_locations: Array<{ _id: string; name: string }>;
  available_chargers: Array<{ _id: string; identity: string; location_id: string }>;
}

export async function fetchAccessList(): Promise<AccessSummary[]> {
  const res = await api.get('admin/access').json<ApiResponse<AccessSummary[]>>();
  return res.data;
}

export async function fetchAccessDetail(userId: string): Promise<AccessDetailView> {
  const res = await api.get(`admin/access/${userId}`).json<ApiResponse<AccessDetailView>>();
  return res.data;
}

export async function updateAccessAssignment(
  userId: string,
  body: { location_ids: string[]; charger_ids: string[] },
): Promise<{ user_id: string; tenant_id: string; location_ids: string[]; charger_ids: string[] }> {
  const res = await api.put(`admin/access/${userId}`, { json: body }).json<ApiResponse<{
    user_id: string;
    tenant_id: string;
    location_ids: string[];
    charger_ids: string[];
  }>>();
  return res.data;
}

export async function deleteAccessAssignment(userId: string): Promise<void> {
  await api.delete(`admin/access/${userId}`);
}

// ── Admin Users (Phase 2.7) ─────────────────────────────────────────

export type UserState = 'pending' | 'active' | 'expired' | 'deactivated';

export interface UserView {
  _id: string;
  name: string;
  email: string;
  is_active: boolean;
  state: UserState;
  invite_expires_at: string | null;
  last_login: string | null;
  role_id: string | null;
  role_name: string | null;
  system_role: string | null;
  /** Whether TOTP MFA is armed — drives the per-row 2FA badge
   *  (admin-MFA arc). The enrolled-at date is detail-only. */
  mfa_enabled: boolean;
  /** The list's answer for THIS viewer: may they Re-send (and so Copy) this
   *  teammate's invite? The Api's own gate (review finding B, 29 October
   *  2026); the Team page shows both actions on it alone. */
  can_resend_invite?: boolean;
}

export interface UserDetailView extends UserView {
  role: {
    _id: string;
    name: string;
    system_role: string | null;
    template: string | null;
  } | null;
  /** ISO timestamp enrollment completed, or null. Surfaced in the
   *  edit modal's detail panel. */
  mfa_enrolled_at: string | null;
}

export interface InviteUserBody {
  name: string;
  email: string;
  role_id: string;
  /** Escape hatch: create a NATIVE admin account (no driver identity)
   *  instead of the default grant path (a driver-shaped account + a role
   *  assignment). Accepted by the backend only from a super_admin caller;
   *  rejected 403 otherwise. */
  native?: boolean;
  /** Optional initial scope. Honored by the backend only when
   *  the picked role's system_role is 'third_party_admin' (the
   *  role tier whose authorization model REQUIRES scope to do
   *  anything meaningful). Other roles silently ignore this
   *  payload. Empty arrays are treated the same as omitting the
   *  field — no AccessAssignment is created. */
  scope?: { location_ids: string[]; charger_ids: string[] };
  /** Fleets the new fleet admin manages (AccessAssignment.fleet_ids).
   *  REQUIRED when the picked role's system_role is 'fleet_admin' — the
   *  backend answers 400 FLEET_REQUIRED without it, and rejects it for any
   *  other role. Every id must be a fleet in the caller's tenant (404
   *  otherwise). */
  fleet_ids?: string[];
}

export interface UpdateUserBody {
  name?: string;
  role_id?: string;
  is_active?: boolean;
}

export async function fetchAdminUsers(): Promise<UserView[]> {
  const res = await api.get('admin/users').json<ApiResponse<UserView[]>>();
  return res.data;
}

export async function fetchAdminUser(userId: string): Promise<UserDetailView> {
  const res = await api.get(`admin/users/${userId}`).json<ApiResponse<UserDetailView>>();
  return res.data;
}

export async function inviteAdminUser(body: InviteUserBody): Promise<UserDetailView> {
  const res = await api.post('admin/users', { json: body }).json<ApiResponse<UserDetailView>>();
  return res.data;
}

export async function updateAdminUser(
  userId: string,
  body: UpdateUserBody,
): Promise<UserDetailView> {
  const res = await api.patch(`admin/users/${userId}`, { json: body })
    .json<ApiResponse<UserDetailView>>();
  return res.data;
}

// ── Admin Drivers (Phase A) ─────────────────────────────────────────

export type DriverState = 'active' | 'locked_out' | 'deactivated';
export type DriverStateFilter = 'all' | DriverState;

/**
 * Activity filter for the drivers list. Anchored on
 * `last_session_date` (charging activity), not `last_login`
 * (app presence) — operators care about commercial activity,
 * not whether someone opened the PWA. Mirrors how the list
 * already sorts rows.
 *
 *   all           → no filter (default)
 *   last_7d       → last_session_date >= now - 7d
 *   last_30d      → last_session_date >= now - 30d
 *   last_90d      → last_session_date >= now - 90d
 *   inactive_30d  → last_session_date < now - 30d, OR null
 *   never         → last_session_date is null (driver registered
 *                   but has yet to charge a session)
 */
export type DriverActivityFilter =
  | 'all'
  | 'last_7d'
  | 'last_30d'
  | 'last_90d'
  | 'inactive_30d'
  | 'never';

/**
 * Joined-date filter for the drivers list. Anchored on
 * Mongoose's `created_at` timestamp on the User doc.
 */
export type DriverJoinedFilter =
  | 'all'
  | 'last_7d'
  | 'last_30d'
  | 'last_90d';

export interface DriverRow {
  _id: string;
  name: string;
  email: string;
  is_active: boolean;
  state: DriverState;
  last_login: string | null;
  /** End of the latest counted session, v3 or mirrored v1. */
  last_session_date: string | null;
  /** The latest of app activity, sign-in and any session start. Absent from an older Api. */
  last_seen_at?: string | null;
  /** Points balance from the ledger. */
  points: number;
  total_sessions: number;
  /** The USD wallet (legacy single-wallet field). */
  wallet_balance_cents: number;
  /** Every wallet the driver holds, one per currency, USD first, each in
   *  its own currency (2026-10-25). Absent from an older Api. */
  wallets?: Array<{ currency: string; balanceCents: number }>;
  payment_lockout_until: string | null;
  failed_charge_count: number;
}

export interface DriverRecentSession {
  _id: string;
  transaction_id: string;
  started_at: string;
  ended_at: string | null;
  status: string;
  energy_kwh: number;
  cost_total_cents: number;
  currency: string;
  charger_identity: string | null;
  location_name: string | null;
  /** Stripe-hosted receipt URL surfaced through the admin
   *  /admin/drivers/:id endpoint (denormalized from Payout via
   *  the payment_intent.succeeded webhook). Null on free /
   *  wallet-only / RFID-only sessions and on sessions whose
   *  webhook hasn't denormalized the URL yet. Drives the
   *  per-row "View receipt" button on driver detail. */
  stripe_receipt_url: string | null;
}

export interface DriverGroupPill {
  _id: string;
  name: string;
}

export interface DriverDetailView extends DriverRow {
  total_kwh: number;
  /** USD spend (legacy single-currency field). */
  total_spending_cents: number;
  /** Spend per currency, largest first. Absent from an older Api. */
  spend?: Array<{ currency: string; cents: number }>;
  /** Lifetime points earned. Absent from an older Api. */
  points_lifetime?: number;
  current_rank: string | null;
  current_streak_days: number;
  recent_sessions: DriverRecentSession[];
  group_memberships: DriverGroupPill[];
}

export interface DriversListResponse {
  data: DriverRow[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export async function fetchAdminDrivers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  state?: DriverStateFilter;
  groupId?: string;
  activity?: DriverActivityFilter;
  joined?: DriverJoinedFilter;
}): Promise<DriversListResponse> {
  const sp = new URLSearchParams();
  if (params?.page) sp.set('page', String(params.page));
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.search) sp.set('search', params.search);
  if (params?.state && params.state !== 'all') sp.set('state', params.state);
  if (params?.groupId) sp.set('group_id', params.groupId);
  if (params?.activity && params.activity !== 'all') sp.set('activity', params.activity);
  if (params?.joined && params.joined !== 'all') sp.set('joined', params.joined);
  const res = await api
    .get('admin/drivers', { searchParams: sp })
    .json<{ success: true; data: DriverRow[]; meta: DriversListResponse['meta'] }>();
  return { data: res.data, meta: res.meta };
}

export async function fetchAdminDriver(driverId: string): Promise<DriverDetailView> {
  const res = await api.get(`admin/drivers/${driverId}`)
    .json<ApiResponse<DriverDetailView>>();
  return res.data;
}

/** One row of GET /admin/drivers/:id/sessions: the driver profile's
 *  paginated session list (item 9, 2026-10-23). */
export interface DriverSessionRow {
  _id: string;
  transaction_id: string;
  status: string;
  started_at: string | null;
  ended_at: string | null;
  duration_minutes: number;
  energy_kwh: number;
  cost_total_cents: number;
  currency: string;
  charger_id: string | null;
  charger_identity: string | null;
  location_id: string | null;
  location_name: string | null;
  stripe_receipt_url: string | null;
}

export type DriverSessionStatusFilter = 'all' | 'completed' | 'failed' | 'in_progress';

export interface DriverSessionsResponse {
  data: DriverSessionRow[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    /** The sites this driver charged at, within the caller's scope. */
    locations: Array<{ _id: string; name: string | null }>;
  };
}

export async function fetchDriverSessions(
  driverId: string,
  params?: { page?: number; limit?: number; status?: DriverSessionStatusFilter; from?: string; locationId?: string },
): Promise<DriverSessionsResponse> {
  const sp = new URLSearchParams();
  if (params?.page) sp.set('page', String(params.page));
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.status && params.status !== 'all') sp.set('status', params.status);
  if (params?.from) sp.set('from', params.from);
  if (params?.locationId) sp.set('location_id', params.locationId);
  const res = await api
    .get(`admin/drivers/${driverId}/sessions`, { searchParams: sp })
    .json<{ success: true; data: DriverSessionRow[]; meta: DriverSessionsResponse['meta'] }>();
  return { data: res.data, meta: res.meta };
}

export interface UpdateDriverBody {
  name?: string;
  is_active?: boolean;
}

export async function updateAdminDriver(
  driverId: string,
  body: UpdateDriverBody,
): Promise<{ _id: string; is_active: boolean; name: string }> {
  const res = await api.patch(`admin/drivers/${driverId}`, { json: body })
    .json<ApiResponse<{ _id: string; is_active: boolean; name: string }>>();
  return res.data;
}

// ── Customer Groups (Phase A) ───────────────────────────────────────

export interface GroupRow {
  _id: string;
  name: string;
  description: string;
  member_count: number;
  // Phase 8 — Fleet Mode scope counts. Optional for back-compat
  // with serializations from API instances predating Phase 8.
  charger_count?: number;
  location_count?: number;
  created_at: string;
  created_by: string;
}

export interface GroupMember {
  _id: string;
  name: string;
  email: string;
}

export interface GroupDetailView extends GroupRow {
  members: GroupMember[];
  // Phase 8 — full scope arrays for the edit modal to seed multi-
  // selects. Optional for back-compat.
  charger_ids?: string[];
  location_ids?: string[];
}

export async function fetchAdminGroups(): Promise<GroupRow[]> {
  const res = await api.get('admin/groups').json<ApiResponse<GroupRow[]>>();
  return res.data;
}

export async function fetchAdminGroup(groupId: string): Promise<GroupDetailView> {
  const res = await api.get(`admin/groups/${groupId}`).json<ApiResponse<GroupDetailView>>();
  return res.data;
}

export interface CreateGroupBody {
  name: string;
  description?: string;
  driver_user_ids?: string[];
  // Phase 8 — Fleet Mode scope. Optional; empty arrays are the
  // legacy Customer Group semantic (pure segmentation, no access
  // restriction).
  charger_ids?: string[];
  location_ids?: string[];
}

export async function createAdminGroup(body: CreateGroupBody): Promise<GroupRow> {
  const res = await api.post('admin/groups', { json: body })
    .json<ApiResponse<GroupRow>>();
  return res.data;
}

export interface UpdateGroupBody {
  name?: string;
  description?: string;
  driver_user_ids?: string[];
  // Phase 8 — Fleet Mode scope. Full-replacement semantic mirrors
  // driver_user_ids: server persists exactly what the modal sends.
  charger_ids?: string[];
  location_ids?: string[];
}

export async function updateAdminGroup(
  groupId: string,
  body: UpdateGroupBody,
): Promise<GroupRow> {
  const res = await api.patch(`admin/groups/${groupId}`, { json: body })
    .json<ApiResponse<GroupRow>>();
  return res.data;
}

export async function deleteAdminGroup(groupId: string): Promise<void> {
  await api.delete(`admin/groups/${groupId}`);
}

// ── Notifications (Phase B) ─────────────────────────────────────────

export interface NotificationView {
  _id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  resolved_at: string | null;
  read_at: string | null;
  dismissed_at: string | null;
  target_type: 'tenant' | 'user' | 'role';
}

export async function fetchNotifications(params?: {
  unreadOnly?: boolean;
  includeDismissed?: boolean;
  limit?: number;
}): Promise<NotificationView[]> {
  const sp = new URLSearchParams();
  if (params?.unreadOnly) sp.set('unread_only', 'true');
  if (params?.includeDismissed) sp.set('include_dismissed', 'true');
  if (params?.limit) sp.set('limit', String(params.limit));
  const res = await api
    .get('admin/notifications', { searchParams: sp })
    .json<ApiResponse<NotificationView[]>>();
  return res.data;
}

export async function fetchNotificationsUnreadCount(): Promise<number> {
  const res = await api
    .get('admin/notifications/unread-count')
    .json<ApiResponse<{ count: number }>>();
  return res.data.count;
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await api.post(`admin/notifications/${notificationId}/read`);
}

export async function markNotificationDismissed(notificationId: string): Promise<void> {
  await api.post(`admin/notifications/${notificationId}/dismiss`);
}

/**
 * Re-send the invite email. Server regenerates magic_token,
 * extends invite_expires_at by 7 days from now, and dispatches
 * a fresh email. Backend rejects USER_ALREADY_ACTIVE (signed in
 * already) and USER_DEACTIVATED states with structured codes.
 */
export async function resendAdminUserInvite(userId: string): Promise<UserDetailView> {
  const res = await api.post(`admin/users/${userId}/resend-invite`)
    .json<ApiResponse<UserDetailView>>();
  return res.data;
}

/** Copy invite link (29 October 2026): the invite's own sign-in link, fresh and single use, within its expiry. Re-send's gate. */
export async function copyAdminUserInviteLink(userId: string): Promise<InviteLink> {
  const res = await api.post(`admin/users/${userId}/invite-link`).json<ApiResponse<InviteLink>>();
  return res.data;
}

/**
 * super_admin-only: clear a teammate's MFA so they re-enroll on
 * their next sign-in (admin-MFA arc, commit 6). Hits the commit-3
 * endpoint POST /auth/mfa/admin/reset/:userId, which clears the
 * four mfa fields, refuses self-targeting, and logs actor+target.
 * The endpoint is the real boundary (403s a non-super_admin); the
 * UI only renders the control for super_admins.
 */
export async function resetUserMfa(userId: string): Promise<{ reset: boolean }> {
  const res = await api.post(`auth/mfa/admin/reset/${userId}`)
    .json<ApiResponse<{ reset: boolean }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

// ── Tenant Connect (Stripe Express, operator side) ───────────────────────
//
// Twin of the resident-side helpers in apps/web/src/lib/api.ts.
// Targets the Tenant by path id (the caller's tenant for tenant_admin
// or any tenant for super_admin; server enforces the role gate).

export interface TenantConnectStatus {
  has_account: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements_currently_due: string[];
  requirements_disabled_reason: string | null;
  /** Warren's ruling 2: the tenant's payouts are held by the EVI platform
   *  and it never holds a Connect account (POST /connect/start answers 409
   *  PLATFORM_HELD_TENANT). Optional so an Api without it reads as false. */
  platform_held?: boolean;
}

/** Kick off (or resume) tenant Connect onboarding. Returns a
 *  one-time Stripe-hosted AccountLink URL. The admin app redirects
 *  the operator to this URL; Stripe redirects back to the
 *  PayoutsCompletePage on completion. */
export async function startTenantConnectOnboarding(
  tenantId: string,
): Promise<{ url: string }> {
  const res = await api
    .post(`admin/tenants/${tenantId}/connect/start`)
    .json<ApiResponse<{ url: string }>>();
  return res.data;
}

/** Real-time tenant Connect status. Reads cached flags off the
 *  Tenant doc and reconciles against Stripe's live account state
 *  so webhook-delivery lag doesn't strand the UI in a stale view. */
export async function fetchTenantConnectStatus(
  tenantId: string,
): Promise<TenantConnectStatus> {
  const res = await api
    .get(`admin/tenants/${tenantId}/connect/status`)
    .json<ApiResponse<TenantConnectStatus>>();
  return res.data;
}

/** Mint a one-time Stripe Express dashboard login URL for the
 *  tenant. 412 EXPRESS_NOT_READY if Connect isn't charges-enabled
 *  (client pre-gates via fetchTenantConnectStatus); 502
 *  STRIPE_UNAVAILABLE on transient Stripe failures. */
export async function fetchTenantDashboardLink(
  tenantId: string,
): Promise<{ url: string }> {
  const res = await api
    .post(`admin/tenants/${tenantId}/connect/dashboard-link`)
    .json<ApiResponse<{ url: string }>>();
  return res.data;
}

// ── Fleets (Phase 2 fleet platform) ─────────────────────────────────

export interface FleetSummary {
  _id: string;
  tenant_id: string;
  name: string;
  billing_email: string;
  stripe_customer_id: string;
  default_stripe_payment_method_id: string | null;
  subscription_tier: string | null;
  is_active: boolean;
  invite_code: string | null;
  /** Server-composed `/join-fleet?code=` link on the PWA origin. Null
   *  exactly when invite_code is null. Drives the invite panel's URL
   *  row, QR, and share sheet. */
  join_url: string | null;
  email_allowlist_domain: string | null;
  // Fleet Mode Phase 1 toggles. requires_driver_payment_method gates
  // Phase 7 (driver-card requirement); allows_mixed_use_default is the
  // fallback the Phase 4 charger UI inherits when allow_mixed_use is
  // null on a fleet-attributed charger.
  requires_driver_payment_method?: boolean;
  allows_mixed_use_default?: boolean;
  created_at: string;
  updated_at: string;
}

export async function fetchFleets(): Promise<FleetSummary[]> {
  const res = await api.get('admin/fleets').json<ApiResponse<FleetSummary[]>>();
  return res.data;
}

export async function fetchFleet(id: string): Promise<FleetSummary> {
  const res = await api.get(`admin/fleets/${id}`).json<ApiResponse<FleetSummary>>();
  return res.data;
}

// ── Fleet overview stats (Phase 3b Item 4) ──────────────────────

export interface FleetStatsPerDriver {
  user_id: string | null;
  name: string | null;
  email: string | null;
  sessions_count: number;
  energy_kwh: number;
  cost_cents: number;
  last_session_at: string | Date;
}

export interface FleetStatsResponse {
  range_days: number;
  active_drivers: number;
  active_keyfobs: number;
  total_sessions: number;
  total_kwh: number;
  total_cost_cents: number;
  sessions_by_day: Array<{ date: string; count: number; kwh: number }>;
  top_drivers: FleetStatsPerDriver[];
  per_driver: FleetStatsPerDriver[];
}

export async function fetchFleetStats(
  id: string,
  days: number = 30,
): Promise<FleetStatsResponse> {
  const res = await api
    .get(`admin/fleets/${id}/stats`, { searchParams: { days } })
    .json<ApiResponse<FleetStatsResponse>>();
  return res.data;
}

export async function createFleet(body: {
  name: string;
  billing_email: string;
  initial_payment_method_id?: string;
  promo_code?: string;
}): Promise<FleetSummary> {
  const res = await api
    .post('admin/fleets', { json: body })
    .json<ApiResponse<FleetSummary>>();
  return res.data;
}

export async function patchFleet(
  id: string,
  body: {
    name?: string;
    billing_email?: string;
    default_stripe_payment_method_id?: string | null;
    is_active?: boolean;
    email_allowlist_domain?: string | null;
    // Phase 7 — toggle the secondary driver-PM-required gate.
    requires_driver_payment_method?: boolean;
  },
): Promise<FleetSummary> {
  const res = await api
    .patch(`admin/fleets/${id}`, { json: body })
    .json<ApiResponse<FleetSummary>>();
  return res.data;
}

export interface FleetInviteCodeView {
  invite_code: string;
  join_url: string;
}

export async function regenerateFleetInviteCode(id: string): Promise<FleetInviteCodeView> {
  const res = await api
    .post(`admin/fleets/${id}/invite-code/regenerate`)
    .json<ApiResponse<FleetInviteCodeView>>();
  return res.data;
}

export interface BulkDriverUploadSummary {
  total_rows: number;
  /** Every address, account or not, gets the emailed invite; an existing
   *  account joins at its next verified sign-in. */
  invited: number;
  no_user_skipped: number;
  errors: Array<{ row: number; email: string; reason: string }>;
}

export async function bulkUploadDrivers(
  fleetId: string,
  file: File,
): Promise<BulkDriverUploadSummary> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api
    .post(`admin/fleets/${fleetId}/drivers/bulk`, { body: formData })
    .json<ApiResponse<BulkDriverUploadSummary>>();
  return res.data;
}

export interface BulkKeyfobUploadSummary {
  total_rows: number;
  issued_pool: number;
  issued_linked: number;
  issued_orphaned: number;
  already_issued: number;
  owned_elsewhere: number;
  errors: Array<{ row: number; tag_uid: string; reason: string }>;
  /** The ONE aggregate charge for the upload (Warren, 2026-09-16): count
   *  is the rows that were issuable, amount_cents = what THIS upload
   *  charged (count minus prepaid_count, × the fleet keyfob price);
   *  payment_intent_id null when nothing was billed (super_admin, a
   *  free-priced tenant, or no rows left to charge). prepaid_count is rows
   *  an earlier upload had already paid for — its process died between
   *  the charge and the inserts — issued now against that money with no
   *  new charge. k inserts that failed behind a charge come back as
   *  refunded_cents. */
  charge: {
    count: number;
    amount_cents: number;
    payment_intent_id: string | null;
    prepaid_count: number;
    refunded_cents: number;
    refund_id: string | null;
    /** The keyfob order rows this upload charged or drew on. */
    order_ids: string[];
  };
}

export async function bulkUploadKeyfobs(
  fleetId: string,
  file: File,
): Promise<BulkKeyfobUploadSummary> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api
    .post(`admin/fleets/${fleetId}/keyfobs/bulk`, { body: formData })
    .json<ApiResponse<BulkKeyfobUploadSummary>>();
  return res.data;
}

/** Always 'invited': an address that already has an account gets the
 *  same emailed invite as a new one and joins once it opens the link and
 *  signs in with an emailed sign-in link. */
export async function inviteFleetDriverByEmail(
  fleetId: string,
  email: string,
): Promise<{ outcome: 'invited'; jti?: string }> {
  const res = await api
    .post(`admin/fleets/${fleetId}/drivers/invite-by-email`, {
      json: { email },
    })
    .json<ApiResponse<{ outcome: 'invited'; jti?: string }>>();
  return res.data;
}

export interface FleetDriverRow {
  _id: string;
  name: string;
  email: string;
  is_active: boolean;
  last_login: string | null;
  /** The latest of app activity, sign-in and any session start. Absent from an older Api. */
  last_seen_at?: string | null;
  total_sessions: number;
}

export async function fetchFleetDrivers(fleetId: string): Promise<FleetDriverRow[]> {
  const res = await api
    .get(`admin/fleets/${fleetId}/drivers`)
    .json<ApiResponse<FleetDriverRow[]>>();
  return res.data;
}

export async function addFleetDriver(
  fleetId: string,
  userId: string,
): Promise<{ _id: string; fleet_id: string }> {
  const res = await api
    .post(`admin/fleets/${fleetId}/drivers`, { json: { user_id: userId } })
    .json<ApiResponse<{ _id: string; fleet_id: string }>>();
  return res.data;
}

export async function removeFleetDriver(
  fleetId: string,
  userId: string,
): Promise<void> {
  await api.delete(`admin/fleets/${fleetId}/drivers/${userId}`);
}

// ── Fleet chargers (Phase 1b) ─────────────────────────────────
export interface FleetChargerRow {
  _id: string;
  identity: string;
  display_name: string | null;
  is_online: boolean;
  is_active: boolean;
  allow_mixed_use: boolean | null;
  // Phase 3c — DLM circuit membership + the control loop's last-pushed cap
  // (read-only observability). circuit_id is null when the charger isn't on a
  // managed circuit; dlm_* are null until the 3b.2 loop first manages it.
  circuit_id: string | null;
  dlm_last_cap_a: number | null;
  dlm_last_pushed_at: string | null;
}

export async function fetchFleetChargers(fleetId: string): Promise<FleetChargerRow[]> {
  const res = await api
    .get(`admin/fleets/${fleetId}/chargers`)
    .json<ApiResponse<FleetChargerRow[]>>();
  return res.data;
}

export async function addFleetCharger(
  fleetId: string,
  chargerId: string,
): Promise<{ _id: string }> {
  const res = await api
    .post(`admin/fleets/${fleetId}/chargers`, { json: { charger_id: chargerId } })
    .json<ApiResponse<{ _id: string }>>();
  return res.data;
}

export async function removeFleetCharger(
  fleetId: string,
  chargerId: string,
): Promise<void> {
  await api.delete(`admin/fleets/${fleetId}/chargers/${chargerId}`);
}

// ── Fleet circuits (Phase 3a/3c — DLM) ─────────────────────────
export type BreakerTripClass = 'B' | 'C' | 'D';

export interface FleetCircuitRow {
  _id: string;
  name: string;
  limit_a: number;
  breaker_rating_a: number;
  breaker_trip_class: BreakerTripClass;
  is_active: boolean;
  charger_count: number;
}

export interface CircuitUpsertBody {
  name: string;
  limit_a: number;
  breaker_rating_a: number;
  breaker_trip_class: BreakerTripClass;
  is_active?: boolean;
}

/** Create/update return the id plus an optional non-blocking derate warning
 *  (limit_a > 0.8 * breaker_rating_a) — the save still succeeds. */
export interface CircuitMutationResult {
  _id: string;
  warning?: string;
}

export async function fetchFleetCircuits(fleetId: string): Promise<FleetCircuitRow[]> {
  const res = await api
    .get(`admin/fleets/${fleetId}/circuits`)
    .json<ApiResponse<FleetCircuitRow[]>>();
  return res.data;
}

export async function createFleetCircuit(
  fleetId: string,
  body: CircuitUpsertBody,
): Promise<CircuitMutationResult> {
  const res = await api
    .post(`admin/fleets/${fleetId}/circuits`, { json: body })
    .json<ApiResponse<CircuitMutationResult>>();
  return res.data;
}

export async function updateFleetCircuit(
  fleetId: string,
  circuitId: string,
  body: Partial<CircuitUpsertBody>,
): Promise<CircuitMutationResult> {
  const res = await api
    .patch(`admin/fleets/${fleetId}/circuits/${circuitId}`, { json: body })
    .json<ApiResponse<CircuitMutationResult>>();
  return res.data;
}

export async function deleteFleetCircuit(
  fleetId: string,
  circuitId: string,
): Promise<void> {
  await api.delete(`admin/fleets/${fleetId}/circuits/${circuitId}`);
}

export async function assignChargerToCircuit(
  fleetId: string,
  circuitId: string,
  chargerId: string,
): Promise<void> {
  await api.post(`admin/fleets/${fleetId}/circuits/${circuitId}/chargers`, {
    json: { charger_id: chargerId },
  });
}

export async function unassignChargerFromCircuit(
  fleetId: string,
  circuitId: string,
  chargerId: string,
): Promise<void> {
  await api.delete(`admin/fleets/${fleetId}/circuits/${circuitId}/chargers/${chargerId}`);
}

// ── Building (location) circuits — DLM building CRUD (unit 1) ───
// Mirrors the fleet circuit client methods, pointed at the building-scoped
// CRUD at /admin/locations/:id/circuits (apps/api/src/routes/admin/
// building-circuits.ts). The response shapes match the fleet ones, so the
// shared CircuitUpsertBody / CircuitMutationResult / BreakerTripClass are
// reused; the row type mirrors FleetCircuitRow.
export interface BuildingCircuitRow {
  _id: string;
  name: string;
  limit_a: number;
  breaker_rating_a: number;
  breaker_trip_class: BreakerTripClass;
  is_active: boolean;
  charger_count: number;
}

export async function fetchLocationCircuits(locationId: string): Promise<BuildingCircuitRow[]> {
  const res = await api
    .get(`admin/locations/${locationId}/circuits`)
    .json<ApiResponse<BuildingCircuitRow[]>>();
  return res.data;
}

export async function createLocationCircuit(
  locationId: string,
  body: CircuitUpsertBody,
): Promise<CircuitMutationResult> {
  const res = await api
    .post(`admin/locations/${locationId}/circuits`, { json: body })
    .json<ApiResponse<CircuitMutationResult>>();
  return res.data;
}

export async function updateLocationCircuit(
  locationId: string,
  circuitId: string,
  body: Partial<CircuitUpsertBody>,
): Promise<CircuitMutationResult> {
  const res = await api
    .patch(`admin/locations/${locationId}/circuits/${circuitId}`, { json: body })
    .json<ApiResponse<CircuitMutationResult>>();
  return res.data;
}

export async function deleteLocationCircuit(
  locationId: string,
  circuitId: string,
): Promise<void> {
  await api.delete(`admin/locations/${locationId}/circuits/${circuitId}`);
}

export async function assignChargerToLocationCircuit(
  locationId: string,
  circuitId: string,
  chargerId: string,
): Promise<void> {
  await api.post(`admin/locations/${locationId}/circuits/${circuitId}/chargers`, {
    json: { charger_id: chargerId },
  });
}

export async function unassignChargerFromLocationCircuit(
  locationId: string,
  circuitId: string,
  chargerId: string,
): Promise<void> {
  await api.delete(`admin/locations/${locationId}/circuits/${circuitId}/chargers/${chargerId}`);
}

// ── Fleet vehicles (Phase 2a) ─────────────────────────────────
export type VehicleOnboardPhases = 1 | 3;
export type VehicleConnectorStandard = 'Type2' | 'CCS' | 'CHAdeMO';

export interface FleetVehicleRow {
  _id: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  label: string | null;
  license_plate: string | null;
  battery_capacity_kwh: number;
  max_charge_power_kw: number;
  max_charge_current_a: number;
  onboard_phases: VehicleOnboardPhases;
  connector_standard: VehicleConnectorStandard;
  primary_driver_id: string | null;
  last_soc_percent: number | null;
  last_odometer_km: number | null;
  last_telematics_at: string | null;
}

export interface VehicleUpsertBody {
  vin: string;
  make: string;
  model: string;
  year: number;
  label?: string | null;
  license_plate?: string | null;
  battery_capacity_kwh: number;
  max_charge_power_kw: number;
  max_charge_current_a: number;
  onboard_phases: VehicleOnboardPhases;
  connector_standard: VehicleConnectorStandard;
  primary_driver_id?: string | null;
}

export async function fetchFleetVehicles(fleetId: string): Promise<FleetVehicleRow[]> {
  const res = await api
    .get(`admin/fleets/${fleetId}/vehicles`)
    .json<ApiResponse<FleetVehicleRow[]>>();
  return res.data;
}

export async function createFleetVehicle(
  fleetId: string,
  body: VehicleUpsertBody,
): Promise<{ _id: string }> {
  const res = await api
    .post(`admin/fleets/${fleetId}/vehicles`, { json: body })
    .json<ApiResponse<{ _id: string }>>();
  return res.data;
}

export async function updateFleetVehicle(
  fleetId: string,
  vehicleId: string,
  body: Partial<VehicleUpsertBody>,
): Promise<{ _id: string }> {
  const res = await api
    .patch(`admin/fleets/${fleetId}/vehicles/${vehicleId}`, { json: body })
    .json<ApiResponse<{ _id: string }>>();
  return res.data;
}

export async function deleteFleetVehicle(
  fleetId: string,
  vehicleId: string,
): Promise<void> {
  await api.delete(`admin/fleets/${fleetId}/vehicles/${vehicleId}`);
}

export interface FleetKeyfobRow {
  _id: string;
  tag_uid: string;
  label: string | null;
  is_active: boolean;
  user_id: string | null;
  user_name: string | null;
  vehicle_id: string | null;
  updated_at: string;
}

export async function fetchFleetKeyfobs(fleetId: string): Promise<FleetKeyfobRow[]> {
  const res = await api
    .get(`admin/fleets/${fleetId}/keyfobs`)
    .json<ApiResponse<FleetKeyfobRow[]>>();
  return res.data;
}

/**
 * GET /admin/fleets/:id/keyfobs/price — what the next keyfob costs THIS
 * fleet (Warren, 2026-09-16: $15 each, the fleet pays; the `fleet` entry of
 * the tenant's rfid_pricing matrix, so a tenant can override it). Also the
 * standing the server will gate on, so the issue / upload modals can show
 * the price line and the past-due prompt before the click rather than
 * after a 402. Not the caller-channel registration-fee preview, which
 * would price the caller's own channel instead of the fleet's.
 */
export interface FleetKeyfobPrice {
  amount_cents: number;
  currency: string;
  /** False when the tenant priced the fleet channel free — no charge is made. */
  billed: boolean;
  /** super_admin issues at no charge (the RFID route's 'internal' channel). */
  exempt: boolean;
  /** The raw subscription bucket the server gates on. */
  standing: FleetKeyfobStanding;
  /** The exact 402 (code + words) an issue would get in this standing, so
   *  the modal shows the same prompt before the click. null when 'ok'. */
  standing_block: { code: FleetKeyfobStandingCode; message: string } | null;
  /** Admin-app path of the fleet's Billing tab. */
  billing_url: string;
}

export type FleetKeyfobStanding = 'ok' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete';
export type FleetKeyfobStandingCode = 'FLEET_PAST_DUE' | 'FLEET_SUBSCRIPTION_INACTIVE';

export async function fetchFleetKeyfobPrice(fleetId: string): Promise<FleetKeyfobPrice> {
  const res = await api
    .get(`admin/fleets/${fleetId}/keyfobs/price`)
    .json<ApiResponse<FleetKeyfobPrice>>();
  return res.data;
}

/** The charge a keyfob issue produced. payment_intent_id null when nothing
 *  was billed (super_admin, or a tenant that priced the channel free).
 *  prepaid: an earlier attempt had already paid for this keyfob (its
 *  process died between the charge and the insert), so it was issued
 *  against that money and amount_cents is 0. */
export interface FleetKeyfobCharge {
  amount_cents: number;
  payment_intent_id: string | null;
  prepaid?: boolean;
}

export async function issueFleetKeyfob(
  fleetId: string,
  body: { tag_uid: string; label?: string; user_id?: string | null; vehicle_id?: string | null },
): Promise<{ _id: string; tag_uid: string; charge: FleetKeyfobCharge }> {
  const res = await api
    .post(`admin/fleets/${fleetId}/keyfobs`, { json: body })
    .json<ApiResponse<{ _id: string; tag_uid: string; charge: FleetKeyfobCharge }>>();
  return res.data;
}

/**
 * The three 402s the keyfob routes (single + bulk) answer BEFORE any
 * Stripe call: FLEET_PAST_DUE (an unpaid invoice — pay it),
 * FLEET_SUBSCRIPTION_INACTIVE (canceled / incomplete — reactivate it) and
 * FLEET_CARD_REQUIRED (no card on the fleet's customer). Both modals render
 * them as an inline prompt with a link to the Billing tab instead of a red
 * error, because the fix lives there. Follows the getDlmAddonRequired
 * idiom: non-throwing, clones the response before reading; null for any
 * other error.
 */
export type FleetKeyfobBillingCode = FleetKeyfobStandingCode | 'FLEET_CARD_REQUIRED';

const FLEET_KEYFOB_BILLING_CODES: readonly string[] = [
  'FLEET_PAST_DUE', 'FLEET_SUBSCRIPTION_INACTIVE', 'FLEET_CARD_REQUIRED',
];

export interface FleetKeyfobBillingBlock {
  code: FleetKeyfobBillingCode;
  message: string;
  /** Admin-app path of the Billing tab. */
  billing_url: string | null;
  /** API path of the fleet's SetupIntent route (FLEET_CARD_REQUIRED). */
  setup_url: string | null;
}

export async function getFleetKeyfobBillingBlock(err: unknown): Promise<FleetKeyfobBillingBlock | null> {
  const response = (err as { response?: Response }).response;
  if (!response || response.status !== 402) return null;
  try {
    const body = await response.clone().json() as Partial<ApiError>
      & { data?: { billing_url?: string; setup_url?: string } };
    const code = body?.error?.code;
    if (typeof code !== 'string' || !FLEET_KEYFOB_BILLING_CODES.includes(code)) return null;
    return {
      code: code as FleetKeyfobBillingCode,
      message: typeof body.error?.message === 'string' ? body.error.message : '',
      billing_url: body.data?.billing_url ?? null,
      setup_url: body.data?.setup_url ?? null,
    };
  } catch {
    return null;
  }
}

export async function patchFleetKeyfob(
  fleetId: string,
  cardId: string,
  body: { label?: string; is_active?: boolean; user_id?: string | null; vehicle_id?: string | null },
): Promise<void> {
  await api.patch(`admin/fleets/${fleetId}/keyfobs/${cardId}`, { json: body });
}

export interface FleetSessionRow {
  _id: string;
  transaction_id: string | null;
  user_id: string | null;
  /** The driver's name (email fallback), as the fleet CSV gives it. */
  driver_name?: string | null;
  /** Who started it, with the profile link; only for a caller who can
   *  open the driver profile (see Session.driver). */
  driver?: SessionDriverRef;
  rfid_tag?: string | null;
  started_at: string;
  ended_at: string | null;
  status: string;
  energy_kwh: number;
  duration_minutes: number;
  cost: { total: number; currency: string };
}

export async function fetchFleetSessions(
  fleetId: string,
  params?: { from?: string; to?: string; driver?: string; page?: number; limit?: number },
): Promise<{ data: FleetSessionRow[]; meta: { page: number; limit: number; total: number } }> {
  const sp = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  return api
    .get(`admin/fleets/${fleetId}/sessions?${sp.toString()}`)
    .json<{ data: FleetSessionRow[]; meta: { page: number; limit: number; total: number } }>();
}

/** The fleet Sessions tab's CSV export, a ky-relative path for
 *  downloadAuthed (a plain link cannot carry the Bearer token). */
export function fleetSessionsCsvPath(
  fleetId: string,
  params?: { from?: string; to?: string; driver?: string },
): string {
  const sp = new URLSearchParams({ csv: 'true' });
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  return `admin/fleets/${fleetId}/sessions?${sp.toString()}`;
}

export interface FleetBillingSummary {
  /** Null for a fleet that has no Stripe customer yet (migrated v1
   *  fleets); the update-payment-method route creates one on demand. */
  stripe_customer_id: string | null;
  default_payment_method_id: string | null;
  subscription_tier: string | null;
  // Phase 4 subscription fields
  stripe_subscription_id: string | null;
  billing_cadence: 'monthly' | 'annual';
  subscription_status:
    | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete'
    | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  // Seat arithmetic, straight from the quantity sync: chargers assigned
  // to the fleet, the checkout-declared floor (null when none), and the
  // quantity Stripe bills = max(charger_count, floor). The Plan card
  // multiplies billed_quantity, never the live count.
  charger_count: number;
  subscribed_charger_floor: number | null;
  billed_quantity: number;
  cancel_at_period_end: boolean;
  cancel_at: number | null;
  default_card: { brand: string; last4: string; exp_month: number; exp_year: number } | null;
  recent_invoices: Array<{
    id: string;
    amount_due_cents: number;
    amount_paid_cents: number;
    status: string | null;
    hosted_invoice_url: string | null;
    invoice_pdf?: string | null;
    number: string | null;
    period_start: number;
    period_end: number;
    created?: number;
  }>;
  upcoming_invoice: {
    amount_due_cents: number;
    period_start: number;
    period_end: number;
  } | null;
  /** The fleet program's prices, each cadence from its own Stripe Price (null where Stripe has none). Warren, 21
   *  October 2026, night: never written into the Admin, never one cadence worked out from the other. */
  plan_prices?: { base: PlanPricePair; per_charger: PlanPricePair };
}

export async function fetchFleetBilling(fleetId: string): Promise<FleetBillingSummary> {
  const res = await api
    .get(`admin/fleets/${fleetId}/billing`)
    .json<ApiResponse<FleetBillingSummary>>();
  return res.data;
}

// Phase 4 — cadence switch + cancel/reactivate + update PM
export async function switchFleetCadence(
  fleetId: string,
  cadence: 'monthly' | 'annual',
): Promise<{ cadence: string; subscription_status: string | null; current_period_end: string | null }> {
  const res = await api
    .post(`admin/fleets/${fleetId}/billing/switch-cadence`, { json: { cadence } })
    .json<ApiResponse<{ cadence: string; subscription_status: string | null; current_period_end: string | null }>>();
  return res.data;
}

export async function cancelFleetSubscription(
  fleetId: string,
): Promise<{ cancel_at_period_end: boolean; cancel_at: number | null; current_period_end: number }> {
  const res = await api
    .delete(`admin/fleets/${fleetId}/billing/subscription`)
    .json<ApiResponse<{ cancel_at_period_end: boolean; cancel_at: number | null; current_period_end: number }>>();
  return res.data;
}

export async function reactivateFleetSubscription(
  fleetId: string,
): Promise<{ cancel_at_period_end: boolean; current_period_end: number }> {
  const res = await api
    .post(`admin/fleets/${fleetId}/billing/subscription/reactivate`)
    .json<ApiResponse<{ cancel_at_period_end: boolean; current_period_end: number }>>();
  return res.data;
}

/** Mints the SetupIntent CardSetupModal hands to Stripe Elements. Also
 *  creates the fleet's Stripe customer when it has none (migrated v1
 *  fleets), so the response carries the customer id. */
export interface FleetPaymentMethodSetup {
  client_secret: string;
  setup_intent_id: string;
  stripe_customer_id: string;
}

export async function startFleetPaymentMethodUpdate(
  fleetId: string,
): Promise<FleetPaymentMethodSetup> {
  const res = await api
    .post(`admin/fleets/${fleetId}/billing/update-payment-method`)
    .json<ApiResponse<FleetPaymentMethodSetup>>();
  return res.data;
}

/** CONFIRM phase of the same route: after Stripe Elements has attached
 *  the card (confirmSetup), make it the fleet's default on Stripe and
 *  in the Fleet-doc mirror. Not PATCH /admin/fleets/:id — that attaches
 *  unconditionally and Stripe rejects re-attaching an attached card. */
export async function confirmFleetPaymentMethod(
  fleetId: string,
  paymentMethodId: string,
): Promise<{
  default_payment_method_id: string;
  default_card: FleetBillingSummary['default_card'];
}> {
  const res = await api
    .post(`admin/fleets/${fleetId}/billing/update-payment-method`, {
      json: { payment_method_id: paymentMethodId },
    })
    .json<ApiResponse<{
      default_payment_method_id: string;
      default_card: FleetBillingSummary['default_card'];
    }>>();
  return res.data;
}

// ── Tenant subscription billing (operator pay-EVI direction) ────────
// The building operator's per-charger subscription to EVI, priced by its
// Stripe Price for each cadence. Mirrors the fleet billing client methods,
// adapted for the Tenant doc and the per-charger-only price. Endpoints
// live in apps/api/src/routes/admin/tenant-billing.ts (commit 2b79004).

export interface TenantBillingSummary {
  name: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  billing_email: string | null;
  billing_cadence: 'monthly' | 'annual';
  subscription_status:
    | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete'
    | null;
  // Withholding + past-due state for the at-a-glance indicators.
  withholding_active: boolean;
  past_due_since: string | null;
  // DLM add-on (unit 5): on/off state, the managed-charger billable count,
  // and the per-cadence cost estimate (null until Stripe enrichment runs).
  dlm_enabled: boolean;
  dlm_managed_charger_count: number;
  dlm_cost_estimate_cents: number | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  /** Every live charger on the tenant (the roster figure). */
  charger_count: number;
  // Seat arithmetic, mirroring the quantity sync: the subscription-
  // billable live count (excludes fleet-assigned and resident-owned
  // chargers), the checkout-declared floor (null when none), and the
  // quantity Stripe bills = max(billable_charger_count, floor). The
  // Plan card multiplies billed_quantity, never a live count.
  billable_charger_count: number;
  subscribed_charger_floor: number | null;
  billed_quantity: number;
  cancel_at_period_end: boolean;
  cancel_at: number | null;
  default_card: { brand: string; last4: string; exp_month: number; exp_year: number } | null;
  recent_invoices: Array<{
    id: string;
    amount_due_cents: number;
    amount_paid_cents: number;
    status: string | null;
    hosted_invoice_url: string | null;
    invoice_pdf?: string | null;
    number: string | null;
    period_start: number;
    period_end: number;
    created?: number;
  }>;
  upcoming_invoice: {
    amount_due_cents: number;
    period_start: number;
    period_end: number;
  } | null;
  /** The network plan per charger and load management, each cadence from its own Stripe Price (null where Stripe
   *  has none). Warren, 21 October 2026, night: never written into the Admin, never one cadence from the other. */
  plan_prices?: { per_charger: PlanPricePair; load_management: PlanPricePair };
}

export async function fetchTenantBilling(tenantId: string): Promise<TenantBillingSummary> {
  const res = await api
    .get(`admin/tenants/${tenantId}/billing`)
    .json<ApiResponse<TenantBillingSummary>>();
  return res.data;
}

// First card add: also creates the Stripe customer and captures the
// billing-contact email. Returns the SetupIntent client_secret (handed
// to Stripe Elements or the Dashboard, mirroring the fleet card flow).
export async function startTenantBillingSetupIntent(
  tenantId: string,
  billingEmail: string,
): Promise<{ client_secret: string; setup_intent_id: string }> {
  const res = await api
    .post(`admin/tenants/${tenantId}/billing/setup-intent`, {
      json: { billing_email: billingEmail },
    })
    .json<ApiResponse<{ client_secret: string; setup_intent_id: string }>>();
  return res.data;
}

// Create the per-charger subscription. An optional promo code is
// validated server-side before anything is created.
export async function createTenantSubscription(
  tenantId: string,
  body: {
    billing_cadence?: 'monthly' | 'annual';
    promo_code?: string;
    trial_period_days?: number;
  },
): Promise<{
  stripe_subscription_id: string;
  subscription_status: string | null;
  billing_cadence: string;
}> {
  const res = await api
    .post(`admin/tenants/${tenantId}/billing/subscription`, { json: body })
    .json<ApiResponse<{
      stripe_subscription_id: string;
      subscription_status: string | null;
      billing_cadence: string;
    }>>();
  return res.data;
}

export async function switchTenantCadence(
  tenantId: string,
  cadence: 'monthly' | 'annual',
): Promise<{ cadence: string }> {
  const res = await api
    .post(`admin/tenants/${tenantId}/billing/switch-cadence`, { json: { cadence } })
    .json<ApiResponse<{ cadence: string }>>();
  return res.data;
}

// Tenant DLM add-on enable/disable (DLM units 2-3). Enable attaches the DLM
// item to the subscription; disable clears the load-management profiles and
// removes the item. Both hit the dlm-entitlement route, gated by
// requireTenantScope (the tenant's own admin or a super_admin).
export async function enableTenantDlm(tenantId: string): Promise<{ dlm_enabled: boolean }> {
  const res = await api
    .post(`admin/tenants/${tenantId}/dlm/enable`)
    .json<ApiResponse<{ dlm_enabled: boolean }>>();
  return res.data;
}

export async function disableTenantDlm(tenantId: string): Promise<{ dlm_enabled: boolean }> {
  const res = await api
    .post(`admin/tenants/${tenantId}/dlm/disable`)
    .json<ApiResponse<{ dlm_enabled: boolean }>>();
  return res.data;
}

// Fleet-side DLM enable. Same dlm-entitlement route family as the tenant
// pair above, keyed on the fleet; the server allows tenant_admin (the
// fleet's own tenant) and super_admin only — a fleet_admin gets 403, which
// is why the circuit-create prompt below carries can_enable.
export async function enableFleetDlm(fleetId: string): Promise<{ dlm_enabled: boolean }> {
  const res = await api
    .post(`admin/fleets/${fleetId}/dlm/enable`)
    .json<ApiResponse<{ dlm_enabled: boolean }>>();
  return res.data;
}

/**
 * The 402 DLM_ADDON_REQUIRED body that circuit CREATE (fleet or building)
 * returns when the account has no DLM add-on. Both circuit-create modals
 * render it as an inline "add the add-on" prompt instead of a red error.
 *
 * can_enable is the server's whole verdict — role AND subscription. It is
 * false for a fleet_admin (the enable routes are tenant_admin / super_admin
 * only) and for any account with no live subscription (the enable route
 * answers 402 SUBSCRIPTION_REQUIRED there, because DLM bills as an item on
 * the subscription and a flag flip without one collects nothing). When it
 * is false the modal shows `hint` with no enable button — re-clicking
 * would only hit the same wall — and links to subscribe_url when present.
 */
export interface DlmAddonRequired {
  /** The account's cadence's load management price from its own Stripe Price; null where Stripe has none, and the
   *  prompt then says EV Initiative confirms the price. */
  price_cents_per_charger: number | null;
  price_interval?: 'month' | 'year' | null;
  currency?: string | null;
  managed_charger_count: number;
  /** The account has a live subscription for the DLM item to attach to. */
  subscribed: boolean;
  can_enable: boolean;
  /** API path of the enable route, e.g. /admin/tenants/:id/dlm/enable.
   *  null whenever can_enable is false. */
  enable_url: string | null;
  /** Admin-app page where the subscription is started (tenant: /settings;
   *  fleet: the fleet's Billing tab). null for a fleet_admin, who cannot
   *  subscribe the fleet either. */
  subscribe_url: string | null;
  /** Plain-English next step, rendered verbatim when can_enable is false. */
  hint: string;
  /** Set by the building route so the modal can call enableTenantDlm
   *  without knowing the location's tenant. */
  tenant_id?: string;
}

/**
 * Extract the DLM_ADDON_REQUIRED payload from a thrown ky HTTPError, or
 * null for any other error (status, code, or parse edge). Follows the
 * getErrorCode idiom: non-throwing, clones the response before reading.
 */
export async function getDlmAddonRequired(err: unknown): Promise<DlmAddonRequired | null> {
  const response = (err as { response?: Response }).response;
  if (!response || response.status !== 402) return null;
  try {
    const body = await response.clone().json() as Partial<ApiError> & { data?: DlmAddonRequired };
    if (body?.error?.code !== 'DLM_ADDON_REQUIRED' || !body.data) return null;
    return body.data;
  } catch {
    return null;
  }
}

export interface TenantRow {
  _id: string;
  name: string;
  billing_email: string | null;
  subscription_status:
    | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete'
    | null;
  billing_cadence: 'monthly' | 'annual';
  stripe_subscription_id: string | null;
  withholding_active: boolean;
  past_due_since: string | null;
}

// super_admin-only tenant roster for the cross-tenant billing surface.
export async function fetchTenants(): Promise<TenantRow[]> {
  const res = await api.get('admin/tenants').json<ApiResponse<TenantRow[]>>();
  return res.data;
}

export interface FleetAdminRow {
  _id: string;
  name: string;
  email: string;
  is_active: boolean;
}

export async function fetchFleetAdmins(fleetId: string): Promise<FleetAdminRow[]> {
  const res = await api
    .get(`admin/fleets/${fleetId}/admins`)
    .json<ApiResponse<FleetAdminRow[]>>();
  return res.data;
}

export async function addFleetAdmin(fleetId: string, userId: string): Promise<void> {
  await api.post(`admin/fleets/${fleetId}/admins`, { json: { user_id: userId } });
}

export async function removeFleetAdmin(fleetId: string, userId: string): Promise<void> {
  await api.delete(`admin/fleets/${fleetId}/admins/${userId}`);
}

// ── ICCID backfill (Phase 5a Item 5) ─────────────────────────────

export interface MissingIccidCharger {
  _id: string;
  identity: string;
  display_name: string | null;
  location_id: string;
  last_heartbeat: string | null;
  is_online: boolean;
  is_active: boolean;
  is_v2_registered: boolean;
  created_via: 'admin' | 'resident_self_service';
  created_at: string;
}

export async function fetchMissingIccid(
  page = 1,
  pageSize = 25,
): Promise<{ data: MissingIccidCharger[]; meta: { page: number; page_size: number; total: number } }> {
  const res = await api
    .get('admin/chargers/missing-iccid', { searchParams: { page, page_size: pageSize } })
    .json<ApiResponse<MissingIccidCharger[]> & { meta: { page: number; page_size: number; total: number } }>();
  return { data: res.data, meta: res.meta };
}

export interface BackfillResult {
  accepted: boolean;
  expected_boot_within_seconds: number;
}

export async function triggerIccidBackfill(chargerId: string): Promise<BackfillResult> {
  const res = await api
    .post(`admin/chargers/${chargerId}/backfill-iccid`)
    .json<ApiResponse<BackfillResult>>();
  return res.data;
}

export interface BackfillBulkOutcome {
  charger_id: string;
  identity?: string;
  outcome: 'success' | 'failed' | 'rejected';
  code?: string;
  message?: string;
}

export async function triggerIccidBackfillBulk(
  chargerIds: string[],
): Promise<BackfillBulkOutcome[]> {
  const res = await api
    .post('admin/chargers/backfill-iccid/bulk', { json: { charger_ids: chargerIds } })
    .json<ApiResponse<BackfillBulkOutcome[]>>();
  return res.data;
}

// ── Alerts admin (Phase 5a Item 10) ──────────────────────────────

export interface AlertRow {
  _id: string;
  tenant_id: string;
  charger_id: string;
  rule_id: string;
  type: string;
  severity: 'info' | 'warning' | 'critical';
  status: 'detected' | 'notified' | 'action_pending' | 'resolved' | 'escalated';
  message: string;
  detected_at: string;
  notified_at: string | null;
  acknowledged_at: string | null;
  resolved_at: string | null;
  escalated_at: string | null;
  /** The charger the alert fired on; null when it can no longer be loaded. */
  charger: { _id: string; name: string; identity: string } | null;
  /** The charger's site. `timezone` is null until the site has a zone. */
  site: { _id: string; name: string; timezone: string | null } | null;
}

export interface AlertsListResponse {
  data: AlertRow[];
  meta: { page: number; page_size: number; total: number };
}

export async function fetchAlerts(filter: AlertListFilter = {}): Promise<AlertsListResponse> {
  // 'open' goes out as the four unresolved statuses (see alertListQuery.ts).
  const sp = alertListSearchParams(filter);
  const res = await api.get(`admin/alerts?${sp.toString()}`)
    .json<ApiResponse<AlertRow[]> & { meta: AlertsListResponse['meta'] }>();
  return { data: res.data, meta: res.meta };
}

export interface AgentActionLogRow {
  _id: string;
  timestamp: string;
  agent_identity: string;
  tenant_id: string;
  charger_id: string | null;
  alert_id: string | null;
  action_type: string;
  action_parameters: Record<string, unknown>;
  outcome: 'success' | 'failed' | 'pending_approval' | 'rejected' | 'timed_out';
  error_message: string | null;
  was_auto: boolean;
  requires_human_approval: boolean;
  approved_by_user_id: string | null;
  approved_at: string | null;
  approval_decision: 'approve' | 'reject' | null;
  approval_note: string | null;
}

export interface AlertDetail {
  alert: AlertRow & {
    trigger_payload: Record<string, unknown>;
    resolution_payload: Record<string, unknown> | null;
    audience_user_ids: string[];
  };
  agent_actions: AgentActionLogRow[];
}

export async function fetchAlertDetail(id: string): Promise<AlertDetail> {
  const res = await api.get(`admin/alerts/${id}`).json<ApiResponse<AlertDetail>>();
  return res.data;
}

export async function acknowledgeAlert(id: string): Promise<void> {
  await api.post(`admin/alerts/${id}/acknowledge`);
}

export async function resolveAlert(id: string, note: string): Promise<void> {
  await api.post(`admin/alerts/${id}/resolve`, { json: { note } });
}

export async function fetchAgentLog(
  filter: {
    tenant_id?: string;
    charger_id?: string;
    action_type?: string;
    outcome?: AgentActionLogRow['outcome'];
    was_auto?: boolean;
    start_at?: string;
    end_at?: string;
    page?: number;
    page_size?: number;
  } = {},
): Promise<{ data: AgentActionLogRow[]; meta: { page: number; page_size: number; total: number } }> {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  const res = await api.get(`admin/agent-log?${sp.toString()}`)
    .json<ApiResponse<AgentActionLogRow[]> & { meta: { page: number; page_size: number; total: number } }>();
  return { data: res.data, meta: res.meta };
}

export async function approveAgentAction(id: string, note?: string): Promise<{ outcome: string; error_message: string | null }> {
  const res = await api
    .post(`admin/agent-actions/${id}/approve`, { json: note ? { note } : {} })
    .json<ApiResponse<{ outcome: string; error_message: string | null }>>();
  return res.data;
}

export async function rejectAgentAction(id: string, note?: string): Promise<void> {
  await api.post(`admin/agent-actions/${id}/reject`, { json: note ? { note } : {} });
}

// ── Admin EVII assistant ────────────────────────────────────────────────────

/**
 * Inline-visual spec carried on a tool call. Mirrors the server's AgentVisual
 * (apps/api adminAgent/tools.ts) — the tool decides which visual fits and shapes
 * its already-scoped data for an existing widget; the chat renders the match
 * (KpiCard / NetworkGauge / EvieBarList) beneath the text answer. No data access
 * lives here — every field is a reshape of what the scoped tool already returned.
 */
export type EvieVisual =
  | { kind: 'kpi'; data: { label: string; value: string; meta?: string } }
  | { kind: 'network_gauge'; data: { online: number; total: number; faulted: number } }
  | {
      kind: 'bar_list';
      data: { unit?: string; bars: Array<{ label: string; value: number; display?: string }> };
    };

export interface AdminAssistantToolCall {
  name: string;
  input: Record<string, unknown>;
  result_summary: string;
  /** Optional visual the chat renders inline beneath the text answer. */
  visual?: EvieVisual;
  /** Reporting slice 2 — small aggregate CSV (per-location rows only)
   *  the chat renders as a download button. Server-built. */
  csv?: { filename: string; content: string };
  /** Reporting slice 2 — deep link to the equivalent filtered report
   *  (the Reports page URL state). */
  report_url?: string;
}

/** A drafted, role-gated action EVII PROPOSED that needs the operator's explicit
 *  confirmation before it runs (Phase 2 propose -> confirm -> execute). The chat
 *  renders a confirm card; confirming calls confirmAdminAssistantAction with the
 *  server-issued token, which re-checks the permission + runs the SAME guarded
 *  write a human uses, as the operator. */
export interface AdminAssistantPendingAction {
  token: string;
  kind: 'coupon_active' | 'charger_alert_frequency' | 'alert' | 'label' | 'charger_delete'
    | 'session_refund' | 'subscription_cadence' | 'user_role';
  title: string;
  summary: string;
  detail: string;
  confirmLabel?: string;
}

export interface AdminAssistantResponse {
  answer: string;
  tool_calls: AdminAssistantToolCall[];
  pending_action?: AdminAssistantPendingAction | null;
}

/**
 * Synthesize EVII's answer text to speech via the server Polly proxy
 * (/admin-assistant/speak) and return the STREAMING Response so the caller can
 * play it progressively (playSpeechStream) -- the voice starts after the first
 * chunk instead of the whole clip. Text-to-audio only: the endpoint holds no
 * data and the AWS credentials stay server-side (task-role IAM). The shared `api`
 * client injects the Bearer token; the afterResponse hook only acts on 401, so
 * the streamed body passes through.
 */
export async function synthesizeAdminSpeech(
  text: string,
  voice: 'female' | 'male' = 'female',
): Promise<Response> {
  return api.post('admin-assistant/speak', { json: { text, voice } });
}

/**
 * Send a message to the admin EVII agent — the proven, server-scoped
 * /admin-assistant endpoint. The shared `api` client injects the caller's Bearer
 * token, so this is just the message text; ALL data access + scoping happen
 * server-side in the scoped tools. The chat shell adds no data access of its own.
 */
export async function sendAdminAssistantMessage(
  message: string,
  conversationId: string,
): Promise<AdminAssistantResponse> {
  const res = await api
    .post('admin-assistant/message', { json: { message, conversation_id: conversationId } })
    .json<ApiResponse<AdminAssistantResponse>>();
  return res.data;
}

/**
 * Confirm or cancel a drafted admin action (Phase 2). On 'confirm' the server
 * re-checks the SPECIFIC permission authoritatively + runs the SAME guarded
 * write a human uses, as the operator; on 'cancel' it discards the draft.
 * Returns a short message to show (and speak in voice mode).
 */
export async function confirmAdminAssistantAction(
  conversationId: string,
  token: string,
  decision: 'confirm' | 'cancel',
): Promise<{ status: string; message: string }> {
  const res = await api
    .post('admin-assistant/confirm', {
      json: { conversation_id: conversationId, token, decision },
    })
    .json<ApiResponse<{ status: string; message: string }>>();
  return res.data;
}

// ── Carbon program (carbon epic, display commit) ────────────────────

export interface CarbonAccrualRow {
  _id: string;
  session_id: string;
  charger_identity: string;
  site_name: string;
  energy_kwh: number;
  session_ended_at: string;
  jurisdiction: string;
  program: string;
  status: string;
  session_source: 'live' | 'v1_history';
  claim_batch_id: string | null;
  rate_version: string;
  host_share_cents: number;
  estimated_credit_value_cents: number;
  realized_credit_value_cents: number | null;
  realized_host_share_cents: number | null;
  payout_transfer_id: string | null;
  payout_error: string | null;
  review_hold: boolean;
  hold_reason: string | null;
  held_at: string | null;
}

export interface CarbonBatchRow {
  _id: string;
  jurisdiction: string;
  rows: number;
  energy_kwh: number;
  statuses: string[];
  estimated_credit_value_cents: number;
  realized_credit_value_cents: number;
  realized_host_share_cents: number;
  unpaid_realized_rows: number;
  v1_history_rows: number;
  floor_topup_cents: number;
  evi_margin_cents: number;
  shortfall_cents: number;
}

export interface CarbonChargerRow {
  identity: string;
  site_name: string;
  is_active: boolean;
  eligible: boolean;
  jurisdiction: string | null;
  registered_at: string | null;
  /** 'aggregator' (EVI claims and keeps) | 'shared' (host opted in).
   *  Null on a charger enrolled before the mode was recorded. */
  mode: string | null;
  /** When the host opted into sharing. Sessions before it stay
   *  aggregator — the sweeper pins each accrual against this. */
  shared_from: string | null;
  partner_registration_id: string | null;
  has_coordinate: boolean;
  excluded_reason: string | null;
}

export interface CarbonAccrualListParams {
  held?: string;
  jurisdiction?: string;
  status?: string;
  batch?: string;
  source?: string;
  limit?: number;
}

export async function fetchCarbonAccruals(
  params: CarbonAccrualListParams = {},
): Promise<{ total: number; truncated: boolean; accruals: CarbonAccrualRow[] }> {
  const searchParams = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') searchParams.set(k, String(v));
  }
  const res = await api
    .get('admin/carbon-accruals', { searchParams })
    .json<ApiResponse<{ total: number; truncated: boolean; accruals: CarbonAccrualRow[] }>>();
  return res.data;
}

export async function fetchCarbonBatches(): Promise<{ batches: CarbonBatchRow[]; held_excluded: CarbonHeldExcluded[] }> {
  const res = await api
    .get('admin/carbon-batches')
    .json<ApiResponse<{ batches: CarbonBatchRow[]; held_excluded: CarbonHeldExcluded[] }>>();
  return res.data;
}

export async function fetchCarbonChargers(): Promise<CarbonChargerRow[]> {
  const res = await api
    .get('admin/carbon-chargers')
    .json<ApiResponse<{ chargers: CarbonChargerRow[] }>>();
  return res.data.chargers;
}

/** Contradiction signals for the enrollment decision. Every signal is
 *  THREE-STATE — 'unknown' (no data) is deliberately distinct from
 *  'agrees', so absence can never be misread as confirmation. */
export interface CarbonReviewSignal {
  key: string;
  label: string;
  state: 'agrees' | 'disagrees' | 'unknown';
  value: string;
  detail: string;
}
export interface CarbonChargerReview {
  identity: string;
  site_name: string;
  created_via: string;
  owner_email: string | null;
  owner_name: string | null;
  first_seen_at: string | null;
  lifetime_kwh: number;
  session_count: number;
  claimed_jurisdiction: string | null;
  signals: CarbonReviewSignal[];
}

export async function fetchCarbonChargerReview(identity: string): Promise<CarbonChargerReview> {
  const res = await api
    .get(`admin/carbon-chargers/${identity}/review`)
    .json<ApiResponse<CarbonChargerReview>>();
  return res.data;
}

/** Enroll refusals carry the SPECIFIC missing-field list in the server's
 *  error.message — surface via getDisplayErrorMessage, not a generic. */
export async function enrollCarbonCharger(
  identity: string,
  body: {
    jurisdiction: string;
    registered_at?: string;
    partner_registration_id?: string;
    /**
     * Omit for 'aggregator' — EVI claims and keeps, which is the correct
     * state for the whole current network. Send 'shared' ONLY once a host
     * has opted in: the server then requires an identified host who has
     * accepted the Carbon Credit Program Terms, and refuses otherwise.
     */
    mode?: 'aggregator' | 'shared';
  },
): Promise<void> {
  await api.post(`admin/carbon-chargers/${identity}/enroll`, { json: body }).json();
}

export async function unenrollCarbonCharger(
  identity: string,
  body: {
    excluded_reason?: string;
    /** Companion hold of already-accrued rows. Explicit, never implied. */
    hold_existing_accruals?: boolean;
    hold_reason?: string;
  },
): Promise<{ accruals_held: number }> {
  const res = await api
    .post(`admin/carbon-chargers/${identity}/unenroll`, { json: body })
    .json<ApiResponse<{ accruals_held: number }>>();
  return res.data;
}

// ── Carbon review hold ──────────────────────────────────────────────

export interface CarbonHeldExcluded {
  jurisdiction: string;
  rows: number;
  energy_kwh: number;
  host_share_cents: number;
}

/** Exactly one of accrual_ids / charger_identity, mirroring the route. */
export async function holdCarbonAccruals(params: {
  accrual_ids?: string[];
  charger_identity?: string;
  reason: string;
}): Promise<{ held: number; skipped_settled: number; note?: string }> {
  const res = await api
    .post('admin/carbon-accruals/hold', { json: params })
    .json<ApiResponse<{ held: number; skipped_settled: number; note?: string }>>();
  return res.data;
}

export async function releaseCarbonAccruals(params: {
  accrual_ids?: string[];
  charger_identity?: string;
  note?: string;
}): Promise<{ released: number }> {
  const res = await api
    .post('admin/carbon-accruals/release', { json: params })
    .json<ApiResponse<{ released: number }>>();
  return res.data;
}

// ── Autocharge (plug & charge by vehicle id) ─────────────────────────────────
// docs/tickets/ocpp-autocharge-vid.md. Settings = per-tenant opt-in for NEW
// enrolments; rules = the model-level kill switch (suspend on create, restore
// exactly those rows on lift).

export type AutochargeRuleKind = 'oui' | 'make' | 'make_model' | 'tag';

export interface AutochargeRule {
  _id: string;
  kind: AutochargeRuleKind;
  value: string;
  reason: string;
  active: boolean;
  suspended_count: number;
  restored_count: number | null;
  created_at: string;
  lifted_at: string | null;
}

export interface AutochargeSettingsResponse {
  tenant_id: string;
  enabled: boolean;
}

export async function fetchAutochargeSettings(tenantId?: string): Promise<AutochargeSettingsResponse> {
  const res = await api
    .get('admin/autocharge/settings', tenantId ? { searchParams: { tenant_id: tenantId } } : undefined)
    .json<ApiResponse<AutochargeSettingsResponse>>();
  return res.data;
}

export async function updateAutochargeSettings(
  body: { tenant_id?: string; enabled: boolean },
): Promise<AutochargeSettingsResponse> {
  const res = await api.put('admin/autocharge/settings', { json: body }).json<ApiResponse<AutochargeSettingsResponse>>();
  return res.data;
}

export async function fetchAutochargeRules(): Promise<AutochargeRule[]> {
  const res = await api.get('admin/autocharge/rules').json<ApiResponse<AutochargeRule[]>>();
  return res.data;
}

export async function createAutochargeRule(
  body: { kind: AutochargeRuleKind; value: string; reason: string },
): Promise<AutochargeRule & { uncovered_without_vehicle: number | null }> {
  const res = await api.post('admin/autocharge/rules', { json: body })
    .json<ApiResponse<AutochargeRule & { uncovered_without_vehicle: number | null }>>();
  return res.data;
}

/** Unbind a vehicle credential (sold car, wrong account) so the real owner can link it. */
/** Release a FOB: unbind it so the person holding it can register it
 *  themselves (POST /admin/rfid-cards/:id/release). Vehicle ids use
 *  releaseAutochargeCredential. */
export async function releaseRfidCard(id: string): Promise<void> {
  await api.post(`admin/rfid-cards/${id}/release`).json<ApiResponse<{ _id: string }>>();
}

export async function releaseAutochargeCredential(id: string): Promise<void> {
  await api.post(`admin/autocharge/credentials/${id}/release`).json<ApiResponse<{ _id: string }>>();
}

export async function liftAutochargeRule(id: string): Promise<AutochargeRule> {
  const res = await api.post(`admin/autocharge/rules/${id}/lift`).json<ApiResponse<AutochargeRule>>();
  return res.data;
}

// ── EVII connector access ────────────────────────────────────────────────────
// The claude.ai OAuth connections to this stage's Mcp server. super_admin or
// operator (GET /admin/mcp/connections; the revoke is audited). A revoke cuts
// the connection's access within a minute.

export type McpConnectionStatus = 'connected' | 'not_connected' | 'revoked';

export interface McpConnection {
  client_id: string;
  client_name: string | null;
  redirect_hosts: string[];
  registered_at: string | null;
  approved_at: string | null;
  approved_ip: string | null;
  approved_user_agent: string | null;
  approval_count: number;
  last_refresh_at: string | null;
  last_used_at: string | null;
  live_refresh_tokens: number;
  status: McpConnectionStatus;
  revoked_at: string | null;
  revoked_by: string | null;
}

export interface McpConnectionList {
  connections: McpConnection[];
  unapproved_registrations: number;
}

export async function fetchMcpConnections(): Promise<McpConnectionList> {
  const res = await api.get('admin/mcp/connections').json<ApiResponse<McpConnectionList>>();
  return res.data;
}

export async function revokeMcpConnection(clientId: string): Promise<{
  client_id: string; already_revoked: boolean; refresh_tokens_deleted: number;
}> {
  const res = await api.post(`admin/mcp/connections/${encodeURIComponent(clientId)}/revoke`)
    .json<ApiResponse<{ client_id: string; already_revoked: boolean; refresh_tokens_deleted: number }>>();
  return res.data;
}

// ── Fleet plug & charge (vehicle ids, free) ──────────────────────────────────
// docs/tickets/ocpp-autocharge-vid.md §10. A vehicle id is linked to a fleet
// vehicle from the list of ids the fleet's own chargers have seen — never
// typed — with no fee.

export interface FleetAutochargeCredentialRow {
  _id: string;
  tag_uid: string;
  label: string | null;
  is_active: boolean;
  suspended_by_rule: boolean;
  vehicle: { _id: string; make: string; model: string; year: number; label: string | null } | null;
  driver: { _id: string; name: string | null; email: string } | null;
  created_at: string;
}

export interface FleetUnknownVehicleIdRow {
  tag_uid: string;
  charger_identity: string;
  charger_label: string;
  last_seen_at: string;
}

export async function fetchFleetAutochargeCredentials(fleetId: string): Promise<FleetAutochargeCredentialRow[]> {
  const res = await api.get(`admin/fleets/${fleetId}/autocharge/credentials`).json<ApiResponse<FleetAutochargeCredentialRow[]>>();
  return res.data;
}

export async function fetchFleetUnknownVehicleIds(fleetId: string): Promise<FleetUnknownVehicleIdRow[]> {
  const res = await api.get(`admin/fleets/${fleetId}/autocharge/unknown-vids`).json<ApiResponse<FleetUnknownVehicleIdRow[]>>();
  return res.data;
}

export async function issueFleetAutochargeCredential(
  fleetId: string,
  body: { tag_uid: string; vehicle_id: string; user_id?: string | null; label?: string },
): Promise<{ _id: string }> {
  const res = await api.post(`admin/fleets/${fleetId}/autocharge/credentials`, { json: body }).json<ApiResponse<{ _id: string }>>();
  return res.data;
}

export async function releaseFleetAutochargeCredential(fleetId: string, cardId: string): Promise<void> {
  await api.delete(`admin/fleets/${fleetId}/autocharge/credentials/${cardId}`).json<ApiResponse<{ _id: string }>>();
}

// ── Operator report tabs (Fleet Billing / Customer Groups / Uptime) ──────────
//
// Three aggregates added 2026-09-24 to replace the "Coming soon" stubs.
// Each takes the shell's resolved from/to plus the shared Advanced
// filter set, so a tab honours the period pills and the filter dialog
// the same way Revenue and Sessions do.

export interface FleetBillingRow {
  fleet_id: string;
  fleet_name: string;
  session_count: number;
  energy_kwh: number;
  cost_cents: number;
  driver_count: number;
  last_session_at: string | null;
}

export interface FleetBillingResponse {
  rows: FleetBillingRow[];
  totals: {
    fleet_count: number; session_count: number; energy_kwh: number; cost_cents: number;
    unattributed_session_count: number; unattributed_cost_cents: number;
  };
}

export async function fetchFleetBillingReport(
  params: { from?: string; to?: string },
  filters?: ReportFilterParams | null,
): Promise<FleetBillingResponse> {
  const searchParams = new URLSearchParams();
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  for (const [k, v] of Object.entries(reportFilterSearchParams(filters))) searchParams.set(k, v);
  const res = await api.get('admin/reports/fleet-billing', { searchParams })
    .json<ApiResponse<FleetBillingResponse>>();
  return res.data;
}

export interface CustomerGroupRow {
  group_id: string;
  group_name: string;
  member_count: number;
  active_member_count: number;
  session_count: number;
  energy_kwh: number;
  cost_cents: number;
}

export interface CustomerGroupsResponse {
  rows: CustomerGroupRow[];
  totals: { group_count: number; session_count: number; energy_kwh: number; cost_cents: number };
}

export async function fetchCustomerGroupsReport(
  params: { from?: string; to?: string },
  filters?: ReportFilterParams | null,
): Promise<CustomerGroupsResponse> {
  const searchParams = new URLSearchParams();
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  for (const [k, v] of Object.entries(reportFilterSearchParams(filters))) searchParams.set(k, v);
  const res = await api.get('admin/reports/customer-groups', { searchParams })
    .json<ApiResponse<CustomerGroupsResponse>>();
  return res.data;
}

export interface ChargerDowntimeRow {
  charger_id: string;
  charger_identity: string;
  location_name: string | null;
  downtime_hours: number;
  outage_count: number;
  longest_outage_hours: number;
  still_open: boolean;
  last_outage_at: string | null;
}

export interface ChargerDowntimeResponse {
  rows: ChargerDowntimeRow[];
  window: { from: string; to: string };
  totals: { downtime_hours: number; chargers_affected: number };
}

export async function fetchChargerDowntimeReport(
  params: { from?: string; to?: string; limit?: number },
): Promise<ChargerDowntimeResponse> {
  const searchParams = new URLSearchParams();
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  if (params.limit) searchParams.set('limit', String(params.limit));
  const res = await api.get('admin/reports/charger-downtime', { searchParams })
    .json<ApiResponse<ChargerDowntimeResponse>>();
  return res.data;
}

export async function fetchSiteComparison(params: { from: string; to: string }): Promise<import('@evi/shared').SiteComparisonResponse> {
  const res = await api.get('dashboard/site-comparison', { searchParams: params }).json<ApiResponse<import('@evi/shared').SiteComparisonResponse>>();
  return res.data;
}

export async function fetchIdleFeeReport(params: { from: string; to: string; filters?: ReportFilterParams }): Promise<import('@evi/shared').IdleFeeReportResponse> {
  const res = await api.get('admin/reports/idle-fees', { searchParams: { from: params.from, to: params.to,
    ...reportFilterSearchParams(params.filters) } }).json<ApiResponse<import('@evi/shared').IdleFeeReportResponse>>();
  return res.data;
}

export async function fetchRevenueDashboard(params: { from: string; to: string; filters?: ReportFilterParams }): Promise<import('@evi/shared').RevenueDashboardResponse> {
  const res = await api.get('admin/reports/revenue', { searchParams: { from: params.from, to: params.to,
    ...reportFilterSearchParams(params.filters) } }).json<ApiResponse<import('@evi/shared').RevenueDashboardResponse>>();
  return res.data;
}
